/**
 * /owner-console — hidden, owner-only admin panel.
 *
 * NOT linked anywhere in the public UI. Indexing is blocked via:
 *   1. /robots.txt  Disallow: /owner-console
 *   2. <meta name="robots" content="noindex,nofollow,noarchive"> set below
 *
 * Authentication is enforced by the backend on every API call:
 *   - logged-in user check (regular session/JWT)
 *   - admin email whitelist (ADMIN_EMAILS env)
 *   - additional PIN          (ADMIN_PIN env)
 *   - TOTP / Google Authenticator
 *
 * The frontend hiding is **defense in depth only** — the backend will refuse
 * any /api/admin/* call from a non-admin even if the SPA were tampered with.
 *
 * On any non-admin probe (no login, wrong email, missing env config), this
 * page renders the same generic 404 the rest of the app uses, so the route
 * is indistinguishable from a typo.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { QRCodeSVG } from "qrcode.react";
import {
  ShieldCheck, KeyRound, Smartphone, Lock, LogOut,
  Loader2, Copy, Check, AlertTriangle, ArrowRight,
  Users, Settings, Search, RotateCcw, X, Trash2,
  Activity, Database, RefreshCw, Wifi, WifiOff,
  BarChart3, ChevronDown, CheckCircle2, XCircle, Stethoscope, Briefcase,
  Monitor, LineChart, Eye, Sparkles, Mail,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import NotFound from "@/pages/NotFound";
import OwnerDashboard from "@/components/owner/OwnerDashboard";
import OwnerAnalyticsPanel from "@/components/owner/OwnerAnalyticsPanel";
import PortfolioGrowthChart from "@/components/portfolio/PortfolioGrowthChart";
import IdentityBackfillCard from "@/components/owner/IdentityBackfillCard";
import HomepageSnapshotComposer from "@/components/owner/HomepageSnapshotComposer";
import { getLocale, useTranslation } from "@/lib/i18n";

// WS-8: the admin credential is now an HttpOnly ``admin_session``
// cookie scoped to /api/admin. The frontend no longer reads or writes
// a bearer token in browser storage. `stockaily_admin_hint` is a
// non-secret sessionStorage marker used solely for UI gating (e.g.
// LiveDataChip) — it holds no credential material.
const ADMIN_HINT_KEY = "stockaily_admin_hint"; // sessionStorage (cleared on tab close)

const PLAN_META = [
  { key: "free",    label: "Free",    color: "#7D8592", desc: "10 wl · limited AI" },
  { key: "pro",     label: "Pro",     color: "#3182CE", desc: "Unlimited · full AI" },
  { key: "premium", label: "Premium", color: "#A47864", desc: "Pro + portfolio" },
];

const PLAN_COLORS = {
  free: "#7D8592",
  pro: "#3182CE",
  premium: "#A47864",
};

/* ----------------------------- helpers ----------------------------- */

function readAdminHint() {
  try { return window.sessionStorage.getItem(ADMIN_HINT_KEY) || ""; }
  catch { return ""; }
}
function writeAdminHint(hint) {
  try {
    if (hint) window.sessionStorage.setItem(ADMIN_HINT_KEY, "1");
    else window.sessionStorage.removeItem(ADMIN_HINT_KEY);
  } catch { /* sessionStorage disabled */ }
}
function adminHeaders() {
  // WS-8: the ``admin_session`` HttpOnly cookie is sent automatically
  // via ``withCredentials: true``.
  //
  // WS-8 correction — interim admin CSRF barrier: cookie-authenticated
  // state-changing requests MUST include the non-secret custom header
  // ``X-Admin-Request: 1``. A hostile origin cannot set that header
  // without a successful CORS preflight. WS-5 will replace this with
  // the full CSRF / exact-origin strategy.
  return { "X-Admin-Request": "1" };
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays < 1) {
      const h = Math.floor(diffMs / 3600000);
      if (h < 1) return "just now";
      return `${h}h ago`;
    }
    if (diffDays < 30) return `${diffDays}d ago`;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return "—"; }
}

/* ----------------------------- main ----------------------------- */

export default function OwnerConsole() {
  // Block indexing as soon as this page mounts.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow,noarchive,nosnippet";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "·";
    // Phase 7.UI.1 — Owner Console is locked to dark mode regardless of
    // the user's chosen theme. Set a marker attribute that activates the
    // owner-console-only CSS variable block in index.css; restore on
    // unmount so the rest of the app honours the user's theme again.
    const prevOwnerAttr = document.documentElement.getAttribute("data-owner-console");
    document.documentElement.setAttribute("data-owner-console", "1");
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
      if (prevOwnerAttr === null) {
        document.documentElement.removeAttribute("data-owner-console");
      } else {
        document.documentElement.setAttribute("data-owner-console", prevOwnerAttr);
      }
    };
  }, []);

  const { user, loading: authLoading, checkAuth, loginWithCredentials } = useAuth();
  const navigate = useNavigate();

  // status: "checking" | "denied" | "identify" | "pin" | "setup_totp" | "totp" | "panel"
  const [status, setStatus] = useState("checking");
  const [serverStatus, setServerStatus] = useState(null);
  // Phase 6.BC — Owner Console auth diagnostics. When access fails, we
  // surface the EXACT reason instead of silently bouncing to NotFound:
  //   no_session_cookie / session_invalid / user_not_loaded /
  //   owner_email_not_whitelisted / admin_disabled / admin_password_missing /
  //   backend_auth_endpoint_failed / cors_credentials_failed / ...
  const [deniedReason, setDeniedReason] = useState(null);
  const [deniedDetail, setDeniedDetail] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Not signed in: render the dedicated owner-identification panel
      // (a private internal sign-in form). We deliberately do NOT bounce
      // to /login \u2014 the owner console is its own self-contained area and
      // should never expose itself via the public auth surface.
      setStatus("identify");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const existingAdmin = readAdminHint();
        if (existingAdmin) {
          try {
            await api.get("/admin/me", { headers: adminHeaders() });
            if (!cancelled) setStatus("panel");
            return;
          } catch {
            writeAdminHint("");
          }
        }
        const res = await api.get("/admin/status");
        if (cancelled) return;
        setServerStatus(res.data);
        if (!res.data?.admin_enabled) {
          // Phase 6.BC — Admin gate is fully disabled on this deploy
          // (ADMIN_EMAILS and/or ADMIN_PIN env vars not configured).
          // This is the #1 cause of "/owner-console redirects me to
          // login" on production deploys.
          setDeniedReason("admin_disabled");
          setDeniedDetail({
            message:
              "Admin gate is fully disabled on this backend. Both ADMIN_EMAILS and ADMIN_PIN environment variables must be set on the Emergent deploy.",
            action:
              "Contact Emergent Support to set ADMIN_EMAILS and ADMIN_PIN on the production deployment, then restart the backend.",
            backend_response: res.data,
          });
          setStatus("denied");
          return;
        }
        if (!res.data?.is_admin_email) {
          setDeniedReason("owner_email_not_whitelisted");
          setDeniedDetail({
            message: `Your email is not on the ADMIN_EMAILS whitelist on this backend.`,
            action:
              "Sign out and sign in with the owner email, OR ask Emergent Support to add your email to ADMIN_EMAILS on this deployment.",
            current_email: user?.email || null,
            backend_response: res.data,
          });
          setStatus("denied");
          return;
        }
        setStatus("pin");
      } catch (err) {
        if (cancelled) return;
        const status_code = err?.response?.status;
        if (status_code === 401) {
          setDeniedReason("session_invalid");
          setDeniedDetail({
            message: "/admin/status returned 401 — your session cookie is not being accepted by this backend.",
            action:
              "Possible causes: (1) cookie SameSite/Secure mismatch between frontend and backend, (2) backend deployment is different from the one that minted the cookie, (3) CORS credentials not allowed for this origin. Try clearing cookies and signing in again.",
            http_status: 401,
          });
        } else if (!err?.response) {
          setDeniedReason("backend_auth_endpoint_failed");
          setDeniedDetail({
            message: "Network/transport error reaching /admin/status. The frontend may be calling a backend URL that doesn't exist or is blocked by CORS.",
            action:
              "Check the Environment banner below: confirm 'frontend api base' resolves to a reachable backend.",
            error_message: String(err?.message || "").slice(0, 240),
            backend_url: (typeof window !== "undefined" && window.__STOCKAILY_BACKEND_URL__) || null,
          });
        } else {
          setDeniedReason("backend_auth_endpoint_failed");
          setDeniedDetail({
            message: `/admin/status returned ${status_code}.`,
            action: "Inspect backend logs for the request_id below.",
            http_status: status_code,
            response_body: err?.response?.data || null,
            request_id: err?.response?.headers?.["x-request-id"] || null,
          });
        }
        setStatus("denied");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.user_id]);

  if (status === "checking" || authLoading) {
    return <BlankShell />;
  }
  if (status === "denied") {
    return <OwnerAuthDiagnostic
      reason={deniedReason}
      detail={deniedDetail}
      user={user}
      serverStatus={serverStatus}
    />;
  }

  const isPanel = status === "panel";

  return (
    <ConsoleShell user={user} wide={isPanel}>
      {status === "identify" && (
        <IdentifyStep
          loginWithCredentials={loginWithCredentials}
          checkAuth={checkAuth}
          onGoToTotp={() => setStatus("totp")}
          onGoToSetupTotp={() => setStatus("setup_totp")}
          onGoogleSuccess={async () => {
            // After Google OAuth completes (handled by AuthCallback), the
            // user is logged in. We probe /admin/status; non-admin emails
            // fall through to the generic 404.
            try {
              const res = await api.get("/admin/status");
              if (res.data?.admin_enabled && res.data?.is_admin_email) {
                setServerStatus(res.data);
                setStatus("pin");
              } else {
                setStatus("denied");
              }
            } catch {
              setStatus("denied");
            }
          }}
        />
      )}
      {status === "pin" && (
        <PinStep
          onSuccess={(next, token) => {
            if (next === "setup_totp") {
              window.sessionStorage.setItem("__owner_setup_token", token);
              setStatus("setup_totp");
            } else if (next === "totp") {
              window.sessionStorage.setItem("__owner_pre_token", token);
              setStatus("totp");
            }
          }}
          onDenied={() => setStatus("denied")}
        />
      )}
      {status === "setup_totp" && (
        <SetupTotpStep
          email={user?.email || ""}
          onSuccess={async (adminToken) => {
            writeAdminHint(adminToken);
            window.sessionStorage.removeItem("__owner_setup_token");
            await checkAuth();
            setStatus("panel");
          }}
        />
      )}
      {status === "totp" && (
        <TotpStep
          onSuccess={async (adminToken) => {
            writeAdminHint(adminToken);
            window.sessionStorage.removeItem("__owner_pre_token");
            await checkAuth();
            setStatus("panel");
          }}
        />
      )}
      {status === "panel" && (
        <PanelStep
          selfUserId={user?.user_id || null}
          onLogout={async () => {
            try { await api.post("/admin/logout", {}, { headers: adminHeaders() }); } catch { /* ignore */ }
            writeAdminHint("");
            navigate("/", { replace: true });
          }}
          onPlanChanged={checkAuth}
        />
      )}
    </ConsoleShell>
  );
}

/* ----------------------------- shell ----------------------------- */

function BlankShell() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );
}

function ConsoleShell({ user, children, wide = false }) {
  const widthClass = wide ? "max-w-5xl" : "max-w-md";
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="owner-console">
      <header className="border-b border-border/60 bg-card/40 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-[rgba(224,179,65,0.45)] bg-card">
            <ShieldCheck className="w-4 h-4" style={{ color: "#E0B341" }} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Owner console</div>
            <div className="text-sm font-medium truncate">{user?.email}</div>
          </div>
          <Badge
            variant="outline"
            className="text-[10px] uppercase tracking-wider border-[rgba(224,179,65,0.35)]"
            style={{ color: "#E0B341" }}
          >
            Owner Access
          </Badge>
        </div>
      </header>
      <main className="flex-1 flex items-start justify-center px-4 py-8 sm:py-12">
        <div className={`w-full ${widthClass}`}>{children}</div>
      </main>
    </div>
  );
}

/* --------------------- Owner identification (dedicated sign-in) --------------------- */

const OWNER_RETURN_KEY = "__owner_return_after_google";

/**
 * Trigger the same Emergent-managed Google OAuth flow the public Login uses,
 * but flag sessionStorage so `AuthCallback` returns to /owner-console (not
 * /watchlist) once the session is established.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
function startGoogleForOwner() {
  try { window.sessionStorage.setItem(OWNER_RETURN_KEY, "1"); } catch { /* ignore */ }
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

/**
 * Dedicated owner-identification step. Replaces the regression where
 * /owner-console redirected to the public /login page.
 *
 * Supports BOTH auth methods so an admin can sign in regardless of how they
 * created their Stockaily account:
 *   - Google sign-in (primary — most Stockaily admins use Google OAuth)
 *   - Email + password (for accounts that registered with a password)
 *
 * On success the parent probes /api/admin/status; non-admin emails fall
 * through to the generic 404 (same UX as if the route did not exist).
 *
 * Backend remains the ultimate enforcement layer (ADMIN_EMAILS whitelist).
 */
function IdentifyStep({ loginWithCredentials, checkAuth, onGoToTotp, onGoToSetupTotp, onGoogleSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !email || !password) return;
    setBusy(true); setErr("");
    try {
      // Unified owner sign-in. Backend verifies (a) ADMIN_EMAILS whitelist,
      // (b) ADMIN_PIN constant-time, and (c) that a user record exists for
      // this email. All failures collapse to a single generic 401 so we
      // cannot enumerate either half of the credential.
      const res = await api.post("/admin/login/owner", {
        email: email.trim().toLowerCase(),
        password,
      });
      const data = res.data || {};
      if (data.token) {
        // WS-8: HttpOnly session_token cookie is set by /admin/login/owner
        // — no JS-readable token is written here.
      }
      // Refresh AuthContext so the owner panel header shows the right email.
      await checkAuth();
      // Hand off directly to the appropriate second-factor step. We skip
      // the now-redundant /admin/login/pin call because the PIN was already
      // verified inside /admin/login/owner above.
      if (data.next === "totp" && data.pre_token) {
        try { window.sessionStorage.setItem("__owner_pre_token", data.pre_token); } catch { /* ignore */ }
        onGoToTotp();
      } else if (data.next === "setup_totp" && data.setup_token) {
        try { window.sessionStorage.setItem("__owner_setup_token", data.setup_token); } catch { /* ignore */ }
        onGoToSetupTotp();
      } else {
        // Unexpected backend response.
        setErr("Unexpected server response. Please retry.");
      }
    } catch (ex) {
      const code = ex?.response?.status;
      if (code === 401) {
        setErr("Email or password incorrect. Please double-check both.");
      } else if (code === 422) {
        setErr("Please enter a valid email and password.");
      } else {
        setErr("Identification failed. Please retry in a moment.");
      }
      setPassword("");
    } finally { setBusy(false); }
  };

  return (
    <Card title="Owner identification" icon={Lock} accent="#E0B341">
      <p className="text-sm text-muted-foreground">
        This area is restricted to approved Stockaily operators. Sign in with
        your admin email and console password. Backend verifies both against
        the owner allow-list before granting access.
      </p>

      {/* Primary: email + admin password (the ADMIN_PIN). */}
      <form onSubmit={submit} className="mt-5 space-y-3" data-testid="owner-identify-form">
        <div>
          <Label htmlFor="owner-email">Admin email</Label>
          <Input
            id="owner-email"
            type="email"
            autoComplete="username"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="owner-identify-email"
            className="mt-1 h-11"
            placeholder="owner@yourdomain"
          />
        </div>
        <div>
          <Label htmlFor="owner-password">Console password</Label>
          <Input
            id="owner-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="owner-identify-password"
            className="mt-1 h-11 font-mono tracking-wider"
            placeholder="••••••••"
          />
        </div>
        {err ? (
          <div className="text-xs text-[#E5484D] flex items-start gap-1.5 leading-[1.45]">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{err}</span>
          </div>
        ) : null}
        <Button type="submit" className="w-full h-11" disabled={busy || !email || !password} data-testid="owner-identify-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </form>

      {/* Divider */}
      <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">
        <div className="flex-1 h-px bg-border/70" /> or already-authenticated path <div className="flex-1 h-px bg-border/70" />
      </div>

      {/* Alternate: sign in with Google first, then enter PIN on the next screen.
          Useful for admins who'd rather not type the console password in this
          form (e.g. shared computer) \u2014 the PIN is still required server-side. */}
      <Button
        variant="secondary"
        className="w-full h-11"
        onClick={startGoogleForOwner}
        data-testid="owner-identify-google"
      >
        <svg className="w-4 h-4 mr-2" viewBox="0 0 48 48" fill="currentColor" aria-hidden>
          <path d="M44.5 20H24v8.5h11.7C34.7 33 30.1 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.1 8.1 3l6.1-6.1C34.4 5 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.4-.2-2.7-.5-4z" />
        </svg>
        Continue with Google
      </Button>

      <div className="mt-5 text-[10px] text-muted-foreground/70 inline-flex items-center gap-1.5">
        <ShieldCheck className="w-3 h-3" />
        Next step: authenticator (TOTP) code.
      </div>
    </Card>
  );
}

/* ----------------------------- PIN step ----------------------------- */

function PinStep({ onSuccess, onDenied }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin || busy) return;
    setBusy(true); setErr("");
    try {
      const res = await api.post("/admin/login/pin", { pin });
      if (res.data?.next && res.data?.token) {
        onSuccess(res.data.next, res.data.token);
      } else {
        setErr("Unexpected response");
      }
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // WS-5 v3 — distinguish CSRF/origin failures from the actual
      // invalid-credentials path. The middleware fires BEFORE
      // ADMIN_PIN is validated, so "Invalid PIN" would be misleading
      // and would also leak that CSRF is the actual failure to any
      // attacker probing the endpoint. Ask the user to refresh
      // instead — the api client caches the freshly-echoed token
      // (see /lib/api.js), so a follow-up submission normally works.
      if (
        code === 403 &&
        (detail === "csrf_token_required" ||
          detail === "origin_required" ||
          detail === "origin_not_allowed")
      ) {
        setErr("Session verification failed. Please refresh the page and try again.");
        setPin("");
        return;
      }
      // 401 with invalid_credentials is the ONLY genuine wrong-PIN case.
      if (code === 401 && detail === "invalid_credentials") {
        setErr("Invalid PIN.");
        setPin("");
        return;
      }
      // 404 → admin flow disabled or path unreachable: keep the
      // existing "denied" screen (does not reveal the reason).
      // 403 without a CSRF detail → e.g. `not_admin`; also route to
      // the denied screen so the current UX is preserved.
      if (code === 404 || code === 403) {
        onDenied();
        return;
      }
      // Anything else — network error, 5xx, unexpected JSON — do NOT
      // show as "Invalid PIN". A concise generic retry keeps the
      // signal accurate without exposing implementation details.
      setErr("Something went wrong. Please try again.");
      setPin("");
    } finally { setBusy(false); }
  };

  return (
    <Card title="Step 1 — Owner PIN" icon={KeyRound}>
      <p className="text-sm text-muted-foreground">
        Enter the owner PIN configured in the backend environment. After 3 minutes of inactivity this step expires.
      </p>
      <form onSubmit={submit} className="mt-5 space-y-3">
        <div>
          <Label htmlFor="admin-pin">PIN</Label>
          <Input
            id="admin-pin"
            ref={inputRef}
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            data-testid="owner-pin-input"
            className="mt-1 h-11 font-mono tabular tracking-wider"
            placeholder="Enter PIN"
          />
        </div>
        {err ? (
          <div className="text-xs text-[#E5484D] inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {err}
          </div>
        ) : null}
        <Button type="submit" className="w-full h-11" disabled={busy || !pin} data-testid="owner-pin-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </form>
    </Card>
  );
}

/* --------------------- TOTP first-time setup --------------------- */

function SetupTotpStep({ email, onSuccess }) {
  const setupToken = useMemo(() => {
    try { return window.sessionStorage.getItem("__owner_setup_token") || ""; }
    catch { return ""; }
  }, []);
  const [provisioning, setProvisioning] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!setupToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.post("/admin/totp/provision", { setup_token: setupToken });
        if (!cancelled) setProvisioning(res.data);
      } catch {
        if (!cancelled) setErr("Could not start TOTP setup. Please retry the PIN step.");
      }
    })();
    return () => { cancelled = true; };
  }, [setupToken]);

  const submit = async (e) => {
    e.preventDefault();
    if (!provisioning || busy) return;
    setBusy(true); setErr("");
    try {
      const res = await api.post("/admin/totp/confirm", {
        setup_token: setupToken,
        secret: provisioning.secret,
        code: code.trim(),
      });
      // WS-8 correction: response no longer carries a reusable
      // admin_token. Successful TOTP confirmation is signaled by
      // ``ok: true`` and the ``admin_session`` HttpOnly cookie set
      // by the server. Pass a truthy marker to onSuccess for the
      // UI hint.
      if (res.data?.ok) {
        onSuccess("1");
      }
    } catch {
      setErr("That code wasn't accepted. Make sure your device clock is in sync and try the next 6-digit code.");
      setCode("");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!provisioning?.secret) return;
    try {
      await navigator.clipboard.writeText(provisioning.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  return (
    <Card title="Step 2 — Set up authenticator" icon={Smartphone}>
      <p className="text-sm text-muted-foreground">
        Scan this QR code in <span className="text-foreground">Google Authenticator</span>, <span className="text-foreground">1Password</span>, <span className="text-foreground">Authy</span> or any TOTP app, then enter the 6-digit code to finish the first-time setup.
      </p>
      <div className="mt-5 flex flex-col items-center gap-3">
        {provisioning ? (
          <>
            <div className="rounded-xl bg-white p-3" data-testid="owner-totp-qr">
              <QRCodeSVG value={provisioning.provisioning_uri} size={176} level="M" />
            </div>
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground font-mono tabular"
              title="Copy secret"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-[#3FAE89]" /> : <Copy className="w-3.5 h-3.5" />}
              <span className="select-all">{provisioning.secret}</span>
            </button>
            <div className="text-[10px] text-muted-foreground/80">Account: {email}</div>
          </>
        ) : (
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        )}
      </div>
      <form onSubmit={submit} className="mt-5 space-y-3">
        <div>
          <Label htmlFor="admin-totp">6-digit code</Label>
          <Input
            id="admin-totp"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoComplete="off"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            data-testid="owner-totp-setup-input"
            className="mt-1 h-11 font-mono text-center tabular tracking-[0.6em] text-lg"
            placeholder="000000"
          />
        </div>
        {err ? (
          <div className="text-xs text-[#E5484D] inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {err}
          </div>
        ) : null}
        <Button type="submit" className="w-full h-11" disabled={busy || code.length !== 6 || !provisioning} data-testid="owner-totp-setup-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Confirm & enter <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </form>
    </Card>
  );
}

/* ------------------------- TOTP verify ------------------------- */

function TotpStep({ onSuccess }) {
  const preToken = useMemo(() => {
    try { return window.sessionStorage.getItem("__owner_pre_token") || ""; }
    catch { return ""; }
  }, []);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!preToken || busy) return;
    setBusy(true); setErr("");
    try {
      const res = await api.post("/admin/login/totp", { pre_token: preToken, code: code.trim() });
      // WS-8 correction — see setup path above.
      if (res.data?.ok) onSuccess("1");
    } catch {
      setErr("Invalid or expired code.");
      setCode("");
    } finally { setBusy(false); }
  };

  return (
    <Card title="Step 2 — Authenticator code" icon={Smartphone}>
      <p className="text-sm text-muted-foreground">
        Enter the 6-digit code from your authenticator app to finish signing in.
      </p>
      <form onSubmit={submit} className="mt-5 space-y-3">
        <div>
          <Label htmlFor="admin-totp">6-digit code</Label>
          <Input
            id="admin-totp"
            ref={inputRef}
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            data-testid="owner-totp-input"
            className="mt-1 h-11 font-mono text-center tabular tracking-[0.6em] text-lg"
            placeholder="000000"
          />
        </div>
        {err ? (
          <div className="text-xs text-[#E5484D] inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {err}
          </div>
        ) : null}
        <Button type="submit" className="w-full h-11" disabled={busy || code.length !== 6} data-testid="owner-totp-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </form>
    </Card>
  );
}

/* --------------------------- panel --------------------------- */

function PanelStep({ selfUserId, onLogout, onPlanChanged }) {
  const [tab, setTab] = useState("dashboard");
  const [portfolioDiagPresetUserId, setPortfolioDiagPresetUserId] = useState("");

  const openPortfolioDiagnosticsFor = useCallback((uid) => {
    if (!uid) return;
    setPortfolioDiagPresetUserId(uid);
    setTab("portfolio");
  }, []);
  const [me, setMe] = useState(null);

  const loadMe = useCallback(async () => {
    try {
      const res = await api.get("/admin/me", { headers: adminHeaders() });
      setMe(res.data);
    } catch {
      writeAdminHint("");
      window.location.reload();
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  return (
    <div className="space-y-4" data-testid="owner-panel">
      {/* Tab strip */}
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex items-center rounded-full border border-border/70 bg-card/60 p-0.5 text-xs flex-wrap">
          <TabBtn active={tab === "dashboard"} onClick={() => setTab("dashboard")} icon={BarChart3} label="Overview" testId="owner-tab-dashboard" />
          <TabBtn active={tab === "analytics"} onClick={() => setTab("analytics")} icon={LineChart} label="Analytics" testId="owner-tab-analytics" />
          <TabBtn active={tab === "users"}     onClick={() => setTab("users")}     icon={Users}    label="Users"   testId="owner-tab-users" />
          <TabBtn active={tab === "portfolio"} onClick={() => setTab("portfolio")} icon={Briefcase} label="Portfolio" testId="owner-tab-portfolio" />
          <TabBtn active={tab === "homepage"}  onClick={() => setTab("homepage")}  icon={Sparkles} label="Homepage" testId="owner-tab-homepage" />
          <TabBtn active={tab === "early_access"} onClick={() => setTab("early_access")} icon={Mail} label="Early Access" testId="owner-tab-early-access" />
          <TabBtn active={tab === "account"}   onClick={() => setTab("account")}   icon={Settings} label="Account" testId="owner-tab-account" />
          <TabBtn active={tab === "system"}    onClick={() => setTab("system")}    icon={Activity} label="System" testId="owner-tab-system" />
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:inline text-[10px] text-muted-foreground inline-flex items-center gap-1">
            <Lock className="w-3 h-3" /> Session expires in 60 min
          </div>
          <Button size="sm" variant="ghost" onClick={onLogout} data-testid="owner-logout">
            <LogOut className="w-3.5 h-3.5 mr-1" /> Sign out
          </Button>
        </div>
      </div>

      {tab === "dashboard" && (
        <OwnerDashboard />
      )}
      {tab === "analytics" && (
        <OwnerAnalyticsPanel />
      )}
      {tab === "account" && (
        <AccountPanel me={me} onReload={loadMe} onPlanChanged={onPlanChanged} />
      )}
      {tab === "users" && (
        <UsersPanel
          selfUserId={selfUserId}
          onSelfPlanChanged={async () => { await onPlanChanged?.(); await loadMe(); }}
          onOpenPortfolioDiagnostics={openPortfolioDiagnosticsFor}
        />
      )}
      {tab === "portfolio" && (
        <>
          <PerformancePreviewSeedPanel presetUserId={portfolioDiagPresetUserId} />
          <SeededPortfolioChartPreview />
          <PortfolioDiagnosticsPanel presetUserId={portfolioDiagPresetUserId} />
        </>
      )}
      {tab === "homepage" && (
        <HomepageSnapshotPanel />
      )}
      {tab === "early_access" && (
        <EarlyAccessPanel />
      )}
      {tab === "system" && (
        <SystemPanel />
      )}
    </div>
  );
}

// =============================================================================
// Phase 8.E.4 — Portfolio Performance Preview Seed + Diagnostics panel
//
// Owner/Admin-only. Read-only by default. Preview-seed controls are visibly
// disabled outside Preview or when the ALLOW_PORTFOLIO_PERF_SEED flag is off.
// Explicit confirmation is required for every write.  No auto-invocation.
// =============================================================================

function PerformancePreviewSeedPanel({ presetUserId = "" }) {
  const [status, setStatus] = React.useState(null);   // {environment, seed_controls}
  const [statusUserId, setStatusUserId] = React.useState(presetUserId || "");
  const [statusLoading, setStatusLoading] = React.useState(false);
  const [perfReport, setPerfReport] = React.useState(null);
  const [statusError, setStatusError] = React.useState(null);
  const [range, setRange] = React.useState("3M");

  const [seedRunId, setSeedRunId] = React.useState("");
  const [seedConfirm, setSeedConfirm] = React.useState(false);
  const [seedBusy, setSeedBusy] = React.useState(false);
  const [seedResult, setSeedResult] = React.useState(null);
  const [seedError, setSeedError] = React.useState(null);

  const [cleanupRunId, setCleanupRunId] = React.useState("");
  const [cleanupConfirm, setCleanupConfirm] = React.useState(false);
  const [cleanupBusy, setCleanupBusy] = React.useState(false);
  const [cleanupResult, setCleanupResult] = React.useState(null);
  const [cleanupError, setCleanupError] = React.useState(null);
  const [cleanupDryRun, setCleanupDryRun] = React.useState(true);

  React.useEffect(() => {
    if (presetUserId) setStatusUserId(presetUserId);
  }, [presetUserId]);

  const isPreview = status?.environment === "preview";
  const seedAllowed = !!status?.seed_controls?.seed_allowed;
  const flagEnabled = !!status?.seed_controls?.feature_flag_enabled;

  const loadDiagnostics = async (targetUserId) => {
    const uid = (targetUserId || statusUserId || "").trim();
    if (!uid) {
      setStatusError("Enter a user_id first.");
      return;
    }
    setStatusLoading(true);
    setStatusError(null);
    setPerfReport(null);
    try {
      const res = await api.get(
        "/admin/debug/portfolio/performance-diagnostics",
        {
          headers: adminHeaders(),
          params: { user_id: uid, range, include_transition_details: false },
        },
      );
      const payload = res?.data || {};
      setStatus({
        environment: payload.environment,
        seed_controls: payload.seed_controls,
      });
      setPerfReport(payload.performance || null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setStatusError(
        typeof detail === "string" ? detail
          : detail?.message || e?.message || "Failed to load diagnostics.",
      );
    } finally {
      setStatusLoading(false);
    }
  };

  const runSeed = async () => {
    if (!seedRunId || !seedConfirm) return;
    setSeedBusy(true);
    setSeedError(null);
    setSeedResult(null);
    try {
      const res = await api.post(
        "/admin/debug/portfolio/performance-seed",
        { test_run_id: seedRunId, confirm: true, scenario_name: "default" },
        { headers: adminHeaders() },
      );
      setSeedResult(res?.data || null);
      toast.success("Preview seed applied.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setSeedError(
        typeof detail === "string" ? detail
          : detail?.message || e?.message || "Seed request failed.",
      );
      toast.error("Preview seed rejected.");
    } finally {
      setSeedBusy(false);
      setSeedConfirm(false);
    }
  };

  const runCleanup = async () => {
    if (!cleanupRunId) return;
    if (!cleanupDryRun && !cleanupConfirm) return;
    setCleanupBusy(true);
    setCleanupError(null);
    setCleanupResult(null);
    try {
      const res = await api.post(
        "/admin/debug/portfolio/performance-seed/cleanup",
        {
          test_run_id: cleanupRunId,
          confirm: !cleanupDryRun,
          dry_run: cleanupDryRun,
        },
        { headers: adminHeaders() },
      );
      setCleanupResult(res?.data || null);
      toast.success(cleanupDryRun ? "Dry-run report ready." : "Cleanup complete.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setCleanupError(
        typeof detail === "string" ? detail
          : detail?.message || e?.message || "Cleanup request failed.",
      );
      toast.error("Cleanup rejected.");
    } finally {
      setCleanupBusy(false);
      setCleanupConfirm(false);
    }
  };

  const envBadge = (() => {
    const env = status?.environment;
    if (!env) return null;
    const tone = env === "preview" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
      : env === "production" ? "bg-red-100 text-red-800 border-red-300"
      : "bg-amber-100 text-amber-800 border-amber-300";
    return (
      <Badge
        data-testid="perf-diag-env-badge"
        variant="outline"
        className={`${tone} border font-mono uppercase text-[0.65rem] tracking-wider`}
      >
        {env}
      </Badge>
    );
  })();

  return (
    <div
      data-testid="perf-preview-seed-panel"
      className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900"
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <BarChart3 className="h-5 w-5 text-indigo-600" />
        <h2
          data-testid="perf-preview-seed-heading"
          className="text-base font-semibold text-slate-800 dark:text-slate-100"
        >
          Portfolio Performance — Diagnostics &amp; Preview Seed
        </h2>
        {envBadge}
        {flagEnabled && (
          <Badge variant="outline" className="border-indigo-300 bg-indigo-50 text-indigo-800 text-[0.65rem] uppercase tracking-wider">
            Seed flag: on
          </Badge>
        )}
        {status && !flagEnabled && (
          <Badge variant="outline" className="border-slate-300 bg-slate-50 text-slate-600 text-[0.65rem] uppercase tracking-wider">
            Seed flag: off
          </Badge>
        )}
      </div>
      <p className="mb-4 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
        Read-only diagnostics plus <strong>Preview-only</strong> deterministic seed / cleanup.
        Writes are blocked unless environment is <code>preview</code> AND the
        <code className="mx-1">ALLOW_PORTFOLIO_PERF_SEED</code> flag is on AND
        explicit confirmation is given. Never affects real users. All seed rows are tagged
        with <code>is_test_data=true</code>, <code>test_run_id</code>, <code>first_source=preview_seed_v1</code>,
        <code>created_by=phase_8e4_preview_seed</code>, <code>environment=preview</code>.
      </p>

      {/* ── Diagnostics loader ─────────────────────────────────────── */}
      <div className="mb-4 grid gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800 sm:grid-cols-[1fr_120px_auto]">
        <div>
          <Label htmlFor="perf-diag-user-id" className="text-xs font-medium text-slate-700 dark:text-slate-200">
            User ID
          </Label>
          <Input
            id="perf-diag-user-id"
            data-testid="perf-diag-user-id-input"
            value={statusUserId}
            onChange={(e) => setStatusUserId(e.target.value)}
            placeholder="e.g. e2e_phase8e4-run-001"
            className="mt-1 h-8 text-sm"
          />
        </div>
        <div>
          <Label htmlFor="perf-diag-range" className="text-xs font-medium text-slate-700 dark:text-slate-200">
            Range
          </Label>
          <select
            id="perf-diag-range"
            data-testid="perf-diag-range-select"
            className="mt-1 h-8 w-full rounded border border-slate-300 bg-white px-2 text-sm dark:border-slate-600 dark:bg-slate-900"
            value={range}
            onChange={(e) => setRange(e.target.value)}
          >
            {["1M", "3M", "6M", "1Y", "YTD", "ALL"].map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <Button
            data-testid="perf-diag-load-btn"
            type="button"
            size="sm"
            className="h-8"
            onClick={() => loadDiagnostics()}
            disabled={statusLoading || !statusUserId.trim()}
          >
            {statusLoading ? (
              <>
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                Loading…
              </>
            ) : "Load diagnostics"}
          </Button>
        </div>
      </div>

      {statusError && (
        <div
          data-testid="perf-diag-error"
          className="mb-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800"
        >
          {statusError}
        </div>
      )}

      {perfReport && (
        <div
          data-testid="perf-diag-summary"
          className="mb-4 rounded-md border border-slate-200 bg-white p-3 text-xs dark:border-slate-700 dark:bg-slate-900"
        >
          <div className="mb-2 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
            <SummaryCell label="Data quality" value={perfReport.data_quality?.status || perfReport.data_quality} testId="perf-diag-quality" />
            <SummaryCell label="Method" value={perfReport.performance_method || perfReport.method_used || perfReport.method_preference} testId="perf-diag-method" />
            <SummaryCell label="Baseline" value={perfReport.performance_history_start_date || perfReport.baseline_date} testId="perf-diag-baseline-date" />
            <SummaryCell label="Portfolio return" value={fmtPct(perfReport.portfolio_return_pct)} testId="perf-diag-portfolio-return" />
            <SummaryCell label="Benchmark" value={fmtPct(perfReport.benchmark_return_pct)} testId="perf-diag-benchmark-return" />
            <SummaryCell label="Relative Δ (pp)" value={fmtPct(perfReport.relative_performance_pct_points ?? perfReport.relative_return_pp)} testId="perf-diag-relative" />
            <SummaryCell label="Absorbed opening lots" value={perfReport.diagnostics?.opening_lots_absorbed_into_baseline_count ?? perfReport.opening_lots_absorbed_into_baseline_count ?? perfReport.legacy_transaction_date_fallback_count} testId="perf-diag-absorbed" />
            <SummaryCell label="Valid snapshots" value={perfReport.diagnostics?.valid_snapshots ?? perfReport.valid_snapshots} testId="perf-diag-valid-snapshots" />
            <SummaryCell label="Daily coverage" value={fmtPct((perfReport.daily_coverage_ratio ?? 0) * 100)} testId="perf-diag-coverage" />
          </div>
          {perfReport.performance_method_reason && (
            <div
              className="mt-2 text-[11px] text-slate-600 dark:text-slate-300"
              data-testid="perf-diag-method-reason"
              title="performance_method_reason"
            >
              <span className="font-medium">Reason:</span>{" "}
              <span className="font-mono">{perfReport.performance_method_reason}</span>
            </div>
          )}
          {perfReport.benchmark?.status === "unavailable" && (
            <div
              className="mt-2 text-[11px] text-slate-600 dark:text-slate-300"
              data-testid="perf-diag-benchmark-reason"
            >
              <span className="font-medium">Benchmark unavailable:</span>{" "}
              <span className="font-mono">
                {perfReport.diagnostics?.benchmark_failure_category || "reason_unknown"}
              </span>
            </div>
          )}
          {perfReport.reason_codes?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {perfReport.reason_codes.map((rc) => (
                <Badge
                  key={rc}
                  variant="outline"
                  data-testid={`perf-diag-reason-${rc}`}
                  className="border-slate-300 bg-slate-50 font-mono text-[0.65rem] text-slate-700"
                >
                  {rc}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Preview Seed ─────────────────────────────────────────── */}
      <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/20">
        <div className="mb-2 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          <span className="text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
            Preview Seed (write)
          </span>
          {!seedAllowed && (
            <Badge variant="outline" className="border-slate-400 bg-slate-100 text-slate-600 text-[0.65rem]">
              Disabled outside Preview
            </Badge>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
          <div>
            <Label htmlFor="perf-seed-run-id" className="text-xs text-slate-700 dark:text-slate-200">
              test_run_id
            </Label>
            <Input
              id="perf-seed-run-id"
              data-testid="perf-seed-run-id-input"
              value={seedRunId}
              onChange={(e) => setSeedRunId(e.target.value)}
              placeholder="phase8e4-run-YYYYMMDD-HHMM"
              className="mt-1 h-8 text-sm"
              disabled={!seedAllowed}
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
              <Checkbox
                data-testid="perf-seed-confirm"
                checked={seedConfirm}
                onCheckedChange={(v) => setSeedConfirm(!!v)}
                disabled={!seedAllowed}
              />
              I confirm this is Preview-only seed data
            </label>
          </div>
          <div className="flex items-end">
            <Button
              data-testid="perf-seed-run-btn"
              type="button"
              size="sm"
              variant="default"
              className="h-8"
              onClick={runSeed}
              disabled={!seedAllowed || !seedRunId.trim() || !seedConfirm || seedBusy}
            >
              {seedBusy ? (
                <>
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  Seeding…
                </>
              ) : "Apply seed"}
            </Button>
          </div>
        </div>
        {seedError && (
          <div
            data-testid="perf-seed-error"
            className="mt-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800"
          >
            {seedError}
          </div>
        )}
        {seedResult && (
          <div
            data-testid="perf-seed-result"
            className="mt-2 rounded border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-900"
          >
            <div><strong>Status:</strong> {seedResult.report?.status}</div>
            <div><strong>user_id:</strong> <code>{seedResult.report?.user_id}</code></div>
            <div><strong>email:</strong> <code>{seedResult.report?.email}</code></div>
            <div className="mt-1 font-mono">
              counts: {JSON.stringify(seedResult.report?.counts)}
            </div>
            <Button
              data-testid="perf-seed-load-into-diag-btn"
              type="button"
              variant="link"
              size="sm"
              className="h-7 px-0 text-emerald-900"
              onClick={() => {
                setStatusUserId(seedResult.report?.user_id || "");
                loadDiagnostics(seedResult.report?.user_id);
              }}
            >
              Load seeded user into diagnostics →
            </Button>
          </div>
        )}
      </div>

      {/* ── Cleanup ─────────────────────────────────────────────── */}
      <div className="rounded-md border border-slate-300 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
        <div className="mb-2 flex items-center gap-2">
          <Trash2 className="h-4 w-4 text-slate-700 dark:text-slate-200" />
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-800 dark:text-slate-100">
            Cleanup (scoped to test_run_id)
          </span>
          {!seedAllowed && (
            <Badge variant="outline" className="border-slate-400 bg-slate-100 text-slate-600 text-[0.65rem]">
              Disabled outside Preview
            </Badge>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto_auto]">
          <div>
            <Label htmlFor="perf-cleanup-run-id" className="text-xs text-slate-700 dark:text-slate-200">
              test_run_id
            </Label>
            <Input
              id="perf-cleanup-run-id"
              data-testid="perf-cleanup-run-id-input"
              value={cleanupRunId}
              onChange={(e) => setCleanupRunId(e.target.value)}
              placeholder="exactly match the seeded test_run_id"
              className="mt-1 h-8 text-sm"
              disabled={!seedAllowed}
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
              <Checkbox
                data-testid="perf-cleanup-dry-run"
                checked={cleanupDryRun}
                onCheckedChange={(v) => setCleanupDryRun(!!v)}
                disabled={!seedAllowed}
              />
              Dry-run
            </label>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
              <Checkbox
                data-testid="perf-cleanup-confirm"
                checked={cleanupConfirm}
                onCheckedChange={(v) => setCleanupConfirm(!!v)}
                disabled={!seedAllowed || cleanupDryRun}
              />
              I confirm destructive delete
            </label>
          </div>
          <div className="flex items-end">
            <Button
              data-testid="perf-cleanup-run-btn"
              type="button"
              size="sm"
              variant={cleanupDryRun ? "outline" : "destructive"}
              className="h-8"
              onClick={runCleanup}
              disabled={
                !seedAllowed || !cleanupRunId.trim() || cleanupBusy ||
                (!cleanupDryRun && !cleanupConfirm)
              }
            >
              {cleanupBusy ? (
                <>
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  Working…
                </>
              ) : (cleanupDryRun ? "Preview cleanup" : "Delete seed data")}
            </Button>
          </div>
        </div>
        {cleanupError && (
          <div
            data-testid="perf-cleanup-error"
            className="mt-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800"
          >
            {cleanupError}
          </div>
        )}
        {cleanupResult && (
          <div
            data-testid="perf-cleanup-result"
            className="mt-2 rounded border border-slate-300 bg-white p-2 text-xs text-slate-800 dark:bg-slate-900 dark:text-slate-100"
          >
            <div><strong>Status:</strong> {cleanupResult.report?.status}</div>
            {cleanupResult.report?.would_delete && (
              <div className="mt-1 font-mono">
                would_delete: {JSON.stringify(cleanupResult.report.would_delete)}
              </div>
            )}
            {cleanupResult.report?.deleted && (
              <div className="mt-1 font-mono">
                deleted: {JSON.stringify(cleanupResult.report.deleted)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCell({ label, value, testId }) {
  return (
    <div className="flex flex-col">
      <span className="text-[0.65rem] uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>
      <span
        data-testid={testId}
        className="font-mono text-sm text-slate-800 dark:text-slate-100"
      >
        {value === null || value === undefined || value === "" ? "—" : String(value)}
      </span>
    </div>
  );
}

function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
}

// =============================================================================
// Phase 8.E.4 — Safe Owner-only Seeded Portfolio Chart Preview
//
// Renders the existing `PortfolioGrowthChart` for a *seeded* demo user
// only. All safety gating is enforced by the backend endpoint
// `GET /api/admin/debug/seeded-portfolio-preview` (Owner/Admin +
// Preview environment + 6-tag seed predicate). The panel adds
// in-memory-only diagnostic simulations (never mutates the DB) plus
// a Desktop/Mobile viewport width toggle.
// =============================================================================

const _SEED_PREVIEW_DEFAULT_USER_ID = "e2e_phase8e4-review-2026";

const _SEED_PREVIEW_SIM_MODES = [
  { value: "live",                  label: "Live seeded dataset" },
  { value: "insufficient_history",  label: "Insufficient history" },
  { value: "no_stable_baseline",    label: "No stable baseline" },
  { value: "benchmark_unavailable", label: "Benchmark unavailable" },
  { value: "fair_value_unavailable", label: "Fair Value unavailable" },
  { value: "partial_fair_value",    label: "Partial Fair Value coverage" },
];

function _seedPreviewApplySim(raw, mode) {
  // In-memory only. Never issues a network request, never mutates the DB.
  if (!raw || mode === "live") return raw;
  if (mode === "insufficient_history") {
    // Force the chart into the "insufficient" state (no history at all).
    return {
      ...raw,
      data_quality: "insufficient",
      performance_method: "NONE",
      chart_display_mode: "unavailable",
      portfolio_series: [],
      benchmark_series: [],
      fair_value_series: [],
      portfolio_return_pct: null,
      benchmark_return_pct: null,
      relative_performance_pct_points: null,
      performance_history_start_date: null,
      excluded_pre_baseline_snapshot_dates: [],
      fair_value_coverage_pct: null,
      benchmark: { ...(raw.benchmark || {}), status: "unavailable" },
    };
  }
  if (mode === "no_stable_baseline") {
    // History exists but a stable baseline can't be established yet.
    // Method must not display TWR / Modified Dietz — no valid method
    // exists in this state (per visual-review issue #5).
    return {
      ...raw,
      data_quality: "insufficient",
      performance_method: "NONE",
      chart_display_mode: "unavailable",
      portfolio_series: [],
      benchmark_series: [],
      fair_value_series: [],
      performance_history_start_date: null,
      excluded_pre_baseline_snapshot_dates: [],
      portfolio_return_pct: null,
      benchmark_return_pct: null,
      relative_performance_pct_points: null,
      fair_value_coverage_pct: null,
    };
  }
  if (mode === "benchmark_unavailable") {
    return {
      ...raw,
      benchmark: { ...(raw.benchmark || {}), status: "unavailable" },
      benchmark_series: [],
      benchmark_return_pct: null,
      relative_performance_pct_points: null,
    };
  }
  if (mode === "fair_value_unavailable") {
    return {
      ...raw,
      fair_value_series: [],
      fair_value_coverage_pct: 0,
    };
  }
  if (mode === "partial_fair_value") {
    const src = Array.isArray(raw.fair_value_series) ? raw.fair_value_series : [];
    const filtered = src.filter((_, i) => i % 2 === 0);
    const base = raw.fair_value_coverage_pct;
    return {
      ...raw,
      fair_value_series: filtered,
      fair_value_coverage_pct: (base !== null && base !== undefined)
        ? Math.max(1, Math.round(base * 0.5 * 100) / 100)
        : 50,
    };
  }
  return raw;
}

function SeededPortfolioChartPreview() {
  const [pendingUserId, setPendingUserId] = React.useState(_SEED_PREVIEW_DEFAULT_USER_ID);
  const [userId, setUserId] = React.useState("");                // "" until user clicks Load
  const [simMode, setSimMode] = React.useState("live");
  const [viewport, setViewport] = React.useState("desktop");     // 'desktop' | 'mobile'
  const [rawOpen, setRawOpen] = React.useState(false);

  // Cached state from the last successful fetch (drives the info panel).
  const [lastEnv, setLastEnv] = React.useState(null);
  const [lastRange, setLastRange] = React.useState(null);
  const [lastRaw, setLastRaw] = React.useState(null);
  const [lastSeededUser, setLastSeededUser] = React.useState(null);
  const [lastCoverage, setLastCoverage] = React.useState(null);

  const [errorMsg, setErrorMsg] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const isSimActive = simMode !== "live";

  // Chart-driven fetcher. The chart calls this on every range change
  // (and whenever this function's reference identity changes, which is
  // exactly what we want when the simulation mode toggles — we re-run
  // the transform against the last raw payload).
  const fetchOverride = React.useCallback(async (rangeKey) => {
    if (!userId) return null;
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await api.get(
        "/admin/debug/seeded-portfolio-preview",
        {
          headers: adminHeaders(),
          params: { user_id: userId, range: rangeKey },
        },
      );
      const data = res?.data || {};
      const raw = data.performance || null;
      setLastRaw(raw);
      setLastRange(data.range || rangeKey);
      setLastEnv(data.environment || null);
      setLastSeededUser(data.seeded_user || null);
      setLastCoverage(data.coverage || null);
      return _seedPreviewApplySim(raw, simMode);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErrorMsg(
        typeof detail === "string" ? detail
          : detail?.message || e?.message || "Failed to load seeded preview payload.",
      );
      setLastRaw(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, simMode]);

  // Phase 8.E.4 visual-review — the compact metadata panel must
  // reflect the *simulated* state so the operator sees consistent
  // values with the chart. Recompute it from the transformed payload
  // whenever the simulation mode changes.
  const simulatedRaw = React.useMemo(
    () => _seedPreviewApplySim(lastRaw, simMode),
    [lastRaw, simMode],
  );
  const simulatedCoverage = React.useMemo(() => {
    if (!lastCoverage) return null;
    if (simMode === "live") return lastCoverage;
    if (simMode === "insufficient_history" || simMode === "no_stable_baseline") {
      return {
        ...lastCoverage,
        snapshot_count: 0,
        benchmark_price_count: simMode === "insufficient_history" ? 0 : lastCoverage.benchmark_price_count,
        fair_value_coverage_pct: null,
      };
    }
    if (simMode === "fair_value_unavailable") {
      return { ...lastCoverage, fair_value_coverage_pct: 0 };
    }
    if (simMode === "partial_fair_value" && simulatedRaw) {
      return { ...lastCoverage, fair_value_coverage_pct: simulatedRaw.fair_value_coverage_pct };
    }
    return lastCoverage;
  }, [lastCoverage, simMode, simulatedRaw]);

  const runLoad = () => {
    const trimmed = (pendingUserId || "").trim();
    setErrorMsg(null);
    if (!trimmed) {
      setErrorMsg("Enter a seeded user_id first.");
      return;
    }
    // Clearing then setting forces the chart's effect to re-run even
    // if the user re-clicks Load with the same id (edge case: the
    // last request failed).
    setUserId("");
    setTimeout(() => setUserId(trimmed), 0);
  };

  const envBadge = (() => {
    if (!lastEnv) return null;
    const tone = lastEnv === "preview" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
      : lastEnv === "production" ? "bg-red-100 text-red-800 border-red-300"
      : "bg-amber-100 text-amber-800 border-amber-300";
    return (
      <Badge
        data-testid="seeded-preview-env-badge"
        variant="outline"
        className={`${tone} border font-mono uppercase text-[0.65rem] tracking-wider`}
      >
        {lastEnv}
      </Badge>
    );
  })();

  const methodValue = simulatedRaw?.performance_method && simulatedRaw.performance_method !== "NONE"
    ? simulatedRaw.performance_method : null;
  const qualityValue = simulatedRaw?.data_quality || null;
  const snapshotCount = simulatedCoverage?.snapshot_count ?? null;
  const benchmarkPriceCount = simulatedCoverage?.benchmark_price_count ?? null;
  const fvCoveragePct = simulatedCoverage?.fair_value_coverage_pct
    ?? simulatedRaw?.fair_value_coverage_pct ?? null;

  const viewportContainerClass = viewport === "mobile" ? "max-w-sm mx-auto" : "w-full";

  return (
    <div
      data-testid="seeded-preview-panel"
      className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900"
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <LineChart className="h-5 w-5 text-indigo-600" />
        <h2
          data-testid="seeded-preview-heading"
          className="text-base font-semibold text-slate-800 dark:text-slate-100"
        >
          Seeded Portfolio Chart Preview
        </h2>
        <Badge
          variant="outline"
          className="border-slate-300 bg-slate-50 text-slate-600 text-[0.65rem] uppercase tracking-wider"
          data-testid="seeded-preview-readonly-badge"
        >
          Read-only
        </Badge>
        {envBadge}
      </div>
      <p className="mb-4 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
        Owner-only visual review of the seeded demo user&apos;s{" "}
        <code>PortfolioGrowthChart</code>. Backend enforces{" "}
        <code>environment=preview</code> + all six seed safety tags. Real users
        cannot be rendered here. Diagnostic simulations transform only the
        already-loaded response and never mutate the database.
      </p>

      {/* ── Loader row ─────────────────────────────────────────────── */}
      <div className="mb-4 grid gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800 sm:grid-cols-[1fr_auto]">
        <div>
          <Label htmlFor="seeded-preview-user-id" className="text-xs font-medium text-slate-700 dark:text-slate-200">
            Seeded user_id
          </Label>
          <Input
            id="seeded-preview-user-id"
            data-testid="seeded-preview-user-id-input"
            value={pendingUserId}
            onChange={(e) => setPendingUserId(e.target.value)}
            placeholder="e.g. e2e_phase8e4-review-2026"
            className="mt-1 h-8 text-sm"
          />
        </div>
        <div className="flex items-end">
          <Button
            data-testid="seeded-preview-load-btn"
            type="button"
            size="sm"
            className="h-8"
            onClick={runLoad}
            disabled={loading || !pendingUserId.trim()}
          >
            {loading ? (
              <>
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                Loading…
              </>
            ) : "Load preview"}
          </Button>
        </div>
      </div>

      {errorMsg && (
        <div
          data-testid="seeded-preview-error"
          className="mb-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800"
        >
          {errorMsg}
        </div>
      )}

      {userId && (
        <>
          {/* ── Compact metadata panel ─────────────────────────────── */}
          <div
            data-testid="seeded-preview-metadata"
            className="mb-3 grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-slate-200 bg-white p-3 text-xs dark:border-slate-700 dark:bg-slate-900 sm:grid-cols-4"
          >
            <SummaryCell
              label="Seeded user_id"
              value={lastSeededUser?.user_id || userId}
              testId="seeded-preview-meta-user-id"
            />
            <SummaryCell
              label="test_run_id"
              value={lastSeededUser?.test_run_id}
              testId="seeded-preview-meta-test-run-id"
            />
            <SummaryCell
              label="Range"
              value={lastRange}
              testId="seeded-preview-meta-range"
            />
            <SummaryCell
              label="first_source"
              value={lastSeededUser?.first_source}
              testId="seeded-preview-meta-first-source"
            />
            <SummaryCell
              label="Method"
              value={methodValue}
              testId="seeded-preview-meta-method"
            />
            <SummaryCell
              label="Data quality"
              value={qualityValue}
              testId="seeded-preview-meta-quality"
            />
            <SummaryCell
              label="Snapshots"
              value={snapshotCount}
              testId="seeded-preview-meta-snapshot-count"
            />
            <SummaryCell
              label="Benchmark points"
              value={benchmarkPriceCount}
              testId="seeded-preview-meta-benchmark-count"
            />
            <SummaryCell
              label="Fair Value coverage"
              value={fvCoveragePct === null || fvCoveragePct === undefined
                ? "—" : `${Number(fvCoveragePct).toFixed(2)}%`}
              testId="seeded-preview-meta-fv-coverage"
            />
            <SummaryCell
              label="Portfolio return"
              value={fmtPct(simulatedRaw?.portfolio_return_pct)}
              testId="seeded-preview-meta-portfolio-return"
            />
            <SummaryCell
              label="Benchmark return"
              value={fmtPct(simulatedRaw?.benchmark_return_pct)}
              testId="seeded-preview-meta-benchmark-return"
            />
            <SummaryCell
              label="Relative Δ (pp)"
              value={fmtPct(simulatedRaw?.relative_performance_pct_points)}
              testId="seeded-preview-meta-relative"
            />
          </div>

          {/* ── Simulation + viewport controls ──────────────────────── */}
          <div className="mb-3 grid gap-3 sm:grid-cols-[2fr_auto]">
            <div>
              <Label htmlFor="seeded-preview-sim-select" className="text-xs font-medium text-slate-700 dark:text-slate-200">
                Diagnostic simulation (in-memory only)
              </Label>
              <select
                id="seeded-preview-sim-select"
                data-testid="seeded-preview-sim-select"
                className="mt-1 h-8 w-full rounded border border-slate-300 bg-white px-2 text-sm dark:border-slate-600 dark:bg-slate-900"
                value={simMode}
                onChange={(e) => setSimMode(e.target.value)}
              >
                {_SEED_PREVIEW_SIM_MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <div
                data-testid="seeded-preview-viewport-toggle"
                className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs dark:border-slate-600"
                role="group"
                aria-label="Viewport width"
              >
                <button
                  type="button"
                  data-testid="seeded-preview-viewport-desktop"
                  onClick={() => setViewport("desktop")}
                  className={`inline-flex items-center gap-1 px-2 py-1 ${
                    viewport === "desktop"
                      ? "bg-indigo-600 text-white"
                      : "bg-white text-slate-700 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200"
                  }`}
                >
                  <Monitor className="h-3.5 w-3.5" /> Desktop
                </button>
                <button
                  type="button"
                  data-testid="seeded-preview-viewport-mobile"
                  onClick={() => setViewport("mobile")}
                  className={`inline-flex items-center gap-1 px-2 py-1 border-l border-slate-300 dark:border-slate-600 ${
                    viewport === "mobile"
                      ? "bg-indigo-600 text-white"
                      : "bg-white text-slate-700 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200"
                  }`}
                >
                  <Smartphone className="h-3.5 w-3.5" /> Mobile
                </button>
              </div>
            </div>
          </div>

          {isSimActive && (
            <div
              data-testid="seeded-preview-sim-banner"
              className="mb-3 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-100"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>
                Diagnostic simulation — no data changed. Simulating:{" "}
                <strong>
                  {_SEED_PREVIEW_SIM_MODES.find((m) => m.value === simMode)?.label}
                </strong>
                . This is a frontend-only transformation of the already-loaded response.
              </span>
            </div>
          )}

          {/* ── Chart (reuses existing component unmodified) ────────── */}
          <div
            data-testid="seeded-preview-chart-container"
            className={viewportContainerClass}
          >
            <PortfolioGrowthChart fetchOverride={fetchOverride} />
          </div>

          {/* ── Collapsible raw diagnostics ─────────────────────────── */}
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
            <button
              type="button"
              data-testid="seeded-preview-raw-toggle"
              onClick={() => setRawOpen((v) => !v)}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-700/40"
              aria-expanded={rawOpen}
            >
              <span className="inline-flex items-center gap-2">
                <Eye className="h-3.5 w-3.5" />
                View raw diagnostics
              </span>
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${rawOpen ? "rotate-180" : ""}`}
              />
            </button>
            {rawOpen && (
              <pre
                data-testid="seeded-preview-raw-json"
                className="max-h-64 overflow-auto rounded-b-md border-t border-slate-200 bg-white p-3 font-mono text-[10px] leading-relaxed text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              >
{JSON.stringify(
  {
    environment: lastEnv,
    range: lastRange,
    simulation_mode: simMode,
    viewport,
    seeded_user: lastSeededUser,
    coverage: lastCoverage,
    performance: lastRaw,
  },
  null, 2,
)}
              </pre>
            )}
          </div>
        </>
      )}
    </div>
  );
}



// =============================================================================
// Phase 8.B — Portfolio Diagnostics admin panel
// =============================================================================

function PortfolioDiagnosticsPanel({ presetUserId = "" }) {
  const { t } = useTranslation();
  const [userId, setUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  // Phase 8.C-3 follow-up — Backfill Legacy Opening Transactions state.
  const [backfillReport, setBackfillReport] = useState(null);
  const [backfillConfirmed, setBackfillConfirmed] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(null); // null | 'dry_run' | 'apply'
  const [backfillResolvedUserId, setBackfillResolvedUserId] = useState(null);

  // Phase 8.D — Daily Portfolio Snapshots admin state.
  // Lives at the diagnostics-panel level so admins always see the global
  // job dashboard, independent of any selected user.
  const [dsStatus, setDsStatus] = useState(null);          // GET .../daily-snapshot-status
  const [dsReport, setDsReport] = useState(null);          // last dry-run / apply / retry report
  const [dsBusy, setDsBusy] = useState(null);              // 'status' | 'dry_run' | 'apply' | 'retry'
  const [dsConfirmed, setDsConfirmed] = useState(false);   // apply confirmation gate
  const [dsRetryJobId, setDsRetryJobId] = useState("");    // retry job_run_id (editable)
  const [dsRetryAutoFilled, setDsRetryAutoFilled] = useState(false);
  const [dsForceRefresh, setDsForceRefresh] = useState(false);
  const [dsBatchLimit, setDsBatchLimit] = useState("");
  const [dsBatchLimitError, setDsBatchLimitError] = useState(null);

  // Phase 8.B QA-4 — accept email, name OR user_id. If the query
  // doesn't already look like a user_id, resolve it via the existing
  // admin users-search endpoint.
  const lookupUser = useCallback(async (query) => {
    const q = (query || "").trim();
    if (!q) return null;
    if (/^user_[a-f0-9]+$/i.test(q) || /^[a-f0-9]{16,}$/i.test(q)) {
      return { user_id: q };
    }
    try {
      const resp = await api.get(`/admin/users`, { params: { q, limit: 1 }, headers: adminHeaders() });
      const list = resp?.data?.users || resp?.data || [];
      const u = Array.isArray(list) ? list[0] : null;
      return u && u.user_id ? u : null;
    } catch { return null; }
  }, []);

  const load = useCallback(async (overrideQuery) => {
    // Phase 8.B QA-5 — `load` is wired both as `onClick={load}` AND
    // called directly with a string override (presetUserId, Enter
    // handler). When called from onClick we receive the React event
    // object; guard against `.trim is not a function` by accepting
    // strings only.
    const rawSource = (typeof overrideQuery === "string") ? overrideQuery : userId;
    const raw = (rawSource || "").trim();
    if (!raw) { setError("Enter a user_id, email or name."); return; }
    setError(null);
    setLoading(true);
    // Phase 8.B QA-5 — best-effort client-side resolver (kept as a hint
    // only). The backend now accepts user_id / email / name natively, so
    // we just pass the raw query straight through and let the server
    // normalise it to a canonical user_id.
    try {
      let diagResp;
      try {
        diagResp = await api.get(`/admin/portfolio/diagnostics`, { params: { user_id: raw }, headers: adminHeaders() });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (detail === "user_not_found") {
          setData(null);
          setAudit(null);
          setError("user_not_found");
          return;
        }
        throw e;
      }
      setData(diagResp.data);
      const canonical = diagResp?.data?.selected_user?.user_id || diagResp?.data?.user_id || raw;
      // Keep the input in sync with the canonical id so the admin
      // sees exactly which user the diagnostics belong to.
      if (canonical && canonical !== userId) setUserId(canonical);
      // Snapshots audit is best-effort — never let it wipe the
      // diagnostics result.
      try {
        const aud = await api.get(`/admin/portfolio/snapshots/audit`, { params: { user_id: canonical }, headers: adminHeaders() });
        setAudit(aud.data);
      } catch {
        setAudit(null);
      }
    } catch (e) {
      setData(null);
      setAudit(null);
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
    // `lookupUser` is intentionally omitted — it captures `userId`
    // in its closure and would recreate `load` on every render if
    // included, which would cause the presetUserId effect below to
    // fire in an infinite loop.  Same pattern used across this file.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  React.useEffect(() => {
    if (presetUserId) {
      setUserId(presetUserId);
      load(presetUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetUserId]);

  // Phase 8.D — load global daily-snapshot status on mount and after
  // any successful run. Also auto-fill the retry job_run_id from the
  // most recent failed/partial run when applicable.
  const loadDailySnapshotsStatus = useCallback(async () => {
    setDsBusy("status");
    try {
      const resp = await api.get(
        `/admin/debug/portfolio/daily-snapshot-status`,
        { headers: adminHeaders() }
      );
      const payload = resp?.data || null;
      setDsStatus(payload);
      // Auto-fill retry job_run_id from last failed/partial run
      // unless the admin has already typed something custom.
      const last = payload?.last_job_run || null;
      const lastStatus = (last?.status || "").toLowerCase();
      const lastFailed = (last?.failed_user_ids || []).length > 0;
      if (last?.job_run_id && (lastFailed || lastStatus === "partial" || lastStatus === "failed")) {
        if (!dsRetryJobId || dsRetryAutoFilled) {
          setDsRetryJobId(last.job_run_id);
          setDsRetryAutoFilled(true);
        }
      }
    } catch (e) {
      // Stay silent on 401/403 (not an owner); surface other errors.
      const code = e?.response?.status;
      if (code !== 401 && code !== 403) {
        toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to load daily-snapshot status");
      }
    } finally {
      setDsBusy(null);
    }
  }, [dsRetryJobId, dsRetryAutoFilled]);

  React.useEffect(() => {
    loadDailySnapshotsStatus();
    // `loadDailySnapshotsStatus` is a useCallback whose deps are
    // dsRetryJobId/dsRetryAutoFilled.  We deliberately want this
    // effect to run once on mount (owner opens the console) and NOT
    // re-run every time those pieces of local state change, which
    // would trigger a second network call per keystroke into the
    // retry job-id field.  The status is refreshed elsewhere after
    // successful actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAction = useCallback(async (path, confirmText) => {
    const id = userId.trim();
    if (!id) return;
    if (!window.confirm(confirmText)) return;
    setBusyAction(path);
    try {
      const resolved = await lookupUser(id);
      const target = resolved?.user_id || id;
      await api.post(path, { user_id: target }, { headers: adminHeaders() });
      toast.success("Action completed.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusyAction(null);
    }
    // `load` is stable across the intent of this callback; `lookupUser`
    // is intentionally not a dep for the same closure-capture reason
    // used across this file.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, load]);

  const fields = data ? [
    ["User", data.email || data.user_id],
    ["Plan", data.plan],
    ["Holdings (open)", `${data.open_holdings_count} / ${data.holdings_count}`],
    ["Composition hash", data.composition_hash ? `${data.composition_hash.slice(0, 12)}…` : "—"],
    ["Latest snapshot", data.latest_snapshot_date || "—"],
    ["Snapshot count", `${data.snapshot_count} (dups: ${data.duplicate_snapshot_count})`],
    // Phase 8.B QA-5 — explicit valid-snapshot accounting.
    ["Earliest valid snapshot", data.earliest_valid_snapshot_date || "—"],
    ["Latest valid snapshot", data.latest_valid_snapshot_date || "—"],
    ["Valid snapshot day count", data.valid_snapshot_day_count ?? "—"],
    ["Total snapshot count", data.total_snapshot_count ?? data.snapshot_count ?? "—"],
    ["Data completeness", data.portfolio_data_completeness == null ? "—" : `${Math.round(data.portfolio_data_completeness * 100)}%`],
    ["Missing price / FV / score / risk", `${data.holdings_missing_price} / ${data.holdings_missing_fair_value} / ${data.holdings_missing_score} / ${data.holdings_missing_risk}`],
    ["AI terminal status", data.portfolio_ai_terminal_status || "—"],
    ["AI source", data.portfolio_ai_source || "—"],
    ["AI model used", data.portfolio_ai_model_used || "—"],
    ["AI fallback used", data.portfolio_ai_model_fallback || "—"],
    ["AI cache hit / age (s)", `${data.portfolio_ai_cache_hit} / ${data.portfolio_ai_cache_age_seconds ?? "—"}`],
    ["AI prompt / schema / sanitizer", `${data.portfolio_ai_prompt_version ?? "—"} / ${data.portfolio_ai_schema_version ?? "—"} / ${data.portfolio_ai_sanitizer_version ?? "—"}`],
    ["AI refreshes used / remaining", `${data.portfolio_ai_refreshes_used ?? "—"} / ${data.portfolio_ai_refreshes_remaining ?? "—"}`],
    ["AI next refresh at", data.portfolio_ai_next_refresh_at || "—"],
    ["AI job in flight", String(data.portfolio_ai_job_in_flight)],
    ["Last AI error type", data.last_portfolio_ai_error_type || "—"],
    ["Latest snapshot write status", data.latest_snapshot_write_status],
  ] : [];

  return (
    <div className="space-y-4" data-testid="owner-portfolio-diagnostics">
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <Label htmlFor="po-diag-user-id" className="text-xs text-muted-foreground">User ID, email or name</Label>
          <Input
            id="po-diag-user-id"
            data-testid="owner-portfolio-diag-userid"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="user_…  •  email@…  •  Jane Doe"
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          />
        </div>
        <Button onClick={load} disabled={loading} data-testid="owner-portfolio-diag-load">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
          Load diagnostics
        </Button>
      </div>

      {error ? (
        <div className="rounded-md border border-[#FF3DA6]/40 bg-[#FF3DA6]/10 px-3 py-2 text-sm text-[#FF3DA6]" data-testid="owner-portfolio-diag-error">
          {error === "user_not_found"
            ? "No user matches that User ID, email or display name."
            : error}
        </div>
      ) : null}

      {data ? (
        <>
          {/* Phase 8.B QA-5 — selected-user summary card. Always rendered
              first so the admin can confirm WHO they are inspecting before
              reading deeper diagnostic numbers. */}
          {data.selected_user ? (
            <div
              className="rounded-2xl border border-[#3DEBFF]/30 bg-[#3DEBFF]/[0.04] p-4"
              data-testid="owner-portfolio-diag-user-summary"
            >
              <div className="text-[10.5px] uppercase tracking-wider text-[#7CECFF] mb-1.5">Selected user</div>
              <div className="grid sm:grid-cols-4 gap-3 text-xs">
                <div className="min-w-0">
                  <div className="text-muted-foreground text-[10.5px]">Name</div>
                  <div className="font-medium truncate" data-testid="diag-user-name">{data.selected_user.name || "—"}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-muted-foreground text-[10.5px]">Email</div>
                  <div className="font-mono truncate" data-testid="diag-user-email">{data.selected_user.email || "—"}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-[10.5px]">Plan</div>
                  <div className="capitalize" data-testid="diag-user-plan">{data.selected_user.plan || "—"}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-muted-foreground text-[10.5px]">Canonical User ID</div>
                  <div className="font-mono text-[11px] truncate" data-testid="diag-user-canonical-id">{data.selected_user.user_id || data.user_id}</div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Phase 8.B QA-5 — user exists but has no portfolio.
              Distinct empty state so admins don't see a confusing
              generic "Not Found". */}
          {data.has_portfolio === false ? (
            <div
              className="rounded-2xl border border-border/70 bg-card/60 p-6 text-center"
              data-testid="owner-portfolio-diag-no-portfolio"
            >
              <Briefcase className="w-5 h-5 text-muted-foreground mx-auto mb-2" />
              <div className="text-sm font-medium">This user does not currently have a portfolio.</div>
              <div className="text-[11px] text-muted-foreground mt-1">
                No holdings and no snapshots have been recorded for {data.selected_user?.email || data.selected_user?.user_id || "this account"}.
              </div>
            </div>
          ) : (
          <div className="rounded-2xl border border-border/70 bg-card/60 p-4" data-testid="owner-portfolio-diag-results">
            <div className="font-display text-base mb-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-[#3DEBFF]" />
              Diagnostics
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
              {fields.map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-3 border-b border-white/5 py-1">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="text-foreground tabular text-right truncate">{String(v ?? "—")}</span>
                </div>
              ))}
            </div>
          </div>
          )}

          {audit ? (
            <div className="rounded-2xl border border-border/70 bg-card/60 p-4" data-testid="owner-portfolio-diag-audit">
              <div className="font-display text-base mb-2 flex items-center gap-2">
                <Database className="w-4 h-4 text-[#3DEBFF]" />
                Snapshots audit
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Total snapshots</span><span className="tabular">{audit.total_snapshots}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Duplicate user+date</span><span className="tabular">{audit.duplicate_count}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Missing market value</span><span className="tabular">{audit.missing_market_value}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Missing fair value</span><span className="tabular">{audit.missing_fair_value}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Zero market value</span><span className="tabular">{audit.zero_market_value_count}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Missing composition hash</span><span className="tabular">{audit.missing_composition_hash}</span></div>
              </div>
            </div>
          ) : null}

          {/* Phase 8.C-3 — ledger recompute report (rendered after the
              admin clicks "Recalculate portfolio ledger"). Read-only —
              never mutates persisted data. */}
          {data && data._ledger_report ? (
            <div
              className="rounded-2xl border border-border/70 bg-card/60 p-4"
              data-testid="owner-portfolio-diag-ledger-report"
            >
              <div className="font-display text-base mb-2 flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-[#3DEBFF]" />
                Recalculate portfolio ledger
                <span className={`ml-auto text-[11px] px-2 py-0.5 rounded-full ${data._ledger_report.mismatch_count === 0 ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>
                  {data._ledger_report.integrity_status} · {data._ledger_report.mismatch_count} mismatch(es)
                </span>
              </div>
              {data._ledger_report.extended_diagnostics ? (
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono">
                  {Object.entries(data._ledger_report.extended_diagnostics).map(([k, v]) => (
                    <div key={k} className="flex items-baseline justify-between gap-3 border-b border-white/5 py-1">
                      <span className="text-muted-foreground" data-testid={`owner-portfolio-diag-ledger-key-${k}`}>{k}</span>
                      <span className="text-foreground tabular text-right truncate" data-testid={`owner-portfolio-diag-ledger-val-${k}`}>{String(v ?? "—")}</span>
                    </div>
                  ))}
                </div>
              ) : null}
              {data._ledger_report.audit_id ? (
                <div className="text-[10px] text-muted-foreground mt-2">
                  Audit id: <span className="font-mono">{data._ledger_report.audit_id}</span> · ran_at {data._ledger_report.ran_at}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Phase 8.B QA-6 — last manual-refresh job stage trace so admins
              can pinpoint where a failed Update Portfolio Analysis died. */}
          {data && data.last_refresh_job ? (
            <div
              className="rounded-2xl border border-border/70 bg-card/60 p-4"
              data-testid="owner-portfolio-diag-last-refresh-job"
            >
              <div className="font-display text-base mb-2 flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-[#3DEBFF]" />
                Last manual refresh job
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono">
                {[
                  ["correlation_id", data.last_refresh_job.correlation_id || "—"],
                  ["status", data.last_refresh_job.status || "—"],
                  ["failure_stage", data.last_refresh_job.failure_stage || "—"],
                  ["error_type", data.last_refresh_job.error_type || "—"],
                  ["client_visible_error_type", data.last_refresh_job.client_visible_error_type || "—"],
                  ["terminal_status", data.last_refresh_job.terminal_status || "—"],
                  ["locale", data.last_refresh_job.locale || "—"],
                  ["composition_hash", (data.last_refresh_job.composition_hash || "").slice(0, 16) + (data.last_refresh_job.composition_hash ? "…" : "—")],
                  ["provider_status", data.last_refresh_job.provider_status || "—"],
                  ["sanitizer_status", data.last_refresh_job.sanitizer_status || "—"],
                  ["persistence_status", data.last_refresh_job.persistence_status || "—"],
                  ["cache_write_status", data.last_refresh_job.cache_write_status || "—"],
                  ["snapshot_write_status", data.last_refresh_job.snapshot_write_status || "—"],
                  ["response_serialization_status", data.last_refresh_job.response_serialization_status || "—"],
                  ["refresh_allowance_consumed", String(data.last_refresh_job.refresh_allowance_consumed ?? "—")],
                  ["request_started_at", (data.last_refresh_job.request_started_at || "").replace("T", " ").slice(0, 19) || "—"],
                  ["request_finished_at", (data.last_refresh_job.request_finished_at || "").replace("T", " ").slice(0, 19) || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-3 border-b border-white/5 py-1">
                    <span className="text-muted-foreground text-[10.5px]">{k}</span>
                    <span className="text-foreground/90 truncate" data-testid={`diag-refresh-${k}`}>{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              data-testid="owner-portfolio-diag-clear-cache"
              onClick={() => runAction("/admin/portfolio/clear-review-cache", "Clear cached AI review for this user?")}
              disabled={busyAction !== null}
            >
              {busyAction === "/admin/portfolio/clear-review-cache" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
              Clear AI review cache
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid="owner-portfolio-diag-rebuild-snapshot"
              onClick={() => runAction("/admin/portfolio/rebuild-today-snapshot", "Rebuild today's snapshot for this user?")}
              disabled={busyAction !== null}
            >
              {busyAction === "/admin/portfolio/rebuild-today-snapshot" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
              Rebuild today&apos;s snapshot
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid="owner-portfolio-diag-recalc-metrics"
              onClick={() => runAction("/admin/portfolio/recalc-deterministic", "Rerun deterministic metrics for this user?")}
              disabled={busyAction !== null}
            >
              {busyAction === "/admin/portfolio/recalc-deterministic" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Rerun deterministic metrics
            </Button>
            {/* Phase 8.C-3 — read-only ledger recompute. Reports
                mismatches between transaction-derived aggregates and
                stored portfolio state. Auto-correction is intentionally
                NOT performed. */}
            <Button
              size="sm"
              variant="outline"
              data-testid="owner-portfolio-diag-recalc-ledger"
              onClick={async () => {
                const id = userId.trim();
                if (!id) return;
                setBusyAction("recompute-ledger");
                try {
                  const resolved = await lookupUser(id);
                  const target = resolved?.user_id || id;
                  const resp = await api.get(`/portfolio/diagnostics/ledger-integrity`, {
                    params: { user_id: target },
                    headers: adminHeaders(),
                  });
                  const report = resp?.data?.report || {};
                  const mismatchCount = report.mismatch_count ?? 0;
                  toast.success(`Ledger recompute complete · ${mismatchCount} mismatch(es)`, {
                    description: mismatchCount === 0
                      ? "Ledger is consistent with stored aggregates."
                      : "Mismatches detected — see report below.",
                  });
                  setData((prev) => prev ? { ...prev, _ledger_report: report } : prev);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Action failed");
                } finally {
                  setBusyAction(null);
                }
              }}
              disabled={busyAction !== null}
            >
              {busyAction === "recompute-ledger" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Recalculate portfolio ledger
            </Button>
          </div>

          {/* Phase 8.C-3 follow-up — Backfill Legacy Opening Transactions.
              Admin-only debug action that runs a dry-run first, lets the
              admin review the planned synthetic BUY transactions, then
              requires an explicit confirmation checkbox before applying.
              Never modifies aggregates; only inserts NEW transaction rows
              that reconstruct the existing aggregate. */}
          <div
            className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 mt-4"
            data-testid="owner-portfolio-diag-backfill-card"
          >
            <div className="font-display text-base flex items-center gap-2 mb-2">
              <Database className="w-4 h-4 text-amber-300" />
              <span data-testid="owner-portfolio-diag-backfill-heading">{t("portfolio.backfill_legacy.heading")}</span>
              <span className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">
                legacy_opening_lot_v1
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3" data-testid="owner-portfolio-diag-backfill-description">
              {t("portfolio.backfill_legacy.description")}
            </p>

            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                size="sm"
                variant="outline"
                data-testid="owner-portfolio-diag-backfill-dry-run"
                disabled={backfillBusy !== null}
                onClick={async () => {
                  const id = userId.trim();
                  if (!id) { toast.error("Enter a user_id, email or name first."); return; }
                  setBackfillBusy("dry_run");
                  setBackfillConfirmed(false);
                  setBackfillReport(null);
                  try {
                    const resolved = await lookupUser(id);
                    const target = resolved?.user_id || id;
                    setBackfillResolvedUserId(target);
                    const resp = await api.post(
                      `/admin/debug/portfolio/backfill-legacy-opening-lots`,
                      { dry_run: true, user_id: target },
                      { headers: adminHeaders() }
                    );
                    setBackfillReport(resp?.data?.report || null);
                    const r = resp?.data?.report;
                    if (r) {
                      toast.success(`Dry-run · ${r.eligible_lifecycle_count} eligible, ${r.skipped_details?.length || 0} skipped`);
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Dry-run failed");
                  } finally {
                    setBackfillBusy(null);
                  }
                }}
              >
                {backfillBusy === "dry_run" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
                {t("portfolio.backfill_legacy.run_dry_run")}
              </Button>

              <Button
                size="sm"
                variant="default"
                data-testid="owner-portfolio-diag-backfill-apply"
                disabled={
                  backfillBusy !== null
                  || !backfillReport
                  || backfillReport.dry_run === false        // already applied — require new dry-run
                  || (backfillReport.eligible_lifecycle_count ?? 0) === 0
                  || !backfillConfirmed
                }
                onClick={async () => {
                  const target = backfillResolvedUserId || userId.trim();
                  if (!target) return;
                  setBackfillBusy("apply");
                  try {
                    const resp = await api.post(
                      `/admin/debug/portfolio/backfill-legacy-opening-lots`,
                      { dry_run: false, confirm: true, user_id: target },
                      { headers: adminHeaders() }
                    );
                    const r = resp?.data?.report;
                    setBackfillReport(r || null);
                    setBackfillConfirmed(false);
                    if (r) {
                      toast.success(t("portfolio.backfill_legacy.created_count").replace("{count}", String(r.created_transaction_count || 0)));
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Apply failed");
                  } finally {
                    setBackfillBusy(null);
                  }
                }}
              >
                {backfillBusy === "apply" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                {t("portfolio.backfill_legacy.apply")}
              </Button>

              {backfillReport ? (
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid="owner-portfolio-diag-backfill-reset"
                  disabled={backfillBusy !== null}
                  onClick={() => {
                    setBackfillReport(null);
                    setBackfillConfirmed(false);
                    setBackfillResolvedUserId(null);
                  }}
                >
                  <X className="w-3.5 h-3.5 mr-1" />
                  {t("portfolio.backfill_legacy.reset")}
                </Button>
              ) : null}
            </div>

            {/* Report panel (rendered after a dry-run or apply). */}
            {backfillReport ? (
              <div className="space-y-3" data-testid="owner-portfolio-diag-backfill-report">
                <div className="text-[11px] font-mono text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                  <span>migration_version=<span className="text-foreground">{backfillReport.migration_version}</span></span>
                  <span>dry_run=<span className="text-foreground">{String(backfillReport.dry_run)}</span></span>
                  <span>resolved_user_id=<span className="text-foreground">{backfillResolvedUserId || backfillReport.user_id || "—"}</span></span>
                  <span>last_run_at=<span className="text-foreground">{backfillReport.last_run_at || "—"}</span></span>
                  <span data-testid="owner-portfolio-diag-backfill-eligible-count">
                    {t("portfolio.backfill_legacy.eligible_count").replace("{count}", String(backfillReport.eligible_lifecycle_count ?? 0))}
                  </span>
                  <span data-testid="owner-portfolio-diag-backfill-skipped-count">
                    {t("portfolio.backfill_legacy.skipped_count").replace("{count}", String(backfillReport.skipped_details?.length ?? 0))}
                  </span>
                </div>

                {(backfillReport.eligible_details || []).length > 0 ? (
                  <div>
                    <div className="text-xs font-medium text-emerald-300 mb-1">{t("portfolio.backfill_legacy.eligible")}</div>
                    <div className="overflow-x-auto rounded-lg border border-emerald-500/20 bg-card/60">
                      <table className="w-full text-[11px] font-mono">
                        <thead className="bg-emerald-500/10 text-emerald-200">
                          <tr>
                            <th className="text-left px-2 py-1">{t("portfolio.backfill_legacy.column_ticker")}</th>
                            <th className="text-right px-2 py-1">{t("portfolio.backfill_legacy.column_shares")}</th>
                            <th className="text-right px-2 py-1">{t("portfolio.backfill_legacy.column_avg_price")}</th>
                            <th className="text-right px-2 py-1">{t("portfolio.backfill_legacy.column_cost_basis")}</th>
                            <th className="text-right px-2 py-1">{t("portfolio.backfill_legacy.column_reconcile")}</th>
                            <th className="text-left px-2 py-1">{t("portfolio.backfill_legacy.column_purchase_date")}</th>
                            <th className="text-left px-2 py-1">{t("portfolio.backfill_legacy.column_date_status")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {backfillReport.eligible_details.map((row, i) => (
                            <tr key={`elig-${i}`} data-testid={`owner-portfolio-diag-backfill-elig-row-${row.ticker}`} className="border-t border-emerald-500/10">
                              <td className="px-2 py-1 font-semibold text-foreground">{row.ticker}</td>
                              <td className="px-2 py-1 text-right tabular">{row.current_shares}</td>
                              <td className="px-2 py-1 text-right tabular">{row.avg_buy_price}</td>
                              <td className="px-2 py-1 text-right tabular">{row.stored_cost_basis}</td>
                              <td className="px-2 py-1 text-right tabular">{row.reconcile_diff}</td>
                              <td className="px-2 py-1 text-muted-foreground">{row.purchase_date || "—"}</td>
                              <td className="px-2 py-1">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] ${row.date_status === "trusted" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                                  {row.date_status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground italic">{t("portfolio.backfill_legacy.no_eligible")}</div>
                )}

                {(backfillReport.skipped_details || []).length > 0 ? (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">{t("portfolio.backfill_legacy.skipped")}</div>
                    <div className="overflow-x-auto rounded-lg border border-white/10 bg-card/40">
                      <table className="w-full text-[11px] font-mono">
                        <thead className="bg-white/5 text-muted-foreground">
                          <tr>
                            <th className="text-left px-2 py-1">{t("portfolio.backfill_legacy.column_ticker")}</th>
                            <th className="text-left px-2 py-1">{t("portfolio.backfill_legacy.column_reason")}</th>
                            <th className="text-right px-2 py-1">{t("portfolio.backfill_legacy.column_shares")}</th>
                            <th className="text-right px-2 py-1">{t("portfolio.backfill_legacy.column_avg_price")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {backfillReport.skipped_details.map((row, i) => (
                            <tr key={`skip-${i}`} data-testid={`owner-portfolio-diag-backfill-skip-row-${row.ticker}`} className="border-t border-white/5">
                              <td className="px-2 py-1 font-semibold text-foreground">{row.ticker}</td>
                              <td className="px-2 py-1 text-amber-300">{row.skip_reason}</td>
                              <td className="px-2 py-1 text-right tabular">{row.current_shares ?? "—"}</td>
                              <td className="px-2 py-1 text-right tabular">{row.avg_buy_price ?? "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}

                {/* Apply-result block: rendered when the latest report
                    is an applied run (dry_run === false). */}
                {backfillReport.dry_run === false ? (
                  <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3" data-testid="owner-portfolio-diag-backfill-applied">
                    <div className="text-xs font-medium text-emerald-300 mb-1">
                      {t("portfolio.backfill_legacy.created_heading")} · {t("portfolio.backfill_legacy.created_count").replace("{count}", String(backfillReport.created_transaction_count || 0))}
                    </div>
                    <div className="text-[11px] text-muted-foreground mb-1">
                      {t("portfolio.backfill_legacy.affected_tickers")}: <span className="text-foreground font-mono">{(backfillReport.affected_tickers || []).join(", ") || "—"}</span>
                    </div>
                    {(backfillReport.created_transaction_ids || []).length > 0 ? (
                      <details className="text-[10px] font-mono mt-1">
                        <summary className="cursor-pointer text-muted-foreground">transaction_ids ({backfillReport.created_transaction_ids.length})</summary>
                        <ul className="mt-1 pl-3 space-y-0.5">
                          {backfillReport.created_transaction_ids.map((tid) => (
                            <li key={tid} className="text-foreground">{tid}</li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                  </div>
                ) : null}

                {/* Confirmation gate — only shown when there's something
                    to apply and the latest report is still a dry-run. */}
                {backfillReport.dry_run === true && (backfillReport.eligible_lifecycle_count ?? 0) > 0 ? (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                    <Checkbox
                      id="owner-portfolio-diag-backfill-confirm"
                      data-testid="owner-portfolio-diag-backfill-confirm"
                      checked={backfillConfirmed}
                      onCheckedChange={(v) => setBackfillConfirmed(Boolean(v))}
                      className="mt-0.5"
                    />
                    <label
                      htmlFor="owner-portfolio-diag-backfill-confirm"
                      className="text-xs text-amber-100 leading-snug cursor-pointer select-none"
                      data-testid="owner-portfolio-diag-backfill-confirm-label"
                    >
                      {t("portfolio.backfill_legacy.confirm_label")}
                      <div className="text-[10px] text-amber-200/70 mt-1">{t("portfolio.backfill_legacy.production_warning")}</div>
                    </label>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      {/* Phase 8.D — Daily Portfolio Snapshots admin panel. Always
          visible to Owner/Admin; operates globally on all eligible
          Premium users and is independent of the selected diagnostics
          user. Zero LLM calls are made in this flow. */}
      <div
        className="rounded-2xl border border-sky-500/30 bg-sky-500/5 p-4"
        data-testid="owner-daily-snapshots-card"
      >
        <div className="font-display text-base flex items-center gap-2 mb-2">
          <Activity className="w-4 h-4 text-sky-300" />
          <span data-testid="owner-daily-snapshots-heading">
            {t("portfolio.daily_snapshots.heading")}
          </span>
          <span className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300">
            portfolio_daily_snapshots_v1
          </span>
        </div>
        <p
          className="text-xs text-muted-foreground mb-3"
          data-testid="owner-daily-snapshots-description"
        >
          {t("portfolio.daily_snapshots.description")}
        </p>

        {/* Status row. Sourced from /admin/debug/portfolio/daily-snapshot-status. */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-[11px] mb-3"
          data-testid="owner-daily-snapshots-status"
        >
          {(() => {
            const sched = dsStatus?.scheduler || {};
            const lock = dsStatus?.lock || null;
            const env = dsStatus?.environment || "—";
            const eligible = dsStatus?.eligible_user_count ?? "—";
            const today = new Date().toISOString().slice(0, 10);
            const lockHeld = !!lock?.holder;
            return (
              <>
                <div className="rounded-md border border-border/70 bg-card/40 px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("portfolio.daily_snapshots.scheduler")}
                  </div>
                  <div className="font-mono">
                    <span
                      className={sched.enabled ? "text-emerald-300" : "text-amber-300"}
                      data-testid="owner-daily-snapshots-sched-enabled"
                    >
                      {sched.enabled
                        ? t("portfolio.daily_snapshots.enabled")
                        : t("portfolio.daily_snapshots.disabled")}
                    </span>
                    <span className="text-muted-foreground"> · </span>
                    <span
                      className={sched.registered ? "text-emerald-300" : "text-muted-foreground"}
                      data-testid="owner-daily-snapshots-sched-registered"
                    >
                      {sched.registered
                        ? t("portfolio.daily_snapshots.registered")
                        : t("portfolio.daily_snapshots.not_registered")}
                    </span>
                  </div>
                  <div
                    className="text-[10px] text-muted-foreground font-mono mt-0.5"
                    data-testid="owner-daily-snapshots-sched-cron"
                  >
                    {t("portfolio.daily_snapshots.cron")}: {sched.cron || "—"}
                  </div>
                </div>
                <div className="rounded-md border border-border/70 bg-card/40 px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("portfolio.daily_snapshots.lock")}
                  </div>
                  <div
                    className={`font-mono ${lockHeld ? "text-amber-300" : "text-emerald-300"}`}
                    data-testid="owner-daily-snapshots-lock-state"
                  >
                    {lockHeld
                      ? t("portfolio.daily_snapshots.lock_status_held")
                          .replace("{holder}", String(lock.holder).slice(0, 12) + "…")
                          .replace("{since}", lock.acquired_at || "—")
                      : t("portfolio.daily_snapshots.lock_status_free")}
                  </div>
                </div>
                <div className="rounded-md border border-border/70 bg-card/40 px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("portfolio.daily_snapshots.env")} · {t("portfolio.daily_snapshots.snapshot_date_label")}
                  </div>
                  <div className="font-mono" data-testid="owner-daily-snapshots-env">
                    <span
                      className={env === "production" ? "text-pink-300" : "text-emerald-300"}
                    >
                      {env}
                    </span>
                    <span className="text-muted-foreground"> · </span>
                    <span data-testid="owner-daily-snapshots-utc-date">{today}</span>
                  </div>
                  <div
                    className="text-[10px] text-muted-foreground mt-0.5"
                    data-testid="owner-daily-snapshots-eligible-count"
                  >
                    {t("portfolio.daily_snapshots.eligible_count_estimate")}:{" "}
                    <span className="text-foreground tabular">{eligible}</span>
                  </div>
                </div>
              </>
            );
          })()}
        </div>

        {/* Scheduler-disabled banner — informational; manual runs are still allowed. */}
        {dsStatus && !dsStatus.scheduler?.enabled ? (
          <div
            className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 mb-3"
            data-testid="owner-daily-snapshots-scheduler-banner"
          >
            {t("portfolio.daily_snapshots.scheduler_disabled_banner")}
          </div>
        ) : null}

        {/* Inputs row — user scope (reuses diagnostics userId), batch_limit,
            force_refresh_prices. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("portfolio.daily_snapshots.user_scope_label")}
            </Label>
            <div
              className="text-[11px] text-muted-foreground mt-1 font-mono"
              data-testid="owner-daily-snapshots-scope"
            >
              {userId.trim()
                ? t("portfolio.daily_snapshots.user_scope_selected").replace(
                    "{user_id}", userId.trim()
                  )
                : t("portfolio.daily_snapshots.user_scope_all")}
            </div>
          </div>
          <div>
            <Label
              htmlFor="ds-batch-limit"
              className="text-[10px] uppercase tracking-wider text-muted-foreground"
            >
              {t("portfolio.daily_snapshots.batch_limit_label")}
            </Label>
            <Input
              id="ds-batch-limit"
              data-testid="owner-daily-snapshots-batch-limit"
              value={dsBatchLimit}
              onChange={(e) => {
                const v = e.target.value;
                setDsBatchLimit(v);
                if (!v.trim()) {
                  setDsBatchLimitError(null);
                } else if (!/^\d+$/.test(v.trim()) || Number(v) <= 0) {
                  setDsBatchLimitError(
                    t("portfolio.daily_snapshots.batch_limit_invalid")
                  );
                } else {
                  setDsBatchLimitError(null);
                }
              }}
              placeholder={t("portfolio.daily_snapshots.batch_limit_placeholder")}
              className="mt-1 h-8 text-xs"
            />
            {dsBatchLimitError ? (
              <div
                className="text-[10px] text-pink-300 mt-1"
                data-testid="owner-daily-snapshots-batch-limit-error"
              >
                {dsBatchLimitError}
              </div>
            ) : null}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("portfolio.daily_snapshots.force_refresh_prices")}
            </Label>
            <div className="mt-1 flex items-start gap-2">
              <Checkbox
                id="ds-force-refresh"
                disabled
                data-testid="owner-daily-snapshots-force-refresh"
                checked={dsForceRefresh}
                onCheckedChange={(v) => setDsForceRefresh(Boolean(v))}
                className="mt-0.5"
              />
              <label
                htmlFor="ds-force-refresh"
                className="text-[10px] text-muted-foreground leading-snug cursor-pointer select-none"
                data-testid="owner-daily-snapshots-force-refresh-label"
              >
                {t("portfolio.daily_snapshots.force_refresh_prices_hint")}
              </label>
            </div>
          </div>
        </div>

        {/* Action buttons. */}
        <div className="flex flex-wrap gap-2 mb-3">
          <Button
            size="sm"
            variant="outline"
            data-testid="owner-daily-snapshots-refresh"
            disabled={dsBusy !== null}
            onClick={loadDailySnapshotsStatus}
          >
            {dsBusy === "status" ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
            )}
            {t("portfolio.daily_snapshots.refresh_status")}
          </Button>

          <Button
            size="sm"
            variant="outline"
            data-testid="owner-daily-snapshots-dry-run"
            disabled={dsBusy !== null || dsBatchLimitError !== null}
            onClick={async () => {
              setDsBusy("dry_run");
              setDsConfirmed(false);
              setDsReport(null);
              try {
                const body = { dry_run: true };
                if (userId.trim()) body.user_id = userId.trim();
                if (dsBatchLimit.trim()) body.batch_limit = Number(dsBatchLimit.trim());
                if (dsForceRefresh) body.force_refresh_prices = true;
                const resp = await api.post(
                  `/admin/debug/portfolio/daily-snapshot-job`,
                  body,
                  { headers: adminHeaders() }
                );
                const r = resp?.data?.report || null;
                setDsReport(r);
                if (r) {
                  toast.success(
                    `Dry-run · ${r.total_eligible_users ?? 0} users · ${r.unique_ticker_count ?? 0} unique tickers`
                  );
                }
              } catch (e) {
                toast.error(
                  e?.response?.data?.detail?.message
                  || e?.response?.data?.detail
                  || "Dry-run failed"
                );
              } finally {
                setDsBusy(null);
              }
            }}
          >
            {dsBusy === "dry_run" ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Search className="w-3.5 h-3.5 mr-1" />
            )}
            {t("portfolio.daily_snapshots.run_dry_run")}
          </Button>

          <Button
            size="sm"
            variant="default"
            data-testid="owner-daily-snapshots-apply"
            disabled={
              dsBusy !== null
              || dsBatchLimitError !== null
              || !dsConfirmed
            }
            onClick={async () => {
              setDsBusy("apply");
              try {
                const body = { dry_run: false, confirm: true };
                if (userId.trim()) body.user_id = userId.trim();
                if (dsBatchLimit.trim()) body.batch_limit = Number(dsBatchLimit.trim());
                if (dsForceRefresh) body.force_refresh_prices = true;
                const resp = await api.post(
                  `/admin/debug/portfolio/daily-snapshot-job`,
                  body,
                  { headers: adminHeaders() }
                );
                const r = resp?.data?.report || null;
                setDsReport(r);
                setDsConfirmed(false);
                if (r) {
                  toast.success(
                    `Job ${r.status} · ${r.successful_snapshots || 0} ok / ${r.partial_snapshots || 0} partial / ${r.failed_snapshots || 0} failed`
                  );
                }
                await loadDailySnapshotsStatus();
              } catch (e) {
                toast.error(
                  e?.response?.data?.detail?.message
                  || e?.response?.data?.detail
                  || "Apply failed"
                );
              } finally {
                setDsBusy(null);
              }
            }}
          >
            {dsBusy === "apply" ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
            )}
            {t("portfolio.daily_snapshots.run_now")}
          </Button>

          {/* Retry — disabled when no failed/partial prior run exists. */}
          {(() => {
            const last = dsStatus?.last_job_run || null;
            const lastFailed = (last?.failed_user_ids || []).length > 0;
            const lastStatus = (last?.status || "").toLowerCase();
            const retryAvailable = !!last?.job_run_id
              && (lastFailed || lastStatus === "partial" || lastStatus === "failed");
            return (
              <Button
                size="sm"
                variant="outline"
                data-testid="owner-daily-snapshots-retry"
                disabled={
                  dsBusy !== null
                  || dsBatchLimitError !== null
                  || !dsRetryJobId.trim()
                  || !retryAvailable
                }
                onClick={async () => {
                  if (!dsRetryJobId.trim()) {
                    toast.error(t("portfolio.daily_snapshots.retry_target_required"));
                    return;
                  }
                  setDsBusy("retry");
                  try {
                    const body = {
                      dry_run: false,
                      confirm: true,
                      retry_job_run_id: dsRetryJobId.trim(),
                    };
                    if (dsForceRefresh) body.force_refresh_prices = true;
                    const resp = await api.post(
                      `/admin/debug/portfolio/daily-snapshot-job`,
                      body,
                      { headers: adminHeaders() }
                    );
                    const r = resp?.data?.report || null;
                    setDsReport(r);
                    if (r) {
                      toast.success(
                        `Retry ${r.status} · ${r.successful_snapshots || 0} ok / ${r.partial_snapshots || 0} partial / ${r.failed_snapshots || 0} failed`
                      );
                    }
                    await loadDailySnapshotsStatus();
                  } catch (e) {
                    toast.error(
                      e?.response?.data?.detail?.message
                      || e?.response?.data?.detail
                      || "Retry failed"
                    );
                  } finally {
                    setDsBusy(null);
                  }
                }}
              >
                {dsBusy === "retry" ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />
                )}
                {t("portfolio.daily_snapshots.retry_failed")}
              </Button>
            );
          })()}

          {dsReport ? (
            <Button
              size="sm"
              variant="ghost"
              data-testid="owner-daily-snapshots-reset"
              disabled={dsBusy !== null}
              onClick={() => {
                setDsReport(null);
                setDsConfirmed(false);
              }}
            >
              <X className="w-3.5 h-3.5 mr-1" />
              {t("portfolio.daily_snapshots.reset")}
            </Button>
          ) : null}
        </div>

        {/* Retry job_run_id field — always editable so admins can paste a
            manual override. Auto-filled from the most recent failed /
            partial run when available. */}
        <div className="mb-3">
          <Label
            htmlFor="ds-retry-jobid"
            className="text-[10px] uppercase tracking-wider text-muted-foreground"
          >
            {t("portfolio.daily_snapshots.retry_target_label")}
          </Label>
          <Input
            id="ds-retry-jobid"
            data-testid="owner-daily-snapshots-retry-jobid"
            value={dsRetryJobId}
            onChange={(e) => {
              setDsRetryJobId(e.target.value);
              setDsRetryAutoFilled(false);
            }}
            placeholder={t("portfolio.daily_snapshots.retry_target_placeholder")}
            className="mt-1 h-8 text-xs font-mono"
          />
          {dsRetryAutoFilled && dsRetryJobId ? (
            <div
              className="text-[10px] text-sky-300 mt-1"
              data-testid="owner-daily-snapshots-retry-autofilled"
            >
              {t("portfolio.daily_snapshots.retry_target_autofilled").replace(
                "{status}",
                (dsStatus?.last_job_run?.status || "").toString()
              )}
            </div>
          ) : null}
          {(() => {
            const last = dsStatus?.last_job_run || null;
            const lastFailed = (last?.failed_user_ids || []).length > 0;
            const lastStatus = (last?.status || "").toLowerCase();
            const retryAvailable = !!last?.job_run_id
              && (lastFailed || lastStatus === "partial" || lastStatus === "failed");
            if (retryAvailable) return null;
            return (
              <div
                className="text-[10px] text-muted-foreground mt-1 italic"
                data-testid="owner-daily-snapshots-retry-empty"
              >
                {t("portfolio.daily_snapshots.retry_no_failed")}
              </div>
            );
          })()}
        </div>

        {/* Confirmation gate — amber, matches Legacy Backfill style. */}
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 mb-3"
          data-testid="owner-daily-snapshots-confirm-card"
        >
          <Checkbox
            id="ds-confirm"
            data-testid="owner-daily-snapshots-confirm"
            checked={dsConfirmed}
            onCheckedChange={(v) => setDsConfirmed(Boolean(v))}
            className="mt-0.5"
          />
          <label
            htmlFor="ds-confirm"
            className="text-xs text-amber-100 leading-snug cursor-pointer select-none"
            data-testid="owner-daily-snapshots-confirm-label"
          >
            {t("portfolio.daily_snapshots.confirm_run_now")}
            <div className="text-[10px] text-amber-200/70 mt-1">
              {t("portfolio.daily_snapshots.production_warning")}
            </div>
          </label>
        </div>

        {/* Last job run / current report. */}
        {(() => {
          const r = dsReport || dsStatus?.last_job_run || null;
          if (!r) {
            return (
              <div
                className="text-xs text-muted-foreground italic"
                data-testid="owner-daily-snapshots-no-last-run"
              >
                {t("portfolio.daily_snapshots.no_last_run")}
              </div>
            );
          }
          const isDry = r.dry_run === true || r.status === "dry_run_complete";
          const previewUsers = r.preview_users || [];
          const results = r.results || [];
          const failureReasons = r.failure_reasons || {};
          return (
            <div className="space-y-3" data-testid="owner-daily-snapshots-report">
              <div className="text-[11px] font-mono text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                <span data-testid="owner-daily-snapshots-jobrunid">
                  {t("portfolio.daily_snapshots.job_run_id_label")}=
                  <span className="text-foreground">{r.job_run_id || "—"}</span>
                </span>
                <span>
                  dry_run=<span className="text-foreground">{String(isDry)}</span>
                </span>
                <span>
                  {t("portfolio.daily_snapshots.status_label")}=
                  <span className="text-foreground">{r.status || "—"}</span>
                </span>
                <span>
                  {t("portfolio.daily_snapshots.started_at")}=
                  <span className="text-foreground">
                    {r.started_at ? String(r.started_at) : "—"}
                  </span>
                </span>
                {r.finished_at ? (
                  <span>
                    {t("portfolio.daily_snapshots.finished_at")}=
                    <span className="text-foreground">{String(r.finished_at)}</span>
                  </span>
                ) : null}
                {typeof r.duration_seconds === "number" ? (
                  <span>
                    {t("portfolio.daily_snapshots.duration")}=
                    <span className="text-foreground">
                      {r.duration_seconds.toFixed(2)}
                      {t("portfolio.daily_snapshots.seconds")}
                    </span>
                  </span>
                ) : null}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                <div className="rounded-md border border-border/70 bg-card/40 px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("portfolio.daily_snapshots.success_count")}
                  </div>
                  <div
                    className="font-mono text-emerald-300 tabular"
                    data-testid="owner-daily-snapshots-success-count"
                  >
                    {r.successful_snapshots ?? "—"}
                  </div>
                </div>
                <div className="rounded-md border border-border/70 bg-card/40 px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("portfolio.daily_snapshots.partial_count")}
                  </div>
                  <div
                    className="font-mono text-amber-300 tabular"
                    data-testid="owner-daily-snapshots-partial-count"
                  >
                    {r.partial_snapshots ?? "—"}
                  </div>
                </div>
                <div className="rounded-md border border-border/70 bg-card/40 px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("portfolio.daily_snapshots.failed_count")}
                  </div>
                  <div
                    className="font-mono text-pink-300 tabular"
                    data-testid="owner-daily-snapshots-failed-count"
                  >
                    {r.failed_snapshots ?? "—"}
                  </div>
                </div>
                <div className="rounded-md border border-border/70 bg-card/40 px-2 py-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("portfolio.daily_snapshots.unique_tickers_label")}
                  </div>
                  <div
                    className="font-mono tabular"
                    data-testid="owner-daily-snapshots-unique-tickers"
                  >
                    {r.unique_ticker_count ?? "—"}
                  </div>
                </div>
              </div>

              <div className="text-[11px] font-mono text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                <span>
                  {t("portfolio.daily_snapshots.api_calls")}=
                  <span className="text-foreground">{r.api_calls ?? "—"}</span>
                </span>
                <span>
                  {t("portfolio.daily_snapshots.cache_hits")}=
                  <span className="text-foreground">{r.cache_hits ?? "—"}</span>
                </span>
                {r.retry_of_job_run_id ? (
                  <span>
                    retry_of=
                    <span className="text-foreground">{r.retry_of_job_run_id}</span>
                  </span>
                ) : null}
              </div>

              {/* Dry-run preview users table. */}
              {isDry && previewUsers.length > 0 ? (
                <div>
                  <div
                    className="text-xs font-medium text-sky-300 mb-1"
                    data-testid="owner-daily-snapshots-preview-heading"
                  >
                    {t("portfolio.daily_snapshots.preview_users_heading").replace(
                      "{count}", String(previewUsers.length)
                    )}
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-sky-500/20 bg-card/60">
                    <table className="w-full text-[11px] font-mono">
                      <thead className="bg-sky-500/10 text-sky-200">
                        <tr>
                          <th className="text-left px-2 py-1">
                            {t("portfolio.daily_snapshots.col_user")}
                          </th>
                          <th className="text-right px-2 py-1">
                            {t("portfolio.daily_snapshots.col_open_holdings")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewUsers.map((u) => (
                          <tr
                            key={u.user_id}
                            data-testid={`owner-daily-snapshots-preview-row-${u.user_id}`}
                            className="border-t border-sky-500/10"
                          >
                            <td className="px-2 py-1 text-foreground">
                              {u.email || u.user_id}
                            </td>
                            <td className="px-2 py-1 text-right tabular">
                              {u.open_holdings_count ?? "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {/* Apply / retry per-user results table. Phase 8.D
                  visibility fix — backend now sends the full per-user
                  coverage + persistence detail; render one row per
                  user with derived partial reason. */}
              {!isDry && results.length > 0 ? (
                <div>
                  <div
                    className="text-xs font-medium text-emerald-300 mb-1"
                    data-testid="owner-daily-snapshots-results-heading"
                  >
                    {t("portfolio.daily_snapshots.results_heading")}
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-emerald-500/20 bg-card/60">
                    <table className="w-full text-[11px] font-mono">
                      <thead className="bg-emerald-500/10 text-emerald-200">
                        <tr>
                          <th className="text-left px-2 py-1">
                            {t("portfolio.daily_snapshots.col_user")}
                          </th>
                          <th className="text-left px-2 py-1">
                            {t("portfolio.daily_snapshots.col_status")}
                          </th>
                          <th className="text-left px-2 py-1">
                            {t("portfolio.daily_snapshots.col_persisted")}
                          </th>
                          <th className="text-right px-2 py-1">
                            {t("portfolio.daily_snapshots.col_open_holdings")}
                          </th>
                          <th className="text-left px-2 py-1">
                            {t("portfolio.daily_snapshots.col_coverage")}
                          </th>
                          <th className="text-left px-2 py-1">
                            {t("portfolio.daily_snapshots.col_partial_reason")}
                          </th>
                          <th className="text-left px-2 py-1">
                            {t("portfolio.daily_snapshots.col_missing")}
                          </th>
                          <th className="text-left px-2 py-1">
                            {t("portfolio.daily_snapshots.col_stale")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((row, i) => {
                          // Derived partial reason: which coverage
                          // dimension(s) had missing values.
                          const dims = [];
                          if ((row.missing_price_tickers || []).length) dims.push(t("portfolio.daily_snapshots.dim_price"));
                          if ((row.missing_fair_value_tickers || []).length) dims.push(t("portfolio.daily_snapshots.dim_fair_value"));
                          if ((row.missing_score_tickers || []).length) dims.push(t("portfolio.daily_snapshots.dim_score"));
                          if ((row.missing_risk_tickers || []).length) dims.push(t("portfolio.daily_snapshots.dim_risk"));
                          const partialReason = row.status === "partial" ? dims.join(", ") : "";
                          // Union of all four missing arrays, with
                          // a dimension prefix so admins can see the
                          // exact bucket a ticker fell into.
                          const missingChips = [];
                          (row.missing_price_tickers || []).forEach((tk) =>
                            missingChips.push({ k: "P", t: tk, cls: "bg-pink-500/20 text-pink-300" }));
                          (row.missing_fair_value_tickers || []).forEach((tk) =>
                            missingChips.push({ k: "FV", t: tk, cls: "bg-amber-500/20 text-amber-300" }));
                          (row.missing_score_tickers || []).forEach((tk) =>
                            missingChips.push({ k: "S", t: tk, cls: "bg-sky-500/20 text-sky-300" }));
                          (row.missing_risk_tickers || []).forEach((tk) =>
                            missingChips.push({ k: "R", t: tk, cls: "bg-purple-500/20 text-purple-300" }));
                          const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
                          return (
                            <tr
                              key={`res-${i}`}
                              data-testid={`owner-daily-snapshots-result-row-${row.user_id}`}
                              className="border-t border-emerald-500/10 align-top"
                            >
                              <td className="px-2 py-1 text-foreground">{row.user_id}</td>
                              <td className="px-2 py-1">
                                <span
                                  className={`px-1.5 py-0.5 rounded text-[10px] ${
                                    row.status === "complete"
                                      ? "bg-emerald-500/20 text-emerald-300"
                                      : row.status === "partial"
                                        ? "bg-amber-500/20 text-amber-300"
                                        : "bg-pink-500/20 text-pink-300"
                                  }`}
                                  data-testid={`owner-daily-snapshots-result-status-${row.user_id}`}
                                >
                                  {row.status}
                                </span>
                              </td>
                              <td
                                className="px-2 py-1"
                                data-testid={`owner-daily-snapshots-result-persisted-${row.user_id}`}
                              >
                                {row.snapshot_persisted ? (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-300">
                                    {t("portfolio.daily_snapshots.persisted_yes")}
                                  </span>
                                ) : (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-pink-500/20 text-pink-300">
                                    {t("portfolio.daily_snapshots.persisted_no")}
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1 text-right tabular">
                                {row.open_holdings ?? "—"}
                              </td>
                              <td
                                className="px-2 py-1 text-muted-foreground whitespace-nowrap"
                                data-testid={`owner-daily-snapshots-result-coverage-${row.user_id}`}
                              >
                                <span title={t("portfolio.daily_snapshots.dim_price")}>P {fmtPct(row.price_coverage_pct)}</span>
                                <span className="mx-1">·</span>
                                <span title={t("portfolio.daily_snapshots.dim_fair_value")}>FV {fmtPct(row.fair_value_coverage_pct)}</span>
                                <span className="mx-1">·</span>
                                <span title={t("portfolio.daily_snapshots.dim_score")}>S {fmtPct(row.score_coverage_pct)}</span>
                                <span className="mx-1">·</span>
                                <span title={t("portfolio.daily_snapshots.dim_risk")}>R {fmtPct(row.risk_coverage_pct)}</span>
                                <span className="ml-2 text-foreground">
                                  ∑ {fmtPct(row.snapshot_completeness_pct)}
                                </span>
                              </td>
                              <td
                                className="px-2 py-1 text-amber-200"
                                data-testid={`owner-daily-snapshots-result-partial-reason-${row.user_id}`}
                              >
                                {partialReason || "—"}
                              </td>
                              <td
                                className="px-2 py-1"
                                data-testid={`owner-daily-snapshots-result-missing-${row.user_id}`}
                              >
                                {missingChips.length === 0 ? (
                                  <span className="text-muted-foreground">—</span>
                                ) : (
                                  <div className="flex flex-wrap gap-1">
                                    {missingChips.map((c, ci) => (
                                      <span
                                        key={`mc-${row.user_id}-${ci}`}
                                        className={`px-1 py-0.5 rounded text-[10px] ${c.cls}`}
                                      >
                                        {c.k}:{c.t}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </td>
                              <td
                                className="px-2 py-1 text-muted-foreground"
                                data-testid={`owner-daily-snapshots-result-stale-${row.user_id}`}
                              >
                                {(row.stale_value_tickers || []).length === 0
                                  ? "—"
                                  : (row.stale_value_tickers || []).join(", ")}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {/* Failure reasons block (when apply/retry surfaces failures). */}
              {!isDry && Object.keys(failureReasons).length > 0 ? (
                <div>
                  <div
                    className="text-xs font-medium text-pink-300 mb-1"
                    data-testid="owner-daily-snapshots-failures-heading"
                  >
                    {t("portfolio.daily_snapshots.failed_count")} (
                    {Object.keys(failureReasons).length})
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-pink-500/20 bg-card/40">
                    <table className="w-full text-[11px] font-mono">
                      <thead className="bg-pink-500/10 text-pink-200">
                        <tr>
                          <th className="text-left px-2 py-1">
                            {t("portfolio.daily_snapshots.col_user")}
                          </th>
                          <th className="text-left px-2 py-1">
                            {t("portfolio.daily_snapshots.col_reason")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(failureReasons).map(([uid, reason]) => (
                          <tr
                            key={uid}
                            data-testid={`owner-daily-snapshots-failure-row-${uid}`}
                            className="border-t border-pink-500/10"
                          >
                            <td className="px-2 py-1 text-foreground">{uid}</td>
                            <td className="px-2 py-1 text-pink-200">{String(reason)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${active ? "bg-card text-foreground" : "text-muted-foreground hover:text-foreground"}`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

/* ------------------------ Account tab ------------------------ */

function AccountPanel({ me, onReload, onPlanChanged }) {
  const [busy, setBusy] = useState(false);
  const [interval, setInterval] = useState("monthly");
  const currentPlan = (me?.user?.plan || "free").toLowerCase();

  const switchPlan = async (plan) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/admin/plan", { plan, interval }, { headers: adminHeaders() });
      toast.success(`Account switched to ${plan.charAt(0).toUpperCase() + plan.slice(1)}`, {
        description: "Public-facing entitlements have been updated for your account.",
      });
      await onPlanChanged?.();
      await onReload();
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) {
        toast.error("Admin session expired. Please re-authenticate.");
        writeAdminHint("");
        window.location.reload();
        return;
      }
      toast.error("Failed to switch plan.");
    } finally { setBusy(false); }
  };

  return (
    <Card title="My account" icon={Settings} accent="#E0B341">
      <div className="text-xs text-muted-foreground leading-relaxed">
        Switch your own account between plans for testing, recording marketing
        videos, or verifying paywall behavior. Changes take effect immediately
        and are persisted on your user record.
      </div>

      <div className="mt-5 rounded-lg border border-border/70 bg-card/40 p-3">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Current plan</div>
        <div className="mt-1 flex items-center justify-between gap-2">
          <div className="font-display text-xl capitalize">{currentPlan}</div>
          {me?.user?.billing_interval ? (
            <Badge variant="outline" className="text-[10px] capitalize">{me.user.billing_interval}</Badge>
          ) : null}
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Billing interval (cosmetic)</div>
        <div className="inline-flex items-center rounded-full border border-border/70 bg-card/60 p-0.5 text-xs">
          {["monthly", "yearly"].map((iv) => (
            <button
              key={iv}
              type="button"
              onClick={() => setInterval(iv)}
              data-testid={`owner-interval-${iv}`}
              className={`px-3 py-1 rounded-full transition-colors capitalize ${interval === iv ? "bg-card text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              {iv}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
        {PLAN_META.map((p) => {
          const active = p.key === currentPlan;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => switchPlan(p.key)}
              disabled={busy || active}
              data-testid={`owner-switch-${p.key}`}
              className={`text-left rounded-lg border px-3 py-2.5 transition-all ${active ? "bg-card/80" : "hover:bg-secondary/40"} ${busy ? "opacity-60 cursor-progress" : ""}`}
              style={{ borderColor: active ? p.color : "var(--border)" }}
            >
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: p.color }} />
                <span className="text-sm font-medium" style={{ color: active ? p.color : "inherit" }}>
                  {p.label}
                </span>
                {active ? (
                  <span className="ml-auto text-[9px] uppercase tracking-wider text-muted-foreground">Active</span>
                ) : busy ? (
                  <Loader2 className="w-3 h-3 ml-auto animate-spin text-muted-foreground" />
                ) : null}
              </div>
              <div className="mt-0.5 text-[11px] text-muted-foreground tabular">{p.desc}</div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

/* ------------------------ Users tab ------------------------ */

function UsersPanel({ selfUserId, onSelfPlanChanged, onOpenPortfolioDiagnostics }) {
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState(""); // "" | "free" | "pro" | "premium"
  // Phase 7.OA-1 — extended filters for plan_source/status/usage/lifecycle.
  // Only one extended filter can be active at a time to keep the URL tidy
  // and the table predictable.
  const [extFilter, setExtFilter] = useState(""); // "" | "paying" | "manual" | "active" | "high_usage" | "recent"
  const [categoryFilter, setCategoryFilter] = useState(""); // "" | "real" | "test_seed" | "manual" | "copcart_customer" | "admin"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [planDialog, setPlanDialog] = useState(null); // {user, plan, interval, plan_source, subscription_status, manual_plan_until}
  const [resetDialog, setResetDialog] = useState(null); // user
  const [deleteDialog, setDeleteDialog] = useState(null); // user
  const [deleteConfirm, setDeleteConfirm] = useState(""); // typed-confirm guard

  const fetchData = useCallback(async (query, plan, ext, cat) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      if (plan) params.set("plan", plan);
      if (ext === "paying") params.set("paying_only", "true");
      else if (ext === "manual") params.set("manual_only", "true");
      else if (ext === "active") params.set("active", "true");
      else if (ext === "high_usage") params.set("high_usage", "true");
      else if (ext === "recent") params.set("recently_signed_up", "true");
      if (cat) params.set("user_category", cat);
      params.set("limit", "100");
      const res = await api.get(`/admin/users?${params.toString()}`, { headers: adminHeaders() });
      setData(res.data);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) {
        toast.error("Admin session expired. Please re-authenticate.");
        writeAdminHint("");
        window.location.reload();
        return;
      }
      toast.error("Failed to load users.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    // `fetchData` is intentionally excluded from the dep array — it
    // is a useCallback that closes over unrelated state and would
    // create a fetch loop if included; the filter primitives are
    // the only real triggers.
    fetchData("", planFilter, extFilter, categoryFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFilter, extFilter, categoryFilter]);

  useEffect(() => {
    const t = setTimeout(() => fetchData(q, planFilter, extFilter, categoryFilter), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const planCounts = data?.plan_counts || { total: 0, free: 0, pro: 0, premium: 0 };

  const applyPlanChange = async () => {
    if (!planDialog?.user) return;
    const {
      user: target, plan, interval,
      plan_source, subscription_status, manual_plan_until, reason,
    } = planDialog;
    setBusyId(target.user_id);
    try {
      // Phase 7.OA-1 — PATCH is the new canonical method; POST works too
      // for backwards compat. The extended body fields are optional so
      // legacy callers continue to work.
      await api.patch(
        `/admin/users/${target.user_id}/plan`,
        {
          plan,
          interval,
          plan_source: plan_source || undefined,
          subscription_status: subscription_status || undefined,
          manual_plan_until: manual_plan_until || undefined,
          reason: reason || undefined,
        },
        { headers: adminHeaders() },
      );
      toast.success(`${target.email} → ${plan.charAt(0).toUpperCase() + plan.slice(1)}`);
      setPlanDialog(null);
      await fetchData(q, planFilter, extFilter, categoryFilter);
      if (target.user_id === selfUserId) {
        await onSelfPlanChanged?.();
      }
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) {
        toast.error("Admin session expired.");
        writeAdminHint("");
        window.location.reload();
        return;
      }
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to change plan.");
    } finally { setBusyId(null); }
  };

  const applyResetUsage = async () => {
    if (!resetDialog) return;
    const target = resetDialog;
    setBusyId(target.user_id);
    try {
      const res = await api.post(
        `/admin/users/${target.user_id}/reset-usage`,
        {},
        { headers: adminHeaders() },
      );
      toast.success(`Cleared ${res.data?.deleted ?? 0} usage events for ${target.email}`);
      setResetDialog(null);
      await fetchData(q, planFilter, extFilter, categoryFilter);
    } catch {
      toast.error("Failed to reset usage.");
    } finally { setBusyId(null); }
  };

  const applyDelete = async () => {
    if (!deleteDialog) return;
    const target = deleteDialog;
    setBusyId(target.user_id);
    try {
      const res = await api.delete(
        `/admin/users/${target.user_id}`,
        { headers: adminHeaders() },
      );
      const d = res.data?.deleted || {};
      toast.success(`Deleted ${target.email}`, {
        description: `Removed ${d.watchlists ?? 0} watchlist items, ${d.usage_events ?? 0} usage events, ${d.sessions ?? 0} sessions.`,
      });
      setDeleteDialog(null);
      setDeleteConfirm("");
      await fetchData(q, planFilter, extFilter, categoryFilter);
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 400 && detail === "cannot_delete_self") {
        toast.error("You cannot delete your own admin account.");
      } else if (code === 401) {
        toast.error("Admin session expired.");
        writeAdminHint("");
        window.location.reload();
        return;
      } else {
        toast.error("Failed to delete user.");
      }
    } finally { setBusyId(null); }
  };

  return (
    <Card title="Users" icon={Users} accent="#3182CE">
      {/* Plan-count chips */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="owner-users-plan-chips">
        <PlanChip
          label="All"
          count={planCounts.total}
          active={planFilter === ""}
          onClick={() => setPlanFilter("")}
          color="#A8AEB7"
          testId="owner-filter-all"
        />
        <PlanChip
          label="Free"
          count={planCounts.free}
          active={planFilter === "free"}
          onClick={() => setPlanFilter("free")}
          color={PLAN_COLORS.free}
          testId="owner-filter-free"
        />
        <PlanChip
          label="Pro"
          count={planCounts.pro}
          active={planFilter === "pro"}
          onClick={() => setPlanFilter("pro")}
          color={PLAN_COLORS.pro}
          testId="owner-filter-pro"
        />
        <PlanChip
          label="Premium"
          count={planCounts.premium}
          active={planFilter === "premium"}
          onClick={() => setPlanFilter("premium")}
          color={PLAN_COLORS.premium}
          testId="owner-filter-premium"
        />
      </div>

      {/* Phase 7.OA-1 — secondary filters: source / status / lifecycle. */}
      <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="owner-users-extended-filters">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-1">Status</span>
        <ExtFilterChip label="All" active={extFilter === ""} onClick={() => setExtFilter("")} testId="ext-filter-none" />
        <ExtFilterChip label="Paying (CopeCart)" active={extFilter === "paying"} onClick={() => setExtFilter("paying")} testId="ext-filter-paying" />
        <ExtFilterChip label="Manual / promo" active={extFilter === "manual"} onClick={() => setExtFilter("manual")} testId="ext-filter-manual" />
        <ExtFilterChip label="Active 24h" active={extFilter === "active"} onClick={() => setExtFilter("active")} testId="ext-filter-active" />
        <ExtFilterChip label="High usage" active={extFilter === "high_usage"} onClick={() => setExtFilter("high_usage")} testId="ext-filter-high-usage" />
        <ExtFilterChip label="Signed up 7d" active={extFilter === "recent"} onClick={() => setExtFilter("recent")} testId="ext-filter-recent" />
      </div>
      {/* Phase 7.OA-2 — user category filter: real / test_seed / manual / copcart / admin. */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="owner-users-category-filters">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-1">Category</span>
        <ExtFilterChip label="All" active={categoryFilter === ""} onClick={() => setCategoryFilter("")} testId="cat-filter-all" />
        <ExtFilterChip label="Real" active={categoryFilter === "real"} onClick={() => setCategoryFilter("real")} testId="cat-filter-real" />
        <ExtFilterChip label="Test / Seed" active={categoryFilter === "test_seed"} onClick={() => setCategoryFilter("test_seed")} testId="cat-filter-test" />
        <ExtFilterChip label="Manual / Promo" active={categoryFilter === "manual"} onClick={() => setCategoryFilter("manual")} testId="cat-filter-manual-cat" />
        <ExtFilterChip label="CopeCart customers" active={categoryFilter === "copcart_customer"} onClick={() => setCategoryFilter("copcart_customer")} testId="cat-filter-copcart" />
        <ExtFilterChip label="Admin / Owner" active={categoryFilter === "admin"} onClick={() => setCategoryFilter("admin")} testId="cat-filter-admin" />
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email or name…"
          data-testid="owner-users-search"
          className="pl-9 h-10"
          autoComplete="off"
          spellCheck={false}
        />
        {q ? (
          <button
            type="button"
            onClick={() => setQ("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border/70 bg-card/40 overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="owner-users-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[260px]">User</TableHead>
                <TableHead className="w-[110px]">Plan</TableHead>
                <TableHead className="w-[100px]">Watchlist</TableHead>
                <TableHead className="w-[120px]">Analyses</TableHead>
                <TableHead className="w-[120px]">Last active</TableHead>
                <TableHead className="w-[180px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && !data ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                  <Loader2 className="w-4 h-4 animate-spin inline" /> Loading users…
                </TableCell></TableRow>
              ) : data?.users?.length ? (
                data.users.map((u) => (
                  <UserRow
                    key={u.user_id}
                    u={u}
                    isSelf={u.user_id === selfUserId}
                    busy={busyId === u.user_id}
                    onChangePlan={(plan, interval) => setPlanDialog({ user: u, plan, interval })}
                    onResetUsage={() => setResetDialog(u)}
                    onDelete={() => { setDeleteConfirm(""); setDeleteDialog(u); }}
                    onOpenDiagnostics={() => onOpenPortfolioDiagnostics?.(u.user_id)}
                  />
                ))
              ) : (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                  No users match.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <div className="mt-2 text-[11px] text-muted-foreground" data-testid="owner-users-summary">
        Showing {data?.users?.length ?? 0} of {data?.total ?? 0} users
        {planFilter ? ` · filtered by ${planFilter}` : ""}
        {q ? ` · matching "${q}"` : ""}
      </div>

      {/* Plan change confirmation */}
      <Dialog open={!!planDialog} onOpenChange={(v) => !v && setPlanDialog(null)}>
        <DialogContent data-testid="owner-plan-dialog">
          <DialogHeader>
            <DialogTitle>Change plan</DialogTitle>
            <DialogDescription>
              Switch <span className="font-mono">{planDialog?.user?.email}</span> to{" "}
              <span className="font-medium capitalize" style={{ color: PLAN_COLORS[planDialog?.plan] }}>
                {planDialog?.plan}
              </span>
              {planDialog?.plan !== "free" ? ` (${planDialog?.interval})` : ""}.
              {planDialog?.user?.user_id === selfUserId ? " This affects your own account." : ""}
            </DialogDescription>
          </DialogHeader>

          {/* Phase 7.OA-2 — separated Plan Source vs Subscription Status
              with dark Shadcn dropdowns + conditional Manual Plan
              Expires (only shown for non-CopeCart sources). */}
          {planDialog?.plan !== "free" ? (
            <div className="space-y-3 text-sm" data-testid="owner-plan-dialog-body">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Plan source
                  </Label>
                  <Select
                    value={planDialog?.plan_source || "manual"}
                    onValueChange={(v) =>
                      setPlanDialog((p) => ({
                        ...p,
                        plan_source: v,
                        // Phase 7.OA-3 — when switching plan source, also
                        // default the subscription status to a value that
                        // is internally consistent. CopeCart sources
                        // default to "active"; everything else (manual,
                        // admin, promo, owner_test) defaults to
                        // "manual" (rendered as "Not billed" in the UI).
                        subscription_status:
                          v === "copcart" ? "active" : "manual",
                        // If user switches off a manual-style source,
                        // forget the manual_plan_until so the API doesn't
                        // try to persist an irrelevant date.
                        manual_plan_until: ["manual", "admin", "promo", "owner_test"].includes(v)
                          ? p.manual_plan_until
                          : "",
                        manual_plan_until_local: ["manual", "admin", "promo", "owner_test"].includes(v)
                          ? p.manual_plan_until_local
                          : "",
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1 h-9 bg-card/60 border-border/70" data-testid="owner-plan-source-select">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover text-popover-foreground border-border">
                      <SelectItem value="manual">Manual access</SelectItem>
                      <SelectItem value="admin">Admin override</SelectItem>
                      <SelectItem value="promo">Promo</SelectItem>
                      <SelectItem value="owner_test" disabled>
                        Owner / Test (read-only)
                      </SelectItem>
                      <SelectItem value="copcart" disabled>
                        CopeCart (webhook-only)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Subscription status
                  </Label>
                  <Select
                    value={planDialog?.subscription_status || "manual"}
                    onValueChange={(v) =>
                      setPlanDialog((p) => ({ ...p, subscription_status: v }))
                    }
                  >
                    <SelectTrigger className="mt-1 h-9 bg-card/60 border-border/70" data-testid="owner-sub-status-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover text-popover-foreground border-border">
                      {/* Phase 7.OA-3 — Subscription Status describes
                          ONLY billing state. "Promo" lives on Plan Source.
                          For non-CopeCart sources, "Not billed" is the
                          only sensible status; we still expose the
                          ex-billing states (past_due / cancelled /
                          expired) so admins can document a wound-down
                          subscription. */}
                      <SelectItem value="manual">Not billed</SelectItem>
                      <SelectItem
                        value="active"
                        disabled={(planDialog?.plan_source || "manual") !== "copcart"}
                      >
                        Active{(planDialog?.plan_source || "manual") !== "copcart" ? " (CopeCart-only)" : ""}
                      </SelectItem>
                      <SelectItem
                        value="trial"
                        disabled={(planDialog?.plan_source || "manual") !== "copcart"}
                      >
                        Trial{(planDialog?.plan_source || "manual") !== "copcart" ? " (CopeCart-only)" : ""}
                      </SelectItem>
                      <SelectItem value="past_due">Past due</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Manual Plan Expires — ONLY visible for non-CopeCart sources */}
              {["manual", "admin", "promo", "owner_test"].includes(
                planDialog?.plan_source || "manual"
              ) ? (
                <div>
                  <Label htmlFor="manual-until" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Manual plan expires (optional)
                  </Label>
                  <Input
                    id="manual-until"
                    type="datetime-local"
                    value={planDialog?.manual_plan_until_local || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPlanDialog((p) => ({
                        ...p,
                        manual_plan_until_local: v,
                        manual_plan_until: v ? new Date(v).toISOString() : "",
                      }));
                    }}
                    className="mt-1 h-9"
                    data-testid="owner-manual-until-input"
                  />
                  <p className="text-[10.5px] text-muted-foreground mt-1">
                    When set, the user reverts to Free after this date unless a
                    CopeCart subscription is active by then.
                  </p>
                </div>
              ) : null}

              <div>
                <Label htmlFor="reason" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Reason (optional, logged)
                </Label>
                <Input
                  id="reason"
                  value={planDialog?.reason || ""}
                  onChange={(e) => setPlanDialog((p) => ({ ...p, reason: e.target.value }))}
                  className="mt-1 h-9"
                  placeholder="e.g. influencer comp, refund, support escalation"
                  data-testid="owner-plan-reason-input"
                  maxLength={240}
                />
              </div>

              <p className="text-[11px] text-muted-foreground" data-testid="owner-plan-revenue-note">
                Manual, promo and admin upgrades are <strong>not</strong> counted as revenue.
                Only CopeCart users with status <em>active</em> or <em>trial</em> contribute to MRR/ARR.
              </p>
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPlanDialog(null)} data-testid="owner-plan-dialog-cancel">Cancel</Button>
            <Button onClick={applyPlanChange} disabled={!!busyId} data-testid="owner-plan-dialog-confirm">
              {busyId ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset usage confirmation */}
      <Dialog open={!!resetDialog} onOpenChange={(v) => !v && setResetDialog(null)}>
        <DialogContent data-testid="owner-reset-dialog">
          <DialogHeader>
            <DialogTitle>Reset usage</DialogTitle>
            <DialogDescription>
              This will clear ALL recorded analyses for{" "}
              <span className="font-mono">{resetDialog?.email}</span>, refilling their quota.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResetDialog(null)} data-testid="owner-reset-dialog-cancel">Cancel</Button>
            <Button onClick={applyResetUsage} disabled={!!busyId} data-testid="owner-reset-dialog-confirm">
              {busyId ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset usage"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user confirmation \u2014 destructive, double-guard */}
      <Dialog open={!!deleteDialog} onOpenChange={(v) => { if (!v) { setDeleteDialog(null); setDeleteConfirm(""); } }}>
        <DialogContent data-testid="owner-delete-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2 text-[#E5484D]">
              <Trash2 className="w-4 h-4" />
              <DialogTitle className="text-[#E5484D]">Delete user</DialogTitle>
            </div>
            <DialogDescription>
              Are you sure you want to delete this user? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteDialog ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-[rgba(229,72,77,0.30)] bg-[rgba(229,72,77,0.06)] p-3 text-[12.5px]">
                <div className="font-mono text-foreground">{deleteDialog.email}</div>
                <div className="mt-1 text-muted-foreground text-[11px]">
                  Plan: <span className="capitalize">{deleteDialog.plan}</span> · watchlist {deleteDialog.watchlist_count} · analyses {deleteDialog.analyses_used}
                </div>
              </div>
              <div className="text-[12px] text-muted-foreground leading-[1.5]">
                The following will be permanently removed:
                <ul className="mt-1 list-disc list-inside text-muted-foreground/85">
                  <li>User account &amp; authentication</li>
                  <li>Watchlist entries</li>
                  <li>All usage events</li>
                  <li>Active sessions</li>
                </ul>
              </div>
              <div>
                <Label htmlFor="delete-confirm" className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Type DELETE to confirm
                </Label>
                <Input
                  id="delete-confirm"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  data-testid="owner-delete-confirm-input"
                  className="mt-1 h-9 font-mono"
                  placeholder="DELETE"
                />
              </div>
            </div>
          ) : null}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDeleteDialog(null); setDeleteConfirm(""); }} data-testid="owner-delete-dialog-cancel">
              Cancel
            </Button>
            <Button
              onClick={applyDelete}
              disabled={!!busyId || deleteConfirm.trim() !== "DELETE"}
              data-testid="owner-delete-dialog-confirm"
              className="bg-[#E5484D] hover:bg-[#D03C40] text-white border-[#E5484D]"
            >
              {busyId ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete permanently</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PlanChip({ label, count, active, onClick, color, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
        active ? "bg-card text-foreground" : "bg-card/40 text-muted-foreground hover:text-foreground"
      }`}
      style={{ borderColor: active ? `${color}88` : "var(--border)" }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      <span className="font-medium">{label}</span>
      <span className="font-mono tabular text-muted-foreground">{count}</span>
    </button>
  );
}

/**
 * Phase 7.OA-1 — Lightweight chip used for secondary user-table filters
 * (paying, manual, active 24h, high usage, recent signup). Visual style
 * intentionally calmer than PlanChip (no coloured dot) so the primary
 * plan filter remains the dominant control.
 */
function ExtFilterChip({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
        active
          ? "bg-card text-foreground border-foreground/40"
          : "bg-card/40 text-muted-foreground border-border/60 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

function UserRow({ u, isSelf, busy, onChangePlan, onResetUsage, onDelete, onOpenDiagnostics }) {
  const [planOpen, setPlanOpen] = useState(false);
  const planColor = PLAN_COLORS[u.plan] || "#7D8592";

  // Phase 8.B QA-4 — localized tooltip / aria-label for the
  // "Open Portfolio Diagnostics" action. Owner Console is mostly
  // English, but admins flipping into DE locale should still get
  // the German label here.
  const diagTooltip = (getLocale && getLocale() === "de")
    ? "Portfolio-Diagnose öffnen"
    : "Open Portfolio Diagnostics";

  const planLabel = `${u.plan.charAt(0).toUpperCase() + u.plan.slice(1)}${
    u.billing_interval ? ` · ${u.billing_interval}` : ""
  }`;

  return (
    <TableRow data-testid="owner-user-row" data-user-id={u.user_id}>
      <TableCell className="py-2.5">
        <div className="flex items-center gap-2">
          <span
            className="w-7 h-7 shrink-0 rounded-md inline-flex items-center justify-center text-[10px] font-mono uppercase"
            style={{ background: `${planColor}22`, color: planColor, border: `1px solid ${planColor}44` }}
          >
            {(u.email || "?").slice(0, 2)}
          </span>
          <div className="min-w-0">
            <div className="text-[13px] font-medium truncate flex items-center gap-1.5">
              {u.email}
              {isSelf ? <Badge variant="outline" className="text-[9px] uppercase tracking-wider">You</Badge> : null}
            </div>
            <div className="text-[10.5px] text-muted-foreground truncate">
              {u.name || "—"} · {u.auth_provider} · signed up {fmtDate(u.signup_at)}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell className="py-2.5">
        <Badge
          variant="outline"
          className="text-[10.5px] capitalize"
          style={{ color: planColor, borderColor: `${planColor}55` }}
          data-testid="owner-user-plan-badge"
        >
          {planLabel}
        </Badge>
        <div className="mt-0.5 flex items-center gap-1 flex-wrap text-[10px] text-muted-foreground">
          {u.user_category ? (
            <span
              className="px-1.5 py-[1px] rounded-md border border-border/60 bg-secondary/40 capitalize"
              data-testid="owner-user-category-badge"
              title={`Category: ${u.user_category}`}
            >
              {u.user_category === "test_seed" ? "Test/Seed"
                : u.user_category === "copcart_customer" ? "CopeCart"
                : u.user_category.replace("_", " ")}
            </span>
          ) : null}
          <span data-testid="owner-user-plan-source" className="capitalize">
            {(() => {
              const s = u.plan_source || (u.plan === "free" ? "free" : "manual");
              const isTest = !!u.latest_payment_test_mode;
              if (s === "manual") return "Manual access";
              if (s === "admin") return "Admin override";
              if (s === "promo") return "Promo";
              if (s === "copcart" || s === "copecart") return isTest ? "CopeCart Test" : "CopeCart";
              if (s === "owner_test") return "Owner / Test";
              return s;
            })()}
          </span>
          {u.latest_payment_test_mode ? (
            <Badge
              variant="outline"
              className="ml-1 px-1 py-0 h-4 text-[9px] uppercase tracking-wider border-amber-500/60 text-amber-700 dark:text-amber-300 bg-amber-500/10"
              data-testid="owner-user-test-payment-badge"
              title="Sandbox / test payment — not counted as revenue"
            >
              TEST
            </Badge>
          ) : null}
          <span>·</span>
          <span data-testid="owner-user-sub-status">
            {(() => {
              const s = u.subscription_status;
              if (s === "trialing" || s === "trial") return "trial";
              if (s === "manual" || s === "promo") return "not billed";
              return s;
            })()}
          </span>
          {u.manual_plan_until ? (
            <>
              <span>·</span>
              <span title={u.manual_plan_until}>until {fmtDate(u.manual_plan_until)}</span>
            </>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="py-2.5">
        <div className="text-sm font-mono tabular">
          {u.watchlist_count}
          {u.watchlist_limit != null ? (
            <span className="text-muted-foreground">/{u.watchlist_limit}</span>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="py-2.5">
        <div className="text-sm font-mono tabular">
          {u.analyses_used}
          {u.analyses_limit != null ? (
            <span className="text-muted-foreground">/{u.analyses_limit}</span>
          ) : (
            <span className="text-muted-foreground"> · unlimited</span>
          )}
        </div>
        <div className="text-[10px] text-muted-foreground">{u.analyses_phase}</div>
      </TableCell>
      <TableCell className="py-2.5">
        <div className="text-[11.5px] tabular">{fmtDate(u.last_active_at)}</div>
      </TableCell>
      <TableCell className="py-2.5 text-right">
        <div className="relative inline-flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onOpenDiagnostics?.()}
            disabled={busy}
            title={diagTooltip}
            aria-label={diagTooltip}
            data-testid="owner-user-open-portfolio-diag"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-[#3DEBFF] hover:bg-[rgba(61,235,255,0.10)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3DEBFF] focus-visible:ring-offset-1 focus-visible:ring-offset-background"
          >
            <Briefcase className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPlanOpen((v) => !v)}
            disabled={busy}
            data-testid="owner-user-plan-trigger"
            className="h-8 px-2.5 text-xs"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Change plan"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onResetUsage}
            disabled={busy}
            title="Reset usage"
            data-testid="owner-user-reset"
            className="h-8 w-8 p-0"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            disabled={busy || isSelf}
            title={isSelf ? "Cannot delete your own admin account" : "Delete user"}
            data-testid="owner-user-delete"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-[#E5484D] hover:bg-[rgba(229,72,77,0.10)] disabled:opacity-30"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
          {planOpen ? (
            <div
              className="absolute z-30 right-0 top-9 min-w-[180px] rounded-lg border border-border/70 bg-popover/95 backdrop-blur-xl shadow-[var(--app-shadow-elev-2)] p-1"
              onMouseLeave={() => setPlanOpen(false)}
              data-testid="owner-user-plan-menu"
            >
              {PLAN_META.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => {
                    setPlanOpen(false);
                    onChangePlan(p.key, p.key === "free" ? "monthly" : (u.billing_interval || "monthly"));
                  }}
                  className={`w-full text-left rounded-md px-2.5 py-2 text-xs hover:bg-secondary/60 ${
                    p.key === u.plan ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={p.key === u.plan}
                  data-testid={`owner-user-plan-option-${p.key}`}
                >
                  <span className="inline-flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.color }} />
                    <span className="font-medium" style={{ color: p.color }}>{p.label}</span>
                    {p.key === u.plan ? <span className="ml-auto text-[9px] uppercase text-muted-foreground">Current</span> : null}
                  </span>
                  <div className="mt-0.5 text-[10.5px] text-muted-foreground">{p.desc}</div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </TableCell>
    </TableRow>
  );
}

/* ----------------------------- card ----------------------------- */

function Card({ title, icon: Icon, accent, children }) {
  return (
    <div
      className="relative rounded-2xl border bg-card/60 backdrop-blur p-5 sm:p-6 shadow-[var(--app-shadow-elev-2)]"
      style={{ borderColor: accent ? `${accent}55` : "var(--border)" }}
    >
      <div className="flex items-center gap-2">
        {Icon ? (
          <span className="inline-flex w-8 h-8 items-center justify-center rounded-lg bg-secondary/60 border border-border/70">
            <Icon className="w-4 h-4" style={{ color: accent || "#3182CE" }} />
          </span>
        ) : null}
        <div className="font-display text-lg tracking-tight">{title}</div>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}


/* ----------------------------- Production Health Check ----------------------------- */
/**
 * Production Health Check v1 — orchestration card.
 *
 * Sits at the top of Owner Console → System. Single button "Run Production
 * Health Check" calls POST /api/admin/health/production-check, then
 * computes the Frontend/Backend Wiring card CLIENT-SIDE (because it
 * depends on window.location.origin + window.__STOCKAILY_BACKEND_URL_MISSING__).
 *
 * Reuses every existing diagnostic surface — does NOT rebuild them.
 *
 * Strictly admin-only (backend enforces /admin gating). Never exposes
 * secrets. 60s server-side cooldown per admin; mirrored client-side.
 */

const HEALTH_STATUS_TONE = {
  healthy: { color: "#16a34a", bg: "rgba(22,163,74,0.10)", border: "rgba(22,163,74,0.45)", label: "Healthy", Icon: CheckCircle2 },
  warning: { color: "#d97706", bg: "rgba(217,119,6,0.10)",  border: "rgba(217,119,6,0.45)",  label: "Warning", Icon: AlertTriangle },
  failed:  { color: "#dc2626", bg: "rgba(220,38,38,0.10)",  border: "rgba(220,38,38,0.45)",  label: "Failed",  Icon: XCircle },
};

const OVERALL_TONE = {
  green:  { color: "#16a34a", label: "Production Healthy",        Icon: CheckCircle2 },
  yellow: { color: "#d97706", label: "Degraded — Needs Attention", Icon: AlertTriangle },
  red:    { color: "#dc2626", label: "Launch-Blocking Issue",      Icon: XCircle },
};

function HealthStatusPill({ status }) {
  const tone = HEALTH_STATUS_TONE[status] || HEALTH_STATUS_TONE.warning;
  const Icon = tone.Icon;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={{ background: tone.bg, color: tone.color, border: `1px solid ${tone.border}` }}
      data-testid={`health-card-status-${status}`}
    >
      <Icon className="w-3 h-3" />
      {tone.label}
    </span>
  );
}

/**
 * Computes the Frontend/Backend Wiring card entirely client-side from
 * window globals + the result of THIS health-check call.
 *
 * Phase 10.3 — same-origin routing (e.g. https://stockaily.ai/api/*) is
 * an INTENTIONAL valid deployment pattern. The card is GREEN whenever
 * the live API probe demonstrably works, regardless of whether
 * REACT_APP_BACKEND_URL points to a separate host or is empty.
 *
 * RED only when the current request signal proves the wiring is broken
 * (live API probe failed / backend identity + analyze AAPL both fail).
 */
function useFrontendBackendWiringCheck({ backendIdentityCard, analyzeCard, liveProbeOk }) {
  return useMemo(() => {
    const configuredBackend =
      (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";
    const cleanedBackend = configuredBackend.replace(/\/$/, "");
    const apiBase = cleanedBackend ? `${cleanedBackend}/api` : "/api";
    const frontendOrigin =
      (typeof window !== "undefined" && window.location?.origin) || "(unknown)";
    const backendUrlMissing =
      typeof window !== "undefined" && !!window.__STOCKAILY_BACKEND_URL_MISSING__;
    const callsOwnOrigin = !cleanedBackend;

    let backendProbeHost = null;
    try {
      backendProbeHost = backendIdentityCard?.hostname || backendIdentityCard?.request_host || null;
    } catch {
      backendProbeHost = null;
    }
    let configuredBackendHost = null;
    if (cleanedBackend) {
      try {
        configuredBackendHost = new URL(cleanedBackend).host;
      } catch {
        configuredBackendHost = null;
      }
    }
    const frontendHost =
      (typeof window !== "undefined" && window.location?.host) || null;

    // The orchestrator only reaches us if the API path actually worked.
    // If liveProbeOk is true AND backend identity / analyze report
    // reachable, the wiring is by definition healthy — even with empty
    // REACT_APP_BACKEND_URL (same-origin reverse-proxy deployment).
    const backendReachable = !!backendIdentityCard?.reachable;
    const analyzeReachable = !!analyzeCard?.analyze_reachable;
    const apiClearlyWorks = !!liveProbeOk && (backendReachable || analyzeReachable);

    let status = "healthy";
    let message = "Frontend → backend wiring is healthy.";
    let recommendedAction = null;

    if (apiClearlyWorks) {
      // Live API call demonstrably works. Wiring is good — describe the
      // routing mode for transparency. Same-origin is VALID.
      if (callsOwnOrigin) {
        message =
          "Frontend uses same-origin routing (no separate backend host configured). " +
          "Live API probe succeeded — wiring is healthy.";
      } else if (
        configuredBackendHost &&
        frontendHost &&
        configuredBackendHost === frontendHost
      ) {
        message =
          "Frontend and backend share the same host (path-based ingress). " +
          "Live API probe succeeded — wiring is healthy.";
      } else if (
        backendProbeHost &&
        configuredBackendHost &&
        !backendProbeHost.includes(configuredBackendHost.split(":")[0])
      ) {
        status = "warning";
        message =
          "Backend identity host differs from configured REACT_APP_BACKEND_URL host, " +
          "but the live API probe succeeded (likely a reverse proxy rewriting Host headers).";
        recommendedAction =
          "Informational: verify REACT_APP_BACKEND_URL matches the intended backend.";
      } else {
        message =
          "Frontend points to a dedicated backend host. Live API probe succeeded — wiring is healthy.";
      }
    } else {
      status = "failed";
      if (backendUrlMissing || (!cleanedBackend && !apiClearlyWorks)) {
        message =
          "Live API probe failed AND no separate backend URL is configured. " +
          "Backend may be unreachable, or (for multi-host deployments) REACT_APP_BACKEND_URL may be missing.";
        recommendedAction =
          "Check backend supervisor status. If a separate backend host is required, " +
          "contact Emergent Support to set REACT_APP_BACKEND_URL and rebuild the frontend.";
      } else {
        message =
          "Live API probe failed. Backend identity / analyze probes are unreachable.";
        recommendedAction =
          "Inspect backend logs and ingress routing; rerun the health check after recovery.";
      }
    }
    return {
      status,
      frontend_origin: frontendOrigin,
      frontend_host: frontendHost,
      configured_backend_url: cleanedBackend || null,
      frontend_api_base: apiBase,
      backend_url_missing: backendUrlMissing,
      configured_backend_host: configuredBackendHost,
      backend_probe_host: backendProbeHost,
      frontend_calls_own_origin: callsOwnOrigin,
      frontend_calls_expected_backend: !callsOwnOrigin && !backendUrlMissing,
      message,
      recommended_action: recommendedAction,
    };
  }, [backendIdentityCard, analyzeCard?.analyze_reachable, liveProbeOk]);
}

function HealthCheckCardRow({ title, card, fields }) {
  if (!card) return null;
  const status = card.status || "warning";
  return (
    <div
      className="rounded-xl border bg-card/50 p-3 sm:p-4"
      style={{
        borderColor:
          HEALTH_STATUS_TONE[status]?.border || "var(--border)",
      }}
      data-testid={`health-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-medium text-sm">{title}</div>
        <HealthStatusPill status={status} />
      </div>
      {card.message ? (
        <div className="text-[12px] text-muted-foreground mb-2">{card.message}</div>
      ) : null}
      {fields && fields.length ? (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11.5px]">
          {fields.map(([k, v]) => {
            if (v === undefined) return null;
            let display;
            if (v === null) display = <span className="text-muted-foreground">—</span>;
            else if (typeof v === "boolean") display = v ? "yes" : "no";
            else if (typeof v === "object") display = JSON.stringify(v);
            else display = String(v);
            return (
              <div key={k} className="flex justify-between gap-2 truncate">
                <dt className="text-muted-foreground truncate">{k}</dt>
                <dd className="font-mono truncate text-foreground/90">{display}</dd>
              </div>
            );
          })}
        </dl>
      ) : null}
      {card.recommended_action ? (
        <div className="mt-2 text-[11.5px] rounded-md px-2 py-1.5"
             style={{
               background: HEALTH_STATUS_TONE[status]?.bg,
               color: HEALTH_STATUS_TONE[status]?.color,
             }}>
          <strong>Action:</strong> {card.recommended_action}
        </div>
      ) : null}
    </div>
  );
}

function ProductionHealthCheckCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cooldownLeft, setCooldownLeft] = useState(0);

  useEffect(() => {
    if (cooldownLeft <= 0) return undefined;
    const t = setTimeout(() => setCooldownLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldownLeft]);

  const runCheck = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post("/admin/health/production-check", null, { headers: adminHeaders() });
      setData(res.data);
      setCooldownLeft(res.data?.cooldown_seconds || 60);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429 && detail && typeof detail === "object") {
        setError(detail.message || "Rate-limited. Try again shortly.");
        setCooldownLeft(detail.retry_after_seconds || 60);
      } else {
        const msg = (detail && typeof detail === "string") ? detail :
                    (detail?.message) ||
                    e?.message ||
                    "Health check failed";
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const wiringCard = useFrontendBackendWiringCheck({
    backendIdentityCard: data?.cards?.backend_identity,
    analyzeCard: data?.cards?.analyze_aapl,
    // The orchestrator only succeeded if `data` is populated — that
    // alone proves the API path works. If `error` is set instead, the
    // probe failed and wiring should be evaluated against that.
    liveProbeOk: !!data && !error,
  });

  const overall = useMemo(() => {
    if (!data) return null;
    const cards = { ...data.cards, frontend_wiring: wiringCard };
    const PRIORITY = [
      "frontend_wiring",
      "backend_identity",
      "fmp",
      "analyze_aapl",
      "llm",
      "cache_health",
      "copecart",
      "recent_failures",
    ];
    let status = "green";
    let firstRedRec = null;
    let firstYellowRec = null;
    for (const key of PRIORITY) {
      const c = cards[key];
      if (!c) continue;
      if (c.status === "failed") {
        status = "red";
        if (!firstRedRec) firstRedRec = c.recommended_action || c.message;
      } else if (c.status === "warning" && status !== "red") {
        status = "yellow";
        if (!firstYellowRec) firstYellowRec = c.recommended_action || c.message;
      }
    }
    const rec =
      firstRedRec ||
      firstYellowRec ||
      "Production analyze pipeline is healthy. It is safe to resume Portfolio pipeline testing.";
    return { overall_status: status, recommended_next_action: rec };
  }, [data, wiringCard]);

  const overallTone = overall ? OVERALL_TONE[overall.overall_status] : null;
  const OverallIcon = overallTone?.Icon || Stethoscope;
  const backendCard = data?.cards?.backend_identity || null;
  const analyzeCard = data?.cards?.analyze_aapl || null;
  const fmpCard = data?.cards?.fmp || null;
  const llmCard = data?.cards?.llm || null;
  const copecartCard = data?.cards?.copecart || null;
  const cacheCard = data?.cards?.cache_health || null;
  const failuresCard = data?.cards?.recent_failures || null;

  return (
    <Card title="Production Health Check" icon={Stethoscope} accent="#16a34a">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="text-xs text-muted-foreground max-w-xl">
          Single launch-readiness verdict that reuses every existing System tab diagnostic. Admin-only, never exposes secrets, never consumes user quota.
        </div>
        <Button
          type="button"
          size="sm"
          variant="default"
          onClick={runCheck}
          disabled={loading || cooldownLeft > 0}
          data-testid="run-production-health-check"
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
          ) : (
            <Stethoscope className="w-3.5 h-3.5 mr-1" />
          )}
          {cooldownLeft > 0 ? `Wait ${cooldownLeft}s` : "Run Production Health Check"}
        </Button>
      </div>

      {error ? (
        <div className="mb-3 text-[12px] rounded-md border border-red-500/40 bg-red-500/10 text-red-700 px-3 py-2" data-testid="health-check-error">
          {error}
        </div>
      ) : null}

      {overall ? (
        <div
          className="rounded-xl border p-4 mb-4"
          style={{
            borderColor: overallTone.color + "55",
            background: overallTone.color + "10",
          }}
          data-testid="health-check-overall"
        >
          <div className="flex items-center gap-2">
            <OverallIcon className="w-5 h-5" style={{ color: overallTone.color }} />
            <div className="font-semibold text-base" style={{ color: overallTone.color }}>
              {overallTone.label}
            </div>
          </div>
          <div className="mt-2 text-[12px] text-foreground/90" data-testid="health-check-recommended-action">
            <strong>Recommended next action:</strong> {overall.recommended_next_action}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span>Checked at: <span className="font-mono">{data.checked_at}</span></span>
            <span>Duration: <span className="font-mono">{data.total_duration_ms} ms</span></span>
            {data.environment ? <span>Environment: <span className="font-mono">{data.environment}</span></span> : null}
            {data.checked_by ? <span>Checked by: <span className="font-mono">{data.checked_by}</span></span> : null}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-border/50 bg-muted/20 p-4 mb-4 text-[12px] text-muted-foreground" data-testid="health-check-empty">
          Click <strong>Run Production Health Check</strong> to evaluate launch readiness.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="health-check-cards-grid">
        <HealthCheckCardRow
          title="Frontend / Backend Wiring"
          card={wiringCard}
          fields={[
            ["frontend_origin", wiringCard.frontend_origin],
            ["configured_backend_url", wiringCard.configured_backend_url],
            ["frontend_api_base", wiringCard.frontend_api_base],
            ["backend_url_missing", wiringCard.backend_url_missing],
            ["frontend_calls_own_origin", wiringCard.frontend_calls_own_origin],
            ["frontend_calls_expected_backend", wiringCard.frontend_calls_expected_backend],
            ["backend_probe_host", wiringCard.backend_probe_host],
            ["configured_backend_host", wiringCard.configured_backend_host],
          ]}
        />
        <HealthCheckCardRow
          title="Backend Identity"
          card={backendCard}
          fields={backendCard ? [
            ["environment", backendCard.environment],
            ["backend_env", backendCard.backend_env],
            ["build_version", backendCard.build_version],
            ["build_timestamp", backendCard.build_timestamp],
            ["hostname", backendCard.hostname],
            ["response_time_ms", backendCard.response_time_ms],
            ["public_backend_url", backendCard.public_backend_url],
            ["cors_origins_count", backendCard.cors_origins_count],
          ] : []}
        />
        <HealthCheckCardRow
          title="Analyze AAPL"
          card={analyzeCard}
          fields={analyzeCard ? [
            ["analyze_reachable", analyzeCard.analyze_reachable],
            ["current_price_available", analyzeCard.current_price_available],
            ["fair_value_available", analyzeCard.fair_value_available],
            ["ai_score_available", analyzeCard.ai_score_available],
            ["risk_level_available", analyzeCard.risk_level_available],
            ["valuation_status_available", analyzeCard.valuation_status_available],
            ["data_completeness", analyzeCard.data_completeness],
            ["result_validity", analyzeCard.result_validity],
            ["served_from_cache", analyzeCard.served_from_cache],
            ["last_known_good", analyzeCard.last_known_good],
            ["refresh_failed", analyzeCard.refresh_failed],
            ["elapsed_ms", analyzeCard.elapsed_ms],
            ["error_type", analyzeCard.error_type],
          ] : []}
        />
        <HealthCheckCardRow
          title="FMP Live Data"
          card={fmpCard}
          fields={fmpCard ? [
            ["fmp_configured", fmpCard.fmp_configured],
            ["api_key_present", fmpCard.api_key_present],
            ["provider_reachable", fmpCard.provider_reachable],
            ["quota_or_rate_limit_error", fmpCard.quota_or_rate_limit_error],
            ["auth_error", fmpCard.auth_error],
            ["plan_restricted", fmpCard.plan_restricted],
            ["diagnosis", fmpCard.diagnosis],
            ["raw_fmp_cache_hit", fmpCard.raw_fmp_cache?.cache_hit],
            ["raw_fmp_fresh", fmpCard.raw_fmp_cache?.fresh],
            ["analysis_cache_hit", fmpCard.analysis_cache?.cache_hit],
          ] : []}
        />
        <HealthCheckCardRow
          title="LLM Provider"
          card={llmCard}
          fields={llmCard ? [
            ["active_llm_provider", llmCard.active_llm_provider],
            ["active_configured", llmCard.active_configured],
            ["emergent_configured", llmCard.emergent?.configured],
            ["openai_configured", llmCard.openai?.configured],
            ["last_success_at", llmCard.last_success_at],
            ["last_error_at", llmCard.last_error_at],
          ] : []}
        />
        <HealthCheckCardRow
          title="CopeCart Billing"
          card={copecartCard}
          fields={copecartCard ? [
            ["copecart_enabled", copecartCard.copecart_enabled],
            ["copecart_checkout_enabled", copecartCard.copecart_checkout_enabled],
            ["webhook_secret_configured", copecartCard.webhook_secret_configured],
            ["product_mapping_present", copecartCard.product_mapping_present],
            ["product_mapping_count", copecartCard.product_mapping_count],
            ["recent_processed_events", copecartCard.recent_processed_events],
            ["recent_rejected_signatures", copecartCard.recent_rejected_signatures],
          ] : []}
        />
        <HealthCheckCardRow
          title="Cache Health"
          card={cacheCard}
          fields={cacheCard ? [
            ["analysis_cache_count", cacheCard.analysis_cache_count],
            ["raw_fmp_cache_count", cacheCard.raw_fmp_cache_count],
            ["chart_cache_count", cacheCard.chart_cache_count],
            ["search_cache_count", cacheCard.search_cache_count],
            ["insufficient_data_count", cacheCard.insufficient_data_count],
            ["partial_raw_fmp_count", cacheCard.partial_raw_fmp_count],
            ["aapl_last_known_good_available", cacheCard.aapl_last_known_good_available],
            ["aapl_analysis_cache_status", cacheCard.aapl_analysis_cache_status],
          ] : []}
        />
        <HealthCheckCardRow
          title="Recent Analyze Failures"
          card={failuresCard}
          fields={failuresCard ? [
            ["total_failures", failuresCard.total_failures],
            ["most_common_error_stage", failuresCard.most_common_error_stage],
            ["pipeline_timeout_count", failuresCard.pipeline_timeout_count],
            ["latest_aapl_failure_at", failuresCard.latest_aapl_failure_at],
            ["top_failing_stages", (failuresCard.top_failing_stages || []).map((p) => p.join(":")).join(", ")],
            ["top_failing_tickers", (failuresCard.top_failing_tickers || []).map((p) => p.join(":")).join(", ")],
          ] : []}
        />
      </div>
    </Card>
  );
}


/* ----------------------------- System / Live Data Status ----------------------------- */


/**
 * SystemPanel — Admin/Dev-only Live Data Status module.
 *
 * Surfaces FMP health + cache stats so the owner can see at a glance whether
 * the live data pipeline is serving FMP live or from cache. When
 * `mock_fallback_blocked` is true (production posture) this panel will never
 * label a response as "mock" — a missing/unavailable FMP source manifests
 * as the explicit "Unavailable" badge instead.
 *
 * Strictly admin-gated by the backend (/api/admin/debug/live-data-status).
 * Never exposes the API key. Auto-refreshes every 20s while the tab is open.
 */

// ---------------------------------------------------------------------------
// Email Foundation v1 (Slice 3) — Owner Console diagnostics card.
// Reads the safe subset from /api/admin/email-diagnostics.  Never
// surfaces the API key, webhook secret, tokens or raw recipient
// email addresses.
// ---------------------------------------------------------------------------
function EmailDiagnosticsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get("/admin/email-diagnostics", {
        headers: adminHeaders(),
      });
      setData(r?.data || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "load_failed");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const email = data?.email || {};
  const recent = data?.recent || {};
  const consent = data?.consent || {};
  const webhooks = data?.webhooks || {};
  const sequences = data?.sequences || {};
  return (
    <div
      className="rounded-lg border border-border/50 bg-card/40 p-3"
      data-testid="owner-email-diagnostics-card"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold">Email Foundation</div>
        <Button
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          data-testid="owner-email-diagnostics-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>
      {err ? (
        <div className="text-xs text-red-500" data-testid="owner-email-diagnostics-error">
          Could not load email diagnostics.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <EmailStat label="Provider mode" value={email.mode || "—"} testid="oed-mode" />
          <EmailStat
            label="API key configured"
            value={email.resend_key_configured ? "yes" : "no"}
            testid="oed-key"
          />
          <EmailStat
            label="Webhook secret"
            value={email.webhook_secret_configured ? "yes" : "no"}
            testid="oed-webhook-secret"
          />
          <EmailStat
            label="Transactional domain"
            value={email.transactional_domain || "—"}
            testid="oed-tx-domain"
          />
          <EmailStat
            label="Marketing domain"
            value={email.marketing_domain || "—"}
            testid="oed-mkt-domain"
          />
          <EmailStat
            label="Public URL configured"
            value={email.public_app_url_configured ? "yes" : "no"}
            testid="oed-public-url"
          />
          <EmailStat
            label="Subscribed contacts"
            value={String(consent.subscribed_count ?? 0)}
            testid="oed-subscribed-count"
          />
          <EmailStat
            label="Suppressed contacts"
            value={String(consent.suppressed_count ?? 0)}
            testid="oed-suppressed-count"
          />
          <EmailStat
            label="Webhooks last received"
            value={webhooks.last_received_at ? fmtDate(webhooks.last_received_at) : "—"}
            testid="oed-webhook-last"
          />
          <EmailStat
            label="Webhook failures (24h)"
            value={String(webhooks.failures_24h ?? 0)}
            testid="oed-webhook-failures"
          />
          <EmailStat
            label="Active sequences"
            value={String(sequences.active_enrollments ?? 0)}
            testid="oed-sequence-enrollments"
          />
          <EmailStat
            label="Last sent"
            value={recent.last_sent_at ? fmtDate(recent.last_sent_at) : "—"}
            testid="oed-last-sent"
          />
          <EmailStat
            label="Last failed"
            value={recent.last_failed_at ? fmtDate(recent.last_failed_at) : "—"}
            testid="oed-last-failed"
          />
          <EmailStat
            label="Sent (24h)"
            value={String((recent.totals && recent.totals.sent) || 0)}
            testid="oed-sent-24h"
          />
          <EmailStat
            label="Failed (24h)"
            value={String(
              ((recent.totals && recent.totals.failed_transient) || 0) +
              ((recent.totals && recent.totals.failed_permanent) || 0)
            )}
            testid="oed-failed-24h"
          />
          <EmailStat
            label="Blocked (24h)"
            value={String(
              ((recent.totals && recent.totals.blocked_disabled) || 0) +
              ((recent.totals && recent.totals.blocked_policy) || 0)
            )}
            testid="oed-blocked-24h"
          />
        </div>
      )}
      {Array.isArray(recent.recent_errors) && recent.recent_errors.length > 0 ? (
        <details className="mt-2" data-testid="oed-recent-errors">
          <summary className="text-xs text-muted-foreground cursor-pointer">
            Recent errors ({recent.recent_errors.length})
          </summary>
          <ul className="mt-1.5 space-y-1 text-[11px] text-muted-foreground">
            {recent.recent_errors.slice(0, 10).map((r, i) => (
              <li key={i} className="font-mono">
                {r.status}: {r.error_code}{r.failed_at ? ` @ ${fmtDate(r.failed_at)}` : ""}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function EmailStat({ label, value, testid }) {
  return (
    <div className="rounded border border-border/50 p-2" data-testid={testid}>
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className="text-xs font-mono mt-0.5 break-all">{value}</div>
    </div>
  );
}

function SystemPanel() {
  const [data, setData] = useState(null);
  const [runtimeData, setRuntimeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  // Phase 6.AA: a single fetchAll function that refreshes EVERY admin
  // diagnostic at once. The "Refresh all" master button calls this so
  // the operator never has to wonder "did the refresh actually do
  // anything?" — every card on the System tab updates in lock-step.
  const fetchAll = useCallback(async () => {
    setError(null);
    setLoading(true);
    const headers = adminHeaders();
    try {
      // Fire both in parallel; whichever returns first updates first.
      const [statusRes, runtimeRes] = await Promise.allSettled([
        api.get("/admin/debug/live-data-status", { headers }),
        api.get("/admin/debug/runtime-status", { headers }),
      ]);
      if (statusRes.status === "fulfilled") {
        setData(statusRes.value.data);
      } else {
        // eslint-disable-next-line no-console
        console.warn("live-data-status failed", statusRes.reason);
      }
      if (runtimeRes.status === "fulfilled") {
        setRuntimeData(runtimeRes.value.data);
      } else {
        // eslint-disable-next-line no-console
        console.warn("runtime-status failed", runtimeRes.reason);
      }
      // Surface a single error string only when BOTH fail — partial
      // failures still let the operator see whatever data did load.
      if (statusRes.status === "rejected" && runtimeRes.status === "rejected") {
        const reason =
          statusRes.reason?.response?.data?.detail ||
          statusRes.reason?.message ||
          "Both diagnostic endpoints failed";
        setError(typeof reason === "string" ? reason : "Failed to load diagnostics");
      }
      setLastFetchedAt(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  // Keep the legacy single-endpoint fetcher for cards that previously
  // referenced it — now it just delegates to fetchAll.
  const fetchStatus = fetchAll;

  useEffect(() => {
    // Manual-refresh only — do NOT auto-poll. This endpoint reads in-memory
    // telemetry + Mongo doc counts; it never calls FMP, but we still keep
    // it user-initiated to make the data flow explicit (and so admins can
    // observe a stable snapshot when investigating an incident).
    fetchAll();
  }, [fetchAll]);

  const fmp = data?.fmp;
  const cache = data?.cache;
  const searchCache = data?.search_cache;
  const llm = data?.llm;
  const versions = data?.versions;

  return (
    <div className="space-y-4" data-testid="owner-system-panel">
      {/* Master refresh — refreshes every card on the System tab at once */}
      <div className="flex items-center justify-between rounded-lg border border-border/50 bg-card/40 px-3 py-2 sticky top-2 z-10 backdrop-blur" data-testid="owner-system-master-bar">
        <div className="text-xs text-muted-foreground">
          {lastFetchedAt
            ? <>Last refreshed <span className="font-mono">{lastFetchedAt.toLocaleTimeString()}</span></>
            : "Not yet refreshed"
          }
        </div>
        <Button
          type="button"
          size="sm"
          variant="default"
          onClick={fetchAll}
          disabled={loading}
          data-testid="owner-system-refresh-all"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh all
        </Button>
      </div>

      <EnvironmentBanner runtime={runtimeData} />

      <ProductionHealthCheckCard />

      <EmailDiagnosticsCard />

      <IdentityBackfillCard adminHeadersFn={adminHeaders} />

      <LastRenderedAnalysisCard runtime={runtimeData} loading={loading && !runtimeData} onRefresh={fetchAll} />

      <LlmSmokeTestCard />

      <LlmProviderAbstractionCard />

      <CopecartBillingCard />

      <AdminQAPlanSimulationCard />

      <RuntimeStatusCard runtime={runtimeData} loading={loading && !runtimeData} onRefresh={fetchAll} />

      <Card title="Live Data Status" icon={Activity} accent="#3182CE">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs text-muted-foreground">
            Admin-only diagnostics for the live market data pipeline. Read from in-memory telemetry + Mongo — never triggers an FMP request. Refresh manually when needed.
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={fetchStatus}
            disabled={loading}
            data-testid="owner-system-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {error ? (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2" data-testid="owner-system-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        {!data && loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="owner-system-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading live status…
          </div>
        ) : null}

        {fmp ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ---- FMP block ---- */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FmpHealthBadge health={fmp.health} />
                <span className="text-xs text-muted-foreground">FMP API</span>
              </div>
              <StatRow label="Status" value={<HealthLabel health={fmp.health} />} testId="fmp-status" />
              <StatRow label="ENABLE_FMP env" value={<code className="text-[11px]">{fmp.enable_fmp_env || "—"}</code>} testId="fmp-enable-env" />
              <StatRow label="Base URL" value={<code className="text-xs">{fmp.base_url || "—"}</code>} testId="fmp-base-url" />
              <StatRow
                label="API key"
                value={
                  fmp.key_present
                    ? <span className="text-xs">Configured ({fmp.key_length} chars) · <span className="text-muted-foreground">{fmp.key_source}</span></span>
                    : <span className="text-xs text-amber-600">Not configured</span>
                }
                testId="fmp-key-status"
              />
              <StatRow label="Current data source" value={<DataSourceBadge source={fmp.current_data_source} mockBlocked={fmp.mock_fallback_blocked} />} testId="fmp-current-source" />
              <StatRow label="Last successful request" value={<FmpEventLine ts={fmp.last_success_at} path={fmp.last_success_path} />} testId="fmp-last-success" />
              <StatRow label="Last failed request" value={<FmpEventLine ts={fmp.last_failure_at} path={fmp.last_failure_path} reason={fmp.last_failure_reason} status={fmp.last_failure_status} />} testId="fmp-last-failure" />
              <div className="grid grid-cols-3 gap-2 pt-2">
                <Stat compact label="Calls" value={fmp.total_calls ?? 0} testId="fmp-total-calls" />
                <Stat compact label="OK" value={fmp.total_successes ?? 0} accent="text-emerald-600" testId="fmp-total-ok" />
                <Stat compact label="Fail" value={fmp.total_failures ?? 0} accent="text-rose-600" testId="fmp-total-fail" />
              </div>
              {(fmp.rate_limit_hits > 0 || fmp.auth_errors > 0 || fmp.plan_restricted_hits > 0 || fmp.not_found_hits > 0 || fmp.network_errors > 0) && (
                <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-2">
                  {fmp.rate_limit_hits > 0 ? <span data-testid="fmp-rate-limit-hits">· {fmp.rate_limit_hits} rate-limit (429)</span> : null}
                  {fmp.plan_restricted_hits > 0 ? <span className="text-amber-700" data-testid="fmp-plan-restricted-hits">· {fmp.plan_restricted_hits} plan-restricted (402)</span> : null}
                  {fmp.auth_errors > 0 ? <span className="text-rose-700" data-testid="fmp-auth-errors">· {fmp.auth_errors} auth (401/403)</span> : null}
                  {fmp.not_found_hits > 0 ? <span data-testid="fmp-not-found-hits">· {fmp.not_found_hits} not found (404)</span> : null}
                  {fmp.network_errors > 0 ? <span className="text-rose-700" data-testid="fmp-network-errors">· {fmp.network_errors} network/parse</span> : null}
                </div>
              )}
              <div className="text-[11px] text-muted-foreground">
                Success rate: {fmp.success_rate != null ? `${fmp.success_rate}%` : "—"}
              </div>

              {/* Quota & circuit breaker block — admin-only operational state. */}
              <div className="pt-2 border-t border-border/40 mt-2 space-y-2">
                <StatRow
                  label="Calls today (UTC)"
                  value={<span className="text-xs font-semibold">{fmp.calls_today ?? 0}</span>}
                  testId="fmp-calls-today"
                />
                <StatRow
                  label="Est. quota reset"
                  value={<span className="text-xs">{fmp.est_quota_reset_at ? new Date(fmp.est_quota_reset_at).toLocaleString() : "—"}</span>}
                  testId="fmp-quota-reset"
                />
                <StatRow
                  label="Last 429"
                  value={<span className="text-xs">{fmp.last_429_at ? fmtDate(fmp.last_429_at) : "—"}</span>}
                  testId="fmp-last-429"
                />
                <StatRow
                  label="Circuit breaker"
                  value={<CircuitBadge open={!!fmp.circuit_open} until={fmp.circuit_open_until} reason={fmp.circuit_open_reason} />}
                  testId="fmp-circuit"
                />
              </div>
            </div>

            {/* ---- Cache block ---- */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Cache (24h TTL · charts 24h · search 7d)</span>
              </div>
              <StatRow label="Analysis cache size" value={<span className="text-xs">{cache?.analysis_cache_size ?? "—"} entries</span>} testId="cache-analysis-size" />
              <StatRow label="Raw FMP cache size" value={<span className="text-xs">{cache?.raw_fmp_cache_size ?? "—"} entries</span>} testId="cache-raw-size" />
              <StatRow label="Chart cache size" value={<span className="text-xs">{cache?.chart_cache_size ?? "—"} entries</span>} testId="cache-chart-size" />
              <StatRow label="Search cache size" value={<span className="text-xs">{searchCache?.size ?? "—"} entries</span>} testId="cache-search-size" />
              <StatRow label="Analysis hit rate" value={<HitRate rate={cache?.analysis_hit_rate} hits={cache?.analysis_hits} misses={cache?.analysis_misses} />} testId="cache-analysis-hitrate" />
              <StatRow label="Raw FMP hit rate" value={<HitRate rate={cache?.raw_fmp_hit_rate} hits={cache?.raw_fmp_hits} misses={cache?.raw_fmp_misses} />} testId="cache-raw-hitrate" />
              <StatRow label="Chart hit rate" value={<HitRate rate={cache?.chart_hit_rate} hits={cache?.chart_hits} misses={cache?.chart_misses} />} testId="cache-chart-hitrate" />
              <StatRow label="Search hit rate" value={<HitRate rate={searchCache?.hit_rate} hits={searchCache?.hits} misses={searchCache?.misses} />} testId="cache-search-hitrate" />
              <StatRow label="Calls per analysis" value={<span className="text-xs font-semibold">{fmp?.calls_per_analysis ?? "—"}</span>} testId="fmp-calls-per-analysis" />
              <div className="grid grid-cols-3 gap-2 pt-2">
                <Stat compact label="Hits" value={(cache?.analysis_hits ?? 0) + (cache?.raw_fmp_hits ?? 0) + (cache?.chart_hits ?? 0)} accent="text-emerald-600" testId="cache-total-hits" />
                <Stat compact label="Misses" value={(cache?.analysis_misses ?? 0) + (cache?.raw_fmp_misses ?? 0) + (cache?.chart_misses ?? 0)} testId="cache-total-misses" />
                <Stat compact label="Writes" value={(cache?.analysis_writes ?? 0) + (cache?.raw_fmp_writes ?? 0) + (cache?.chart_writes ?? 0)} testId="cache-total-writes" />
              </div>
              <div className="text-[11px] text-muted-foreground pt-1">
                Engine: <code>{versions?.engine_version || "—"}</code> · Prompt: <code>{versions?.prompt_version || "—"}</code> · API: <code>{versions?.analysis_version || "—"}</code>
              </div>
            </div>
          </div>
        ) : null}

        {lastFetchedAt ? (
          <div className="mt-4 pt-3 border-t border-border/50 text-[10px] text-muted-foreground">
            Last refreshed {lastFetchedAt.toLocaleTimeString()}
          </div>
        ) : null}
      </Card>

      {/* Top endpoints + top tickers — surface where FMP quota is being spent. */}
      {fmp && ((fmp.top_endpoints || []).length > 0 || (fmp.top_tickers || []).length > 0) ? (
        <Card title="FMP Usage Breakdown" icon={Activity} accent="#A47864">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Top endpoints (by FMP calls)</div>
              {(fmp.top_endpoints || []).length === 0 ? (
                <div className="text-xs text-muted-foreground">No data yet — first analyze call will populate this.</div>
              ) : (
                <ul className="space-y-1.5" data-testid="fmp-top-endpoints">
                  {(fmp.top_endpoints || []).map(([path, count]) => (
                    <li key={path} className="flex items-center justify-between text-xs">
                      <code className="text-[11px] truncate max-w-[70%]">{path}</code>
                      <span className="font-mono text-muted-foreground">{count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Top tickers (by analyses)</div>
              {(fmp.top_tickers || []).length === 0 ? (
                <div className="text-xs text-muted-foreground">No data yet.</div>
              ) : (
                <ul className="space-y-1.5" data-testid="fmp-top-tickers">
                  {(fmp.top_tickers || []).map(([sym, count]) => (
                    <li key={sym} className="flex items-center justify-between text-xs">
                      <code className="text-[11px]">{sym}</code>
                      <span className="font-mono text-muted-foreground">{count} calls</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="mt-4 text-[10px] text-muted-foreground">
            Calls are tracked per-endpoint and per-ticker. Lists reset on backend restart.
            Note: counts include retries (none for 429, the breaker stops those) and bundle calls (~7 endpoints per uncached analysis).
          </div>
          {/* Phase 6.AF — Per-endpoint health table. Shows the success
              rate AND last failure reason per FMP path so the operator
              can immediately see "/key-metrics is 100% failing with 402
              plan_restricted on this FMP plan tier". */}
          {Array.isArray(fmp.endpoint_table) && fmp.endpoint_table.length ? (
            <div className="mt-5 pt-4 border-t border-border/40">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
                Endpoint health (success vs failure)
              </div>
              <div className="overflow-x-auto rounded-lg border border-border/50">
                <table className="w-full text-[11px]" data-testid="fmp-endpoint-table">
                  <thead className="bg-muted/40">
                    <tr className="text-left text-muted-foreground">
                      <th className="px-2 py-1.5 font-medium">Path</th>
                      <th className="px-2 py-1.5 font-medium text-right">Total</th>
                      <th className="px-2 py-1.5 font-medium text-right text-emerald-700">OK</th>
                      <th className="px-2 py-1.5 font-medium text-right text-rose-700">Fail</th>
                      <th className="px-2 py-1.5 font-medium text-right">Success%</th>
                      <th className="px-2 py-1.5 font-medium">Last reason</th>
                      <th className="px-2 py-1.5 font-medium">Last status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fmp.endpoint_table.map((row) => {
                      const reasonColor = (
                        row.last_reason === "ok" ? "text-emerald-700" :
                        row.last_reason === "plan_restricted" ? "text-amber-700" :
                        row.last_reason === "rate_limited" ? "text-rose-700" :
                        row.last_reason === "auth_error" ? "text-rose-700" :
                        "text-muted-foreground"
                      );
                      const sr = row.success_rate_pct;
                      const srColor = (
                        sr == null ? "text-muted-foreground" :
                        sr >= 95 ? "text-emerald-700" :
                        sr >= 50 ? "text-amber-700" : "text-rose-700"
                      );
                      return (
                        <tr key={row.path} className="border-t border-border/40" data-testid={`fmp-endpoint-row-${row.path.replace(/\//g, '-')}`}>
                          <td className="px-2 py-1 font-mono">{row.path}</td>
                          <td className="px-2 py-1 text-right font-mono">{row.total}</td>
                          <td className="px-2 py-1 text-right font-mono text-emerald-700">{row.success}</td>
                          <td className="px-2 py-1 text-right font-mono text-rose-700">{row.failure}</td>
                          <td className={`px-2 py-1 text-right font-mono ${srColor}`}>{sr != null ? `${sr}%` : "—"}</td>
                          <td className={`px-2 py-1 ${reasonColor}`}><code className="text-[10px]">{row.last_reason || "—"}</code></td>
                          <td className="px-2 py-1 font-mono">{row.last_status ?? "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-[10px] text-muted-foreground">
                If <code>/key-metrics</code> shows 100% failure with <code>plan_restricted (402)</code>,
                the current FMP plan doesn't include this endpoint. The analysis pipeline already
                degrades gracefully — partial data + admin "Limited fundamental data" banner.
              </div>
            </div>
          ) : null}
        </Card>
      ) : null}

      {/* LLM cost dashboard — admin-only OpenAI/Emergent usage breakdown. */}
      {llm ? (
        <Card title="LLM Cost & Usage" icon={Activity} accent="#10B981">
          {/* Phase 6.BI-14 — Provider status banner.
              Reads `llm.provider_status` + `llm.last_quota_*` from the
              snapshot that admin `/system` returns. No live LLM call
              is triggered — pure read of the rehydrated in-memory
              telemetry. */}
          <LlmProviderStatusBanner llm={llm} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">OpenAI spend (USD)</span>
              </div>
              <StatRow
                label="Cost today"
                value={<span className="text-xs font-semibold">${(llm.cost_today_usd || 0).toFixed(4)}</span>}
                testId="llm-cost-today"
              />
              <StatRow
                label="Cost month-to-date"
                value={<span className="text-xs font-semibold">${(llm.cost_mtd_usd || 0).toFixed(4)}</span>}
                testId="llm-cost-mtd"
              />
              <StatRow
                label="Avg cost / call"
                value={<span className="text-xs">{llm.avg_cost_per_call_usd != null ? `$${llm.avg_cost_per_call_usd.toFixed(6)}` : "—"}</span>}
                testId="llm-avg-cost"
              />
              <StatRow
                label="Calls today"
                value={<span className="text-xs">{llm.calls_today_total ?? 0} ({llm.cache_hits_today ?? 0} cache hits)</span>}
                testId="llm-calls-today"
              />
              <StatRow
                label="Tokens today"
                value={<span className="text-xs">in: {(llm.input_tokens_today || 0).toLocaleString()} · out: {(llm.output_tokens_today || 0).toLocaleString()}</span>}
                testId="llm-tokens-today"
              />
              {llm.cached_input_tokens_today > 0 ? (
                <StatRow
                  label="Cached input tokens"
                  value={<span className="text-xs">{(llm.cached_input_tokens_today || 0).toLocaleString()}</span>}
                  testId="llm-cached-tokens"
                />
              ) : null}
              <StatRow
                label="Errors today"
                value={<span className={`text-xs ${(llm.errors_today || 0) > 0 ? "text-rose-600" : ""}`}>{llm.errors_today ?? 0}</span>}
                testId="llm-errors"
              />
              <StatRow
                label="Last call"
                value={<span className="text-xs">{llm.last_call_at ? fmtDate(llm.last_call_at) : "—"}</span>}
                testId="llm-last-call"
              />
              {/* Phase 6.BI-14 — provider-health metrics. */}
              <StatRow
                label="Last successful LLM call"
                value={
                  <span className="text-xs">
                    {llm.last_success_at ? fmtDate(llm.last_success_at) : "—"}
                    {llm.last_success_model ? <span className="ml-1 opacity-70">({llm.last_success_model})</span> : null}
                  </span>
                }
                testId="llm-last-success"
              />
              {llm.last_quota_max_budget_usd != null ? (
                <StatRow
                  label="Universal key budget"
                  value={
                    <span className="text-xs font-mono">
                      ${(llm.last_quota_current_cost_usd || 0).toFixed(3)} / ${(llm.last_quota_max_budget_usd || 0).toFixed(3)}
                    </span>
                  }
                  testId="llm-universal-key-budget"
                />
              ) : null}
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">By plan & by model (today)</span>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Calls by plan</div>
                {(llm.calls_by_plan || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">No LLM calls yet today.</div>
                ) : (
                  <ul className="space-y-1" data-testid="llm-calls-by-plan">
                    {(llm.calls_by_plan || []).map(([planName, count]) => {
                      const costEntry = (llm.cost_by_plan || []).find((p) => p[0] === planName);
                      return (
                        <li key={planName} className="flex items-center justify-between text-xs">
                          <span className="capitalize">{planName}</span>
                          <span className="font-mono text-muted-foreground">
                            {count} calls · ${(costEntry?.[1] || 0).toFixed(4)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5 mt-3">Calls by model</div>
                {(llm.calls_by_model || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">No LLM calls yet today.</div>
                ) : (
                  <ul className="space-y-1" data-testid="llm-calls-by-model">
                    {(llm.calls_by_model || []).map(([modelId, count]) => {
                      const costEntry = (llm.cost_by_model || []).find((p) => p[0] === modelId);
                      return (
                        <li key={modelId} className="flex items-center justify-between text-xs">
                          <code className="text-[11px]">{modelId}</code>
                          <span className="font-mono text-muted-foreground">
                            {count} · ${(costEntry?.[1] || 0).toFixed(4)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="pt-3 border-t border-border/40">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Model routing config</div>
                <ul className="space-y-0.5 text-[11px]" data-testid="llm-config">
                  {llm.config && llm.config.plans ? Object.entries(llm.config.plans).map(([planName, planCfg]) => (
                    <li key={planName} className="flex items-center justify-between">
                      <span className="capitalize">{planName}</span>
                      <span className="font-mono text-muted-foreground">
                        {planCfg.primary}
                        {planCfg.fallback ? <span className="opacity-60"> → {planCfg.fallback}</span> : null}
                      </span>
                    </li>
                  )) : null}
                </ul>
                <div className="text-[10px] text-muted-foreground mt-2">
                  Key source: <code>{llm.config?.key_source || "—"}</code> · {llm.config?.enabled ? "enabled" : "disabled"}
                </div>
              </div>
            </div>
          </div>
        </Card>
      ) : null}

      {fmp?.health === "rate_limited" ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2" data-testid="owner-system-rate-limit-tip">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            FMP is rate-limited. The app silently falls back to mock data so users still see results.
            Consider upgrading the FMP plan or waiting for quota reset. The API key is never exposed.
          </span>
        </div>
      ) : null}

      {fmp?.health === "auth_failure" ? (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-700 dark:text-rose-300 flex items-start gap-2" data-testid="owner-system-auth-tip">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            FMP is rejecting the API key (401/403). Verify <code>FMP_API_KEY</code> in deployment env.
          </span>
        </div>
      ) : null}

      <AnalyzeFailuresCard />
      <OperationalMetricsCard metrics={data?.analyze_metrics} loading={loading} />
    </div>
  );
}

/**
 * OperationalMetricsCard — admin-only operational analytics for the
 * analyze pipeline (avg/p95 duration, in-flight gauge, AI generation
 * duration, missing KPI hot-spots).
 *
 * Reads `data.analyze_metrics` from the parent /api/admin/debug/live-data-status
 * fetch. Manual refresh inherits from the parent card.
 */

/**
 * OwnerAuthDiagnostic — Phase 6.BC.
 *
 * Replaces the previous silent NotFound bounce when /owner-console access
 * is denied. Shows the EXACT reason, what to do next, plus the runtime
 * evidence (which backend, which env, what /admin/status returned) so
 * the operator can fix the root cause without guessing.
 *
 * This page is itself owner-only — it never reveals secrets or whether
 * a specific email is whitelisted. It only displays diagnostic info that
 * the backend has already chosen to share via /admin/status.
 */
function OwnerAuthDiagnostic({ reason, detail, user, serverStatus }) {
  const navigate = useNavigate();
  const [identity, setIdentity] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/_health/identity", { timeout: 6000 });
        if (!cancelled) setIdentity(res.data || null);
      } catch {
        if (!cancelled) setIdentity({ _error: true });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const reasonLabel = {
    admin_disabled: "Admin gate disabled on backend (no ADMIN_EMAILS / ADMIN_PIN)",
    owner_email_not_whitelisted: "Your email is not on the owner whitelist",
    session_invalid: "Session cookie not accepted by backend",
    user_not_loaded: "User not loaded (sign in required)",
    backend_auth_endpoint_failed: "Backend auth endpoint failed",
    cors_credentials_failed: "CORS credentials failure",
    no_session_cookie: "No session cookie present",
    admin_password_missing: "Admin password not configured on backend",
    admin_password_invalid: "Admin password rejected by backend",
    totp_required: "TOTP setup required",
    totp_failed: "TOTP code rejected",
  }[reason] || "Owner Console access denied";

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-1 flex items-start justify-center px-4 py-10">
        <div className="w-full max-w-3xl space-y-4">
          <div
            className="rounded-lg border border-rose-500/50 bg-rose-500/10 p-4 text-rose-700"
            role="alert"
            data-testid="owner-auth-diagnostic"
          >
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-5 h-5" />
              <h1 className="text-base font-semibold" data-testid="owner-auth-diagnostic-title">
                Owner Console access denied
              </h1>
            </div>
            <div className="text-sm font-medium" data-testid="owner-auth-diagnostic-reason">
              Reason: {reasonLabel} ({reason || "unknown"})
            </div>
            {detail?.message ? (
              <div className="mt-2 text-[13px]" data-testid="owner-auth-diagnostic-message">
                {detail.message}
              </div>
            ) : null}
            {detail?.action ? (
              <div className="mt-2 text-[13px] opacity-90">
                <span className="font-semibold">What to do:</span> {detail.action}
              </div>
            ) : null}
          </div>

          <div className="rounded-lg border border-border/60 bg-card p-4 space-y-2">
            <h2 className="text-sm font-semibold">Diagnostic evidence</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[11.5px] font-mono">
              <div>signed_in: {user ? "true" : "false"}</div>
              <div>current_email: {user?.email || "(none)"}</div>
              <div>admin_enabled: {String(serverStatus?.admin_enabled ?? "?")}</div>
              <div>is_admin_email: {String(serverStatus?.is_admin_email ?? "?")}</div>
              <div>frontend url: {typeof window !== "undefined" ? window.location.href : "?"}</div>
              <div>frontend api base: {(typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "?"}</div>
              <div>backend environment: {identity?.environment || "?"}</div>
              <div>backend host: {identity?.runtime?.hostname || identity?.host || "?"}</div>
              <div>request_origin: {identity?.request_origin || "?"}</div>
              <div>x_forwarded_host: {identity?.x_forwarded_host || "(not set)"}</div>
              <div>build: {identity?.runtime?.build_version || "?"}</div>
              <div>git_sha: {String(identity?.runtime?.build_git_sha || "?").slice(0, 7)}</div>
              {detail?.http_status ? <div>http_status: {detail.http_status}</div> : null}
              {detail?.request_id ? <div>request_id: {detail.request_id}</div> : null}
            </div>
            {detail?.response_body ? (
              <details className="mt-2 text-[11px]">
                <summary className="cursor-pointer text-muted-foreground">backend response body</summary>
                <pre className="mt-1 overflow-auto bg-muted/40 p-2 rounded text-[10px]">
{JSON.stringify(detail.response_body, null, 2)}
                </pre>
              </details>
            ) : null}
            {detail?.backend_response ? (
              <details className="mt-2 text-[11px]">
                <summary className="cursor-pointer text-muted-foreground">/admin/status response</summary>
                <pre className="mt-1 overflow-auto bg-muted/40 p-2 rounded text-[10px]">
{JSON.stringify(detail.backend_response, null, 2)}
                </pre>
              </details>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.location.reload()}
              data-testid="owner-auth-diagnostic-retry"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              data-testid="owner-auth-diagnostic-home"
            >
              Back to home
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}




/**
 * EnvironmentBanner — Phase 6.AZ + 6.BA.
 *
 * Sits at the top of the System tab and answers, AT A GLANCE, the
 * single most important debugging question: "Is this Owner Console
 * looking at preview or production state?".
 *
 * Phase 6.BA: When the user navigates to the production URL but the
 * frontend serves at the preview URL (a platform-level routing issue),
 * the banner now surfaces a giant actionable warning so this isn't
 * silently confused with a normal preview deploy. We fetch the full
 * request-side evidence from `/api/_health/identity` and compare
 * three independent signals:
 *
 *   1. window.location.hostname  — where the browser is RIGHT NOW
 *   2. request_browser_environment — env inferred by backend from
 *      Origin / X-Forwarded-Host headers
 *   3. runtime.environment       — env stamped by the backend pod
 *
 * Disagreements between (1)/(2) and (3) almost always mean the
 * production deployment hasn't been refreshed, so emergent.host is
 * routing back to the preview pod.
 */
function EnvironmentBanner({ runtime }) {
  const r = runtime || {};
  const build = r.build || {};
  const dh = build.deployment_hints || {};
  // Phase 6.BA — fetch full identity evidence directly from the backend
  // so the banner shows what the BROWSER's request actually looks like
  // (Origin / X-Forwarded-Host) — distinct from what the backend POD
  // identifies itself as.
  const [identity, setIdentity] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/_health/identity", { timeout: 6000 });
        if (!cancelled) setIdentity(res.data || null);
      } catch {
        if (!cancelled) setIdentity({ _error: true });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Prefer the explicit environment tag we now stamp on /admin/debug/runtime-status
  // (via build_identity.runtime_identity()); fall back to deployment_hints.
  const backendEnv = (
    identity?.environment
    || dh.deployment_env
    || dh.environment
    || dh.emergent_deployment
    || dh.app_env
    || dh.node_env
    || ""
  ).toLowerCase();
  // Best-effort env detection from frontend host as a sanity check.
  let frontendHost = "";
  let frontendOrigin = "";
  let frontendHref = "";
  let frontendIsProd = false;
  let frontendIsPreview = false;
  if (typeof window !== "undefined") {
    frontendHost = window.location?.hostname || "";
    frontendOrigin = window.location?.origin || "";
    frontendHref = window.location?.href || "";
    frontendIsProd = /\.emergent\.host$/i.test(frontendHost);
    frontendIsPreview = /preview\.emergentagent/i.test(frontendHost);
  }
  const backendUrl = (
    (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL)
    || (typeof window !== "undefined" && window.__STOCKAILY_BACKEND_URL__)
    || "(unknown)"
  );
  // What environment does the URL the frontend is CALLING resolve to?
  // (This is independent of where the user's browser is currently at.)
  const backendUrlEnv = /\.emergent\.host/i.test(backendUrl)
    ? "production"
    : /preview\.emergentagent/i.test(backendUrl)
      ? "preview"
      : "unknown";

  // Backend's own inference from the request headers.
  const browserRequestEnv = (identity?.request_browser_environment || "").toLowerCase();

  const isProd = (
    backendEnv === "production"
    || backendEnv === "prod"
    || frontendIsProd
    || String(build.build_version || "").toLowerCase().includes("prod")
  );
  const isPreview = (
    backendEnv === "preview"
    || backendEnv === "dev"
    || backendEnv === "development"
    || backendEnv === "staging"
    || frontendIsPreview
    || String(build.build_version || "").toLowerCase().includes("preview")
  );

  const label = isProd && !frontendIsPreview ? "PRODUCTION"
    : isPreview || frontendIsPreview ? "PREVIEW"
    : "UNKNOWN";
  const tone = (isProd && !frontendIsPreview)
    ? "border-rose-500/60 bg-rose-500/10 text-rose-700"
    : (isPreview || frontendIsPreview)
      ? "border-amber-500/60 bg-amber-500/10 text-amber-700"
      : "border-slate-500/60 bg-slate-500/10 text-slate-700";

  // Phase 6.BA — "Production-routes-to-preview" detection.
  // The smoking gun: window.location says preview while either the
  // backend or the REACT_APP_BACKEND_URL says production (or vice
  // versa). This is almost always a deployment routing problem rather
  // than a normal preview test, so the banner promotes it to a giant
  // actionable warning instead of just a thin chip.
  const prodRedirectedToPreview = (
    frontendIsPreview && (
      backendEnv === "production"
      || backendUrlEnv === "production"
      || browserRequestEnv === "production"
    )
  );
  const previewServedAsProd = (
    frontendIsProd && (
      backendEnv === "preview"
      || backendUrlEnv === "preview"
      || browserRequestEnv === "preview"
    )
  );
  const mismatch = prodRedirectedToPreview || previewServedAsProd
    || (frontendIsProd && backendEnv === "preview")
    || (frontendIsPreview && backendEnv === "production");

  return (
    <div
      className={`rounded-lg border px-3 py-2 ${tone}`}
      data-testid="owner-environment-banner"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Activity className="w-4 h-4" />
          <span data-testid="owner-environment-label">Environment: {label}</span>
          {mismatch ? (
            <span
              className="ml-1 inline-flex items-center gap-1 rounded-full border border-rose-500/60 bg-rose-500/15 text-rose-700 px-2 py-0.5 text-[10px]"
              data-testid="owner-environment-mismatch"
            >
              <AlertTriangle className="w-3 h-3" /> frontend / backend env mismatch
            </span>
          ) : null}
        </div>
        <div className="text-[11px] text-muted-foreground font-mono">
          build {build.build_version || "?"} · {String(build.build_git_sha || "?").slice(0, 7)} · {build.build_timestamp || "?"}
        </div>
      </div>
      <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] font-mono opacity-90">
        <div>frontend url (browser): <span data-testid="env-frontend-href">{frontendHref || "?"}</span></div>
        <div>frontend api base: <span data-testid="env-frontend-api-base">{backendUrl}</span></div>
        <div>backend hostname: <span data-testid="env-backend-host">{build.hostname || "?"}</span></div>
        <div>backend env: <span data-testid="env-backend-env">{backendEnv || "?"}</span></div>
        <div>request origin (browser→backend): {identity?.request_origin || "?"}</div>
        <div>request host header: {identity?.request_host || "?"}</div>
        <div>x-forwarded-host: {identity?.x_forwarded_host || "(not set)"}</div>
        <div>browser request env (backend-inferred): {browserRequestEnv || "?"}</div>
      </div>

      {/* Phase 6.BA — actionable warning when the user typed the production
          URL but the frontend ends up serving from the preview host. */}
      {prodRedirectedToPreview ? (
        <div
          className="mt-3 rounded-md border border-rose-500/60 bg-rose-500/10 p-3 text-[12px] text-rose-700"
          data-testid="owner-environment-routing-warning"
        >
          <div className="font-semibold mb-1 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" />
            Deployment routing problem detected
          </div>
          <div className="opacity-90">
            You appear to have navigated to <strong>{backendUrl}</strong> or the
            production URL, but the browser is currently serving from{" "}
            <strong>{frontendOrigin || frontendHost}</strong>. This means the
            production deployment is currently routed back to the preview
            infrastructure.
          </div>
          <ul className="list-disc ml-5 mt-1.5 space-y-0.5 opacity-90">
            <li>Re-deploy the app on Emergent to refresh the production routing.</li>
            <li>If a fresh deploy doesn't fix it, contact Emergent Support — this is a platform-level routing/env issue, not an app bug.</li>
            <li>The frontend bundle's <code>REACT_APP_BACKEND_URL</code> is baked at build time. If you need the production frontend to call the production backend (rather than the preview backend), Emergent Support must set <code>REACT_APP_BACKEND_URL</code> to the production URL for the production deploy environment.</li>
          </ul>
        </div>
      ) : null}
      {previewServedAsProd ? (
        <div
          className="mt-3 rounded-md border border-amber-500/60 bg-amber-500/10 p-3 text-[12px] text-amber-700"
          data-testid="owner-environment-served-as-prod"
        >
          <div className="font-semibold mb-1 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" />
            Backend identifies as preview but frontend is at the production URL
          </div>
          <div className="opacity-90">
            The browser is at <strong>{frontendHost}</strong> but the backend
            answering is in <strong>{backendEnv || browserRequestEnv}</strong>{" "}
            mode. The production frontend is calling the preview backend.
            Re-deploy or contact Emergent Support to align the deployment.
          </div>
        </div>
      ) : null}
    </div>
  );
}


/**
 * LastRenderedAnalysisCard — Phase 6.AY rendered-analysis provenance.
 *
 * Sits at the top of the System tab. Reads from
 * `runtime.last_rendered_analysis` (the latest `analysis_rendered`
 * lifecycle event emitted by the Analyze page when the analysis
 * component actually committed to the DOM). This is the canonical
 * answer to: "what did the user actually SEE? was it live FMP, cache,
 * mock, fallback?".
 *
 * Surfaces:
 *   - ticker, request_id, analysis_id, rendered_at, route, user_email
 *   - data_source / cache_hit / cache_layers_used
 *   - mock_used / fallback_used (+ LIVE_DATA_ONLY violation banner)
 *   - endpoint_coverage_pct / chart_data_status / chart_points_count
 *
 * Manual refresh — inherits the System tab's `Refresh all`.
 */
function LastRenderedAnalysisCard({ runtime, loading, onRefresh }) {
  const lra = runtime?.last_rendered_analysis || null;

  const fmtRel = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const diffMs = Date.now() - d.getTime();
      const sec = Math.floor(diffMs / 1000);
      if (sec < 0) return "just now";
      if (sec < 60) return `${sec}s ago`;
      const min = Math.floor(sec / 60);
      if (min < 60) return `${min}m ago`;
      const hr = Math.floor(min / 60);
      if (hr < 24) return `${hr}h ago`;
      return `${Math.floor(hr / 24)}d ago`;
    } catch { return iso; }
  };

  const sourceTone = (src) => {
    if (!src) return "border-border/60 bg-muted/40 text-muted-foreground";
    if (src === "live_fmp") return "border-emerald-500/40 bg-emerald-500/10 text-emerald-700";
    if (src === "partial_live_fmp") return "border-amber-500/40 bg-amber-500/10 text-amber-700";
    if (src === "mock") return "border-rose-500/50 bg-rose-500/10 text-rose-700";
    if (src === "fallback") return "border-rose-400/50 bg-rose-400/10 text-rose-600";
    return "border-blue-500/40 bg-blue-500/10 text-blue-700";
  };

  return (
    <Card title="Last Rendered Analysis" icon={Activity} accent="#0EA5E9">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="text-[11.5px] text-muted-foreground">
          What the user actually SAW on screen (from the frontend
          render-confirmation event). Distinct from the backend audit log
          — this is the canonical "the analysis component committed to
          the DOM" signal with full provenance attached.
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onRefresh}
          disabled={loading}
          data-testid="last-rendered-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {!lra ? (
        <div className="text-sm text-muted-foreground italic" data-testid="last-rendered-empty">
          No rendered analysis captured yet. Open the Analyze page on any
          ticker to produce a render-confirmation event.
        </div>
      ) : (
        <div className="space-y-4" data-testid="last-rendered-content">
          {lra.live_data_only_violation ? (
            <div className="rounded-md border border-rose-500/50 bg-rose-500/10 p-3 text-[12px] text-rose-700" data-testid="last-rendered-violation">
              <div className="font-semibold mb-0.5 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" /> Invalid live result: mock data used while LIVE_DATA_ONLY is active.
              </div>
              <div className="text-[11px] opacity-90">
                This rendered analysis is INVALID and should not be trusted.
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-base font-semibold" data-testid="last-rendered-ticker">
              {lra.ticker || "—"}
            </span>
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${sourceTone(lra.data_source)}`}
              data-testid="last-rendered-data-source"
            >
              {lra.data_source || "unknown"}
            </span>
            {lra.cache_hit ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-700 px-2 py-0.5 text-[10px] font-medium" data-testid="last-rendered-cache-hit">
                cache_hit
              </span>
            ) : null}
            {lra.mock_used ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/50 bg-rose-500/10 text-rose-700 px-2 py-0.5 text-[10px] font-medium" data-testid="last-rendered-mock-used">
                mock_used
              </span>
            ) : null}
            {lra.fallback_used ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-rose-400/40 bg-rose-400/10 text-rose-600 px-2 py-0.5 text-[10px] font-medium" data-testid="last-rendered-fallback-used">
                fallback_used
              </span>
            ) : null}
            <span className="text-[11px] text-muted-foreground ml-auto" data-testid="last-rendered-rendered-at">
              rendered {fmtRel(lra.rendered_at)}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-[12px]">
            <StatRow label="company_name" value={lra.company_name || "—"} testId="last-rendered-company-name" />
            <StatRow label="analysis_id" value={<code className="font-mono text-[10px] break-all">{lra.analysis_id || "—"}</code>} testId="last-rendered-analysis-id" />
            <StatRow label="request_id" value={<code className="font-mono text-[10px] break-all">{lra.request_id || "—"}</code>} testId="last-rendered-request-id" />
            <StatRow label="user_email" value={lra.user_email || <span className="text-muted-foreground italic">anonymous</span>} testId="last-rendered-user-email" />
            <StatRow label="frontend_route" value={<code className="font-mono text-[10.5px]">{lra.frontend_route || lra.route || "—"}</code>} testId="last-rendered-route" />
            <StatRow label="cache_layers_used" value={Array.isArray(lra.cache_layers_used) && lra.cache_layers_used.length ? lra.cache_layers_used.join(", ") : "—"} testId="last-rendered-cache-layers" />
            <StatRow label="result_validity" value={lra.result_validity || "—"} testId="last-rendered-result-validity" />
            <StatRow label="endpoint_coverage_pct" value={lra.endpoint_coverage_pct === null || lra.endpoint_coverage_pct === undefined ? "—" : `${lra.endpoint_coverage_pct}%`} testId="last-rendered-endpoint-coverage" />
            <StatRow label="chart_data_status" value={lra.chart_data_status || "—"} testId="last-rendered-chart-status" />
            <StatRow label="chart_points_count" value={lra.chart_points_count ?? "—"} testId="last-rendered-chart-points" />
            <StatRow label="ai_summary_status" value={lra.ai_summary_status || "—"} testId="last-rendered-ai-summary-status" />
            <StatRow label="llm_skipped_reason" value={lra.llm_skipped_reason || "—"} testId="last-rendered-llm-skipped-reason" />
            <StatRow label="last_completed_stage" value={lra.last_completed_stage || "—"} testId="last-rendered-last-completed-stage" />
            <StatRow label="skipped_stages" value={Array.isArray(lra.skipped_stages) && lra.skipped_stages.length ? lra.skipped_stages.join(", ") : "—"} testId="last-rendered-skipped-stages" />
            <StatRow label="partial_response" value={lra.partial_response === true ? "true" : lra.partial_response === false ? "false" : "—"} testId="last-rendered-partial-response" />
            <StatRow label="canonical_current_price" value={lra.canonical_current_price ?? "—"} testId="last-rendered-canonical-current-price" />
            <StatRow label="canonical_fair_value" value={lra.canonical_fair_value ?? "—"} testId="last-rendered-canonical-fair-value" />
            <StatRow label="canonical_upside_downside_pct" value={lra.canonical_upside_downside_pct === null || lra.canonical_upside_downside_pct === undefined ? "—" : `${lra.canonical_upside_downside_pct}%`} testId="last-rendered-canonical-upside" />
            <StatRow label="canonical_valuation_status" value={lra.canonical_valuation_status || "—"} testId="last-rendered-canonical-valuation-status" />
            <StatRow label="canonical_score" value={lra.canonical_score ?? "—"} testId="last-rendered-canonical-score" />
            <StatRow label="rendered_at" value={<code className="font-mono text-[10.5px]">{lra.rendered_at || "—"}</code>} testId="last-rendered-rendered-at-full" />
            {/* Phase 6.BH+ — explicit AI diagnostic fields surfaced at top level. */}
            <StatRow label="ai_model_primary" value={lra.ai_model_primary || "—"} testId="last-rendered-ai-model-primary" />
            <StatRow label="ai_model_fallback" value={lra.ai_model_fallback || "—"} testId="last-rendered-ai-model-fallback" />
            <StatRow label="ai_model_used" value={lra.ai_model_used || "—"} testId="last-rendered-ai-model-used" />
            <StatRow label="ai_mode" value={lra.ai_mode || "—"} testId="last-rendered-ai-mode" />
            <StatRow label="ai_fallback_attempted" value={lra.ai_fallback_attempted === true ? "true" : lra.ai_fallback_attempted === false ? "false" : "—"} testId="last-rendered-ai-fallback-attempted" />
            <StatRow label="ai_fallback_success" value={lra.ai_fallback_success === true ? "true" : lra.ai_fallback_success === false ? "false" : "—"} testId="last-rendered-ai-fallback-success" />
            <StatRow label="ai_error_type" value={lra.ai_error_type || "—"} testId="last-rendered-ai-error-type" />
            <StatRow label="ai_skipped_reason" value={lra.ai_skipped_reason || lra.llm_skipped_reason || "—"} testId="last-rendered-ai-skipped-reason" />
            <StatRow label="ai_duration_ms" value={lra.ai_duration_ms ?? "—"} testId="last-rendered-ai-duration-ms" />
            <StatRow label="ai_primary_duration_ms" value={lra.ai_primary_duration_ms ?? "—"} testId="last-rendered-ai-primary-duration-ms" />
            <StatRow label="ai_fallback_duration_ms" value={lra.ai_fallback_duration_ms ?? "—"} testId="last-rendered-ai-fallback-duration-ms" />
            <StatRow label="ai_budget_total_ms" value={lra.ai_budget_total_ms ?? "—"} testId="last-rendered-ai-budget-total-ms" />
            <StatRow label="ai_budget_remaining_before_start_ms" value={lra.ai_budget_remaining_before_start_ms ?? "—"} testId="last-rendered-ai-budget-remaining-ms" />
            <StatRow label="ai_tokens_in" value={lra.ai_tokens_in ?? "—"} testId="last-rendered-ai-tokens-in" />
            <StatRow label="ai_tokens_out" value={lra.ai_tokens_out ?? "—"} testId="last-rendered-ai-tokens-out" />
            <StatRow label="ai_cost_usd" value={lra.ai_cost_usd != null ? `$${Number(lra.ai_cost_usd).toFixed(6)}` : "—"} testId="last-rendered-ai-cost-usd" />
            <StatRow label="llm_cache_hit" value={lra.llm_cache_hit === true ? "true" : lra.llm_cache_hit === false ? "false" : "—"} testId="last-rendered-llm-cache-hit" />
            {/* Phase 6.BI-7 — new diagnostic fields */}
            <StatRow label="ai_status_final" value={lra.ai_status_final || lra.ai_summary_status || "—"} testId="last-rendered-ai-status-final" />
            <StatRow label="ai_cancel_source" value={lra.ai_cancel_source || "—"} testId="last-rendered-ai-cancel-source" />
            <StatRow label="ai_model_requested" value={lra.ai_model_requested || "—"} testId="last-rendered-ai-model-requested" />
            <StatRow label="ai_primary_attempted" value={lra.ai_primary_attempted === true ? "true" : lra.ai_primary_attempted === false ? "false" : "—"} testId="last-rendered-ai-primary-attempted" />
            <StatRow label="ai_primary_success" value={lra.ai_primary_success === true ? "true" : lra.ai_primary_success === false ? "false" : "—"} testId="last-rendered-ai-primary-success" />
            <StatRow label="ai_primary_error_type" value={lra.ai_primary_error_type || "—"} testId="last-rendered-ai-primary-error-type" />
            <StatRow label="ai_fallback_error_type" value={lra.ai_fallback_error_type || "—"} testId="last-rendered-ai-fallback-error-type" />
            <StatRow label="ai_budget_remaining_before_primary_ms" value={lra.ai_budget_remaining_before_primary_ms ?? "—"} testId="last-rendered-ai-budget-remaining-primary" />
            <StatRow label="ai_budget_remaining_before_fallback_ms" value={lra.ai_budget_remaining_before_fallback_ms ?? "—"} testId="last-rendered-ai-budget-remaining-fallback" />
            <StatRow label="llm_cache_status" value={lra.llm_cache_status || "—"} testId="last-rendered-llm-cache-status" />
            <StatRow label="llm_cache_write" value={lra.llm_cache_write === true ? "true" : lra.llm_cache_write === false ? "false" : "—"} testId="last-rendered-llm-cache-write" />
            <StatRow label="ai_primary_requested" value={lra.ai_primary_requested || "—"} testId="last-rendered-ai-primary-requested" />
            <StatRow label="ai_routing_reason" value={lra.ai_routing_reason || "—"} testId="last-rendered-ai-routing-reason" />
            <StatRow label="user_plan" value={lra.user_plan || "—"} testId="last-rendered-user-plan" />
            <StatRow label="ai_budget_strategy" value={lra.ai_budget_strategy || "—"} testId="last-rendered-ai-budget-strategy" />
            <StatRow label="full_ai_generated" value={lra.full_ai_generated === true ? "true" : lra.full_ai_generated === false ? "false" : "—"} testId="last-rendered-full-ai-generated" />
            <StatRow label="locked_sections_generated" value={lra.locked_sections_generated === true ? "true" : lra.locked_sections_generated === false ? "false" : "—"} testId="last-rendered-locked-sections-generated" />
            {lra.enriched_from_audit_log ? (
              <StatRow
                label="enriched_from_audit_log"
                value={<span className="inline-flex items-center gap-1 rounded-full border border-blue-500/40 bg-blue-500/10 text-blue-700 px-1.5 py-0.5 text-[10px] font-medium">{Array.isArray(lra.enriched_fields) ? lra.enriched_fields.join(", ") : "true"}</span>}
                testId="last-rendered-enriched-from-audit-log"
              />
            ) : null}
            {typeof lra.audit_polling_attempts === "number" ? (
              <StatRow label="audit_polling_attempts" value={lra.audit_polling_attempts} testId="last-rendered-audit-polling-attempts" />
            ) : null}
            {lra.missing_enrichment ? (
              <StatRow
                label="missing_enrichment"
                value={<span className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/10 text-rose-700 px-1.5 py-0.5 text-[10px] font-medium">{Array.isArray(lra.missing_enrichment_fields) && lra.missing_enrichment_fields.length ? lra.missing_enrichment_fields.join(", ") : "true"}</span>}
                testId="last-rendered-missing-enrichment"
              />
            ) : null}
          </div>

          {/* Phase 6.BG — AI diagnostics (admin-only). Shows the exact
              reason AI was skipped + which model was tried + whether the
              fallback model also ran. */}
          {lra.ai_diagnostics ? (
            <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.04] p-3" data-testid="last-rendered-ai-diagnostics">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300 mb-2">AI Diagnostics</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-[11.5px]">
                <StatRow label="ai_model_requested" value={lra.ai_diagnostics.ai_model_requested || "—"} testId="ai-diag-model-requested" />
                <StatRow label="ai_model_used" value={lra.ai_diagnostics.ai_model_used || "—"} testId="ai-diag-model-used" />
                <StatRow label="ai_retry_attempted" value={String(lra.ai_diagnostics.ai_retry_attempted ?? false)} testId="ai-diag-retry-attempted" />
                <StatRow label="ai_retry_model" value={lra.ai_diagnostics.ai_retry_model || "—"} testId="ai-diag-retry-model" />
                <StatRow label="ai_duration_ms" value={lra.ai_diagnostics.ai_duration_ms ?? "—"} testId="ai-diag-duration-ms" />
                <StatRow label="ai_error_type" value={lra.ai_diagnostics.ai_error_type || "—"} testId="ai-diag-error-type" />
                <StatRow label="ai_skip_reason" value={lra.ai_diagnostics.ai_skip_reason || "—"} testId="ai-diag-skip-reason" />
                <StatRow label="ai_status_final" value={lra.ai_diagnostics.ai_status_final || "—"} testId="ai-diag-status-final" />
                <StatRow label="llm_provider_status" value={lra.ai_diagnostics.llm_provider_status || "—"} testId="ai-diag-provider-status" />
                <StatRow label="llm_budget_status" value={lra.ai_diagnostics.llm_budget_status || "—"} testId="ai-diag-budget-status" />
              </div>
              {Array.isArray(lra.ai_diagnostics.attempts) && lra.ai_diagnostics.attempts.length > 0 ? (
                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Attempts</div>
                  <ul className="space-y-0.5 text-[10.5px] font-mono">
                    {lra.ai_diagnostics.attempts.map((a, i) => (
                      <li key={i} data-testid={`ai-diag-attempt-${i}`}>
                        #{i + 1} <span className="text-amber-700 dark:text-amber-300">{a.model}</span> · attempt={a.attempt} · {a.ms}ms · {a.outcome}{a.error_type ? ` (${a.error_type})` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

/**
 * LlmProviderStatusBanner — Phase 6.BI-14.
 *
 * Renders a green / amber / red strip at the TOP of the LLM Cost & Usage
 * card showing the live provider status DOT plus, when the latest
 * known error is a budget exhaustion, an actionable explanation with
 * the exact $current / $max numbers parsed from the LiteLLM error.
 *
 * IMPORTANT: this component is read-only. It does NOT trigger any LLM
 * call. All state comes from the in-memory `_TELEMETRY` snapshot that
 * the backend rehydrates from Mongo on boot. The "Run smoke test"
 * button on the dedicated `LlmSmokeTestCard` remains the only way to
 * actively probe the provider (which DOES cost budget).
 */
function LlmProviderStatusBanner({ llm }) {
  const status = (llm && llm.provider_status) || "unknown";
  const lastErrorType = (llm && llm.last_error_type) || null;
  const lastErrorMsg = (llm && llm.last_error_message) || null;
  const lastSuccessAt = (llm && llm.last_success_at) || null;
  const quotaCurrent = llm && llm.last_quota_current_cost_usd;
  const quotaMax = llm && llm.last_quota_max_budget_usd;
  const quotaAt = (llm && llm.last_quota_observed_at) || null;

  const styles = {
    ok: {
      dot: "bg-emerald-500",
      label: "OK",
      border: "border-emerald-500/40",
      bg: "bg-emerald-500/[0.06]",
      text: "text-emerald-700 dark:text-emerald-300",
    },
    quota_exhausted: {
      dot: "bg-rose-500",
      label: "Quota exhausted",
      border: "border-rose-500/50",
      bg: "bg-rose-500/[0.08]",
      text: "text-rose-700 dark:text-rose-300",
    },
    auth_error: {
      dot: "bg-rose-500",
      label: "Auth error",
      border: "border-rose-500/50",
      bg: "bg-rose-500/[0.08]",
      text: "text-rose-700 dark:text-rose-300",
    },
    rate_limited: {
      dot: "bg-amber-500",
      label: "Rate limited",
      border: "border-amber-500/40",
      bg: "bg-amber-500/[0.08]",
      text: "text-amber-700 dark:text-amber-300",
    },
    provider_unavailable: {
      dot: "bg-amber-500",
      label: "Provider unavailable",
      border: "border-amber-500/40",
      bg: "bg-amber-500/[0.08]",
      text: "text-amber-700 dark:text-amber-300",
    },
    degraded: {
      dot: "bg-amber-500",
      label: "Degraded",
      border: "border-amber-500/40",
      bg: "bg-amber-500/[0.08]",
      text: "text-amber-700 dark:text-amber-300",
    },
    unknown: {
      dot: "bg-zinc-400",
      label: "Unknown",
      border: "border-border/50",
      bg: "bg-muted/30",
      text: "text-muted-foreground",
    },
  };
  const s = styles[status] || styles.unknown;

  return (
    <div
      className={`mb-4 rounded-lg border ${s.border} ${s.bg} ${s.text} p-3`}
      data-testid="llm-provider-status-banner"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-fit">
          <span
            className={`inline-block w-2.5 h-2.5 rounded-full ${s.dot}`}
            data-testid="llm-provider-status-dot"
          />
          <span className="text-[12px] font-semibold uppercase tracking-wide" data-testid="llm-provider-status-label">
            LLM provider: {s.label}
          </span>
        </div>
        {status === "quota_exhausted" && quotaCurrent != null && quotaMax != null ? (
          <div className="text-[12px] flex-1 min-w-[260px]" data-testid="llm-provider-quota-banner">
            <div className="font-semibold">
              Emergent LLM key budget exhausted (${quotaCurrent.toFixed(3)} / ${quotaMax.toFixed(3)}).
            </div>
            <div className="opacity-90">
              All plans are serving deterministic templates until balance is topped up.{" "}
              <b>Profile → Universal Key → Add Balance.</b>
            </div>
            {quotaAt ? (
              <div className="text-[11px] opacity-70 mt-0.5">Observed at: {fmtDate(quotaAt)}</div>
            ) : null}
          </div>
        ) : status === "quota_exhausted" ? (
          <div className="text-[12px] flex-1 min-w-[260px]" data-testid="llm-provider-quota-banner">
            <div className="font-semibold">Emergent LLM key budget exhausted.</div>
            <div className="opacity-90">
              All plans are serving deterministic templates until balance is topped up.{" "}
              <b>Profile → Universal Key → Add Balance.</b>
            </div>
          </div>
        ) : status !== "ok" && status !== "unknown" ? (
          <div className="text-[12px] flex-1 min-w-[260px]">
            <div className="font-semibold">
              {lastErrorType || "Unknown error"}
            </div>
            {lastErrorMsg ? (
              <div className="opacity-80 font-mono text-[11px] break-all">
                {lastErrorMsg}
              </div>
            ) : null}
          </div>
        ) : status === "unknown" ? (
          <div className="text-[12px] flex-1 min-w-[260px]">
            No LLM activity observed since the backend last restarted, and no historical
            row found in <code>llm_usage_log</code>. Status will refresh on the next analyze
            call, or click <b>Run smoke test</b> below.
          </div>
        ) : (
          <div className="text-[12px] flex-1 min-w-[260px] opacity-90">
            Provider reachable. Last successful call: {lastSuccessAt ? fmtDate(lastSuccessAt) : "—"}.
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * LlmSmokeTestCard — Phase 6.BI-8 admin-only LLM smoke test.
 *
 * Tests Emergent LLM provider reachability with a tiny prompt
 * ("Return exactly: OK"). Calls each configured model individually
 * (gpt-5-mini, gpt-4o-mini, gpt-5) and reports per-model success /
 * timeout / error + duration_ms. Does NOT touch FMP or the analyze
 * pipeline — isolates whether the LLM provider itself is reachable.
 *
 * Use this when AAPL on production reports `fallback_template` to
 * immediately determine whether the LLM provider is reachable at all.
 */
function LlmSmokeTestCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const runTest = async () => {
    setLoading(true);
    setErr(null);
    setData(null);
    try {
      // Phase 6.BI-9 — use the same `adminHeaders()` helper as every
      // other Owner Console request. This reads the X-Admin-Token from
      // sessionStorage (key: stockaily_admin_hint) instead of the
      // wrong localStorage("adminToken") I had before — which sent no
      // X-Admin-Token at all and caused a 401 → global axios
      // interceptor → cleared bearer token → Owner Console redirected
      // to the identify screen (login-loop bug reported by user).
      const res = await api.get("/_health/llm_smoke_test", { headers: adminHeaders() });
      setData(res.data);
    } catch (e) {
      // Phase 6.BI-9 — show INLINE error in the System tab. Do NOT let
      // a 401/403 here kick the Owner Console back to identify. The
      // global 401 interceptor in /lib/api.js already clears the
      // bearer JWT on auth failure, but we DO NOT want that here:
      // smoke-test 401s are admin-side, not user-side. Suppress the
      // auth-failed broadcast by stamping `__skip_auth_broadcast` on
      // the request config (handled by the interceptor below).
      const status = e?.response?.status;
      if (status === 401) {
        setErr("Admin session expired or invalid. Re-authenticate Owner Console and retry. (Owner session was preserved.)");
      } else if (status === 403) {
        setErr("Not an admin email. Contact a Stockaily operator.");
      } else if (status === 404) {
        setErr("LLM smoke test endpoint not available on this backend (admin disabled, or older build).");
      } else {
        setErr(e?.response?.data?.detail || e?.message || "Smoke test failed");
      }
    } finally {
      setLoading(false);
    }
  };
  const statusColor = (status) => {
    if (status === "success") return "text-emerald-600 dark:text-emerald-400";
    if (status === "timeout") return "text-amber-600 dark:text-amber-400";
    return "text-rose-600 dark:text-rose-400";
  };
  const summaryBadge = (s) => {
    const map = {
      ok: { cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300", label: "OK" },
      partial: { cls: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300", label: "PARTIAL" },
      all_failed: { cls: "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300", label: "ALL FAILED" },
    };
    const m = map[s] || map.all_failed;
    return (
      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.cls}`} data-testid="llm-smoke-test-summary">
        {m.label}
      </span>
    );
  };
  return (
    <Card title="LLM Smoke Test" icon={Activity} accent="#A855F7">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-muted-foreground max-w-prose">
          Admin-only LLM reachability probe. Calls each configured model with a tiny
          5-token prompt ("Return exactly: OK"). Does NOT touch FMP or the analyze
          pipeline — answers "is Emergent LLM access working AT ALL?" in isolation.
        </div>
        <Button
          type="button"
          size="sm"
          variant="default"
          onClick={runTest}
          disabled={loading}
          data-testid="llm-smoke-test-run-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Testing…" : "Run smoke test"}
        </Button>
      </div>
      {err ? (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-[12px] text-rose-700 dark:text-rose-300" data-testid="llm-smoke-test-error">
          Error: {err}
        </div>
      ) : null}
      {data ? (
        <div className="space-y-3" data-testid="llm-smoke-test-result">
          <div className="flex items-center gap-3 flex-wrap text-[12px]">
            <span>Summary:</span> {summaryBadge(data.summary)}
            <span className="text-muted-foreground">
              · provider_enabled: <span className="font-mono">{String(data.provider_enabled)}</span>
              {" "}· key_source: <span className="font-mono">{data.key_source || "—"}</span>
              {" "}· elapsed: <span className="font-mono">{data.elapsed_ms}ms</span>
            </span>
          </div>
          {data.error ? (
            <div className="rounded border border-rose-500/30 bg-rose-500/[0.04] p-2 text-[12px] text-rose-700 dark:text-rose-300">
              error: <span className="font-mono">{data.error}</span>
            </div>
          ) : null}
          <div className="rounded-lg border border-border/50 overflow-hidden" data-testid="llm-smoke-test-table">
            <table className="w-full text-[12px]">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Model</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Duration</th>
                  <th className="text-right px-3 py-2">Tokens In/Out</th>
                  <th className="text-left px-3 py-2">Response / Error</th>
                </tr>
              </thead>
              <tbody>
                {(data.models_tested || []).map((m, i) => (
                  <tr key={m.model || i} className="border-t border-border/40" data-testid={`llm-smoke-row-${m.model}`}>
                    <td className="px-3 py-2 font-mono">{m.model}</td>
                    <td className={`px-3 py-2 font-semibold ${statusColor(m.status)}`}>{m.status}</td>
                    <td className="px-3 py-2 text-right font-mono">{m.duration_ms}ms</td>
                    <td className="px-3 py-2 text-right font-mono">{m.tokens_in}/{m.tokens_out}</td>
                    <td className="px-3 py-2 text-[11px]">
                      {m.status === "success" ? (
                        <code className="text-emerald-700 dark:text-emerald-300">{m.response_preview}</code>
                      ) : (
                        <span className="text-rose-700 dark:text-rose-300">
                          {m.error_type}{m.raw_error ? `: ${m.raw_error}` : ""}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (!err && !loading ? (
        <div className="text-[12px] text-muted-foreground" data-testid="llm-smoke-test-empty">
          Click "Run smoke test" to probe LLM provider reachability.
        </div>
      ) : null)}
    </Card>
  );
}


/**
 * RuntimeStatusCard — Phase 6.AA production diagnostics.
 *
 * Renders the response from `GET /api/admin/debug/runtime-status`. Designed
 * to be the FIRST thing the operator looks at when investigating "why is
 * production behaving differently from preview" issues. Shows:
 *
 *   - build identity (timestamp, git SHA, hostname, pid, uptime)
 *   - effective env flags actually seen by THIS process
 *   - provider selection + FMP enable state
 *   - last analyze request observed by THIS replica
 *   - Mongo connectivity
 *   - cache sizes (which can be cleared from the Cache Clear card below)
 */


/**
 * AdminQAPlanSimulationCard — Phase 6.BI-12 admin QA tool.
 *
 * Lets the operator run an analyze request as if they were on a
 * different plan tier (free / pro / premium) WITHOUT being blocked
 * by the free-tier quota. Useful for verifying:
 *   * Free shows basic_summary + locked AI Insights teaser
 *   * Pro/Premium shows structured Full AI Insights
 *   * Deterministic valuation (Score, Fair Value, Risk, KPIs, Chart)
 *     is IDENTICAL across plans
 *
 * Backend support:
 *   * /api/analyze/{ticker}?simulate_plan=free|pro|premium (admin only)
 *   * usage_service.can_analyze + record_analyze both bypass quota /
 *     KPI tracking for admin accounts.
 */
/**
 * LlmProviderAbstractionCard — Phase 7.UI.14.
 *
 * Owner-only diagnostics for the LLM provider abstraction layer.
 *
 *  • Shows the active provider (env: LLM_PROVIDER, default "emergent").
 *  • Shows configuration state for BOTH providers (configured / not).
 *  • Button A: "Test active LLM provider"  — runs against whatever
 *    provider is selected by LLM_PROVIDER.
 *  • Button B: "Test OpenAI configuration" — always tests the OpenAI
 *    provider regardless of LLM_PROVIDER, so admins can verify the
 *    OPENAI_API_KEY / model routing WITHOUT live-switching providers.
 *
 * Both smoke tests are isolated:
 *   - no FMP call
 *   - no user quota consumption
 *   - no analysis cache mutation
 *   - never expose API key material
 */
function LlmProviderAbstractionCard() {
  const [status, setStatus] = useState(null);
  const [statusErr, setStatusErr] = useState(null);
  const [active, setActive] = useState(null);
  const [openai, setOpenai] = useState(null);
  const [loadingActive, setLoadingActive] = useState(false);
  const [loadingOpenai, setLoadingOpenai] = useState(false);

  const fetchStatus = async () => {
    setStatusErr(null);
    try {
      const res = await api.get("/admin/debug/llm-provider-status", { headers: adminHeaders() });
      setStatus(res.data);
    } catch (e) {
      setStatusErr(e?.response?.data?.detail || e?.message || "Failed to load provider status");
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const runActive = async () => {
    setLoadingActive(true);
    setActive(null);
    try {
      const res = await api.post("/admin/debug/llm-smoke-test", {}, { headers: adminHeaders() });
      setActive(res.data);
    } catch (e) {
      setActive({ status: "failed", error_type: "client_error", message: e?.response?.data?.detail || e?.message || "Request failed" });
    } finally {
      setLoadingActive(false);
    }
  };

  const runOpenai = async () => {
    setLoadingOpenai(true);
    setOpenai(null);
    try {
      const res = await api.post("/admin/debug/openai-smoke-test", {}, { headers: adminHeaders() });
      setOpenai(res.data);
    } catch (e) {
      setOpenai({ status: "failed", error_type: "client_error", message: e?.response?.data?.detail || e?.message || "Request failed" });
    } finally {
      setLoadingOpenai(false);
    }
  };

  const statusColor = (s) => {
    if (s === "success") return "text-emerald-600 dark:text-emerald-400";
    if (s === "not_configured" || s === "timeout") return "text-amber-600 dark:text-amber-400";
    return "text-rose-600 dark:text-rose-400";
  };

  const renderResult = (r, prefix) => {
    if (!r) return null;
    return (
      <div className="mt-2 rounded-lg border border-border/60 bg-card/40 p-3 text-[12px]" data-testid={`${prefix}-result`}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2">
            <span className={`font-semibold ${statusColor(r.status)}`} data-testid={`${prefix}-status`}>
              {r.status}
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">provider: <span className="font-mono">{r.provider}</span></span>
          </div>
          {r.duration_ms != null ? (
            <span className="text-muted-foreground font-mono">{r.duration_ms}ms</span>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11.5px] text-muted-foreground">
          {r.model_requested ? (<><span>model requested</span><span className="font-mono text-right">{r.model_requested}</span></>) : null}
          {r.model_used ? (<><span>model used</span><span className="font-mono text-right">{r.model_used}</span></>) : null}
          {r.token_usage_prompt ? (<><span>tokens prompt</span><span className="font-mono text-right">{r.token_usage_prompt}</span></>) : null}
          {r.token_usage_completion ? (<><span>tokens completion</span><span className="font-mono text-right">{r.token_usage_completion}</span></>) : null}
          {r.estimated_cost_usd ? (<><span>est. cost</span><span className="font-mono text-right">${Number(r.estimated_cost_usd).toFixed(6)}</span></>) : null}
          {r.provider_request_id ? (<><span>request id</span><span className="font-mono text-right truncate">{r.provider_request_id}</span></>) : null}
          {r.error_type ? (<><span>error type</span><span className="font-mono text-right">{r.error_type}</span></>) : null}
        </div>
        {r.message ? (
          <div className="mt-1.5 text-[11px] text-muted-foreground italic break-words">{r.message}</div>
        ) : null}
      </div>
    );
  };

  return (
    <Card title="LLM Provider Abstraction" icon={Activity} accent="#22D3EE">
      <div className="text-xs text-muted-foreground mb-3 max-w-prose">
        Lets the platform switch between Emergent and OpenAI without touching analysis logic.
        Default is <span className="font-mono">emergent</span>. To switch later, set the backend env
        variable <span className="font-mono">LLM_PROVIDER=openai</span> and provide{" "}
        <span className="font-mono">OPENAI_API_KEY</span> — no code change required. Never exposes the API key.
      </div>

      {statusErr ? (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-[12px] text-rose-700 dark:text-rose-300 mb-3">
          {statusErr}
        </div>
      ) : null}

      {status ? (
        <div className="rounded-lg border border-border/60 bg-card/30 p-3 mb-3" data-testid="llm-provider-status">
          <div className="grid grid-cols-3 gap-3 text-[12px]">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mb-0.5">Active provider</div>
              <div className="font-mono font-semibold" data-testid="llm-active-provider">{status.active_llm_provider}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mb-0.5">Emergent</div>
              <div className={`font-mono ${status.emergent?.configured ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                {status.emergent?.configured ? "configured" : "not configured"}
              </div>
              {status.emergent?.key_source ? (
                <div className="text-[10.5px] text-muted-foreground font-mono">key: {status.emergent.key_source}</div>
              ) : null}
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mb-0.5">OpenAI</div>
              <div className={`font-mono ${status.openai?.configured ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                {status.openai?.configured ? "configured" : "not configured"}
              </div>
              {status.openai?.base_url ? (
                <div className="text-[10.5px] text-muted-foreground font-mono truncate">{status.openai.base_url}</div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-lg border border-border/60 bg-card/30 p-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-sm font-medium">Test active LLM provider</div>
            <Button type="button" size="sm" onClick={runActive} disabled={loadingActive} data-testid="llm-active-smoke-run">
              {loadingActive ? "Testing…" : "Run"}
            </Button>
          </div>
          <div className="text-[11px] text-muted-foreground">
            Honors <span className="font-mono">LLM_PROVIDER</span>. No FMP call, no analysis cache, no user quota.
          </div>
          {renderResult(active, "llm-active-smoke")}
        </div>

        <div className="rounded-lg border border-border/60 bg-card/30 p-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-sm font-medium">Test OpenAI configuration</div>
            <Button type="button" size="sm" variant="outline" onClick={runOpenai} disabled={loadingOpenai} data-testid="llm-openai-smoke-run">
              {loadingOpenai ? "Testing…" : "Run"}
            </Button>
          </div>
          <div className="text-[11px] text-muted-foreground">
            Always tests the OpenAI provider — even when Emergent is active. Returns{" "}
            <span className="font-mono">not_configured</span> cleanly when the API key is missing.
          </div>
          {renderResult(openai, "llm-openai-smoke")}
        </div>
      </div>
    </Card>
  );
}



/**
 * CopecartBillingCard — Phase 7.UI.15.
 *
 * Owner-only diagnostics for the CopeCart billing integration:
 *  • Provider status (enabled, checkout_enabled, secret_configured)
 *  • Product mapping table
 *  • Recent webhook events (with raw payload accessible per-event)
 *  • Unmatched events with manual link action
 *  • Simulation form (no real CopeCart traffic, never counts as revenue)
 */
function CopecartBillingCard() {
  const [status, setStatus] = useState(null);
  const [statusErr, setStatusErr] = useState(null);
  const [events, setEvents] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [busy, setBusy] = useState(false);
  // simulation form state
  const [simEvent, setSimEvent] = useState("payment_success");
  const [simPlan, setSimPlan] = useState("pro");
  const [simInterval, setSimInterval] = useState("monthly");
  const [simEmail, setSimEmail] = useState("");
  const [simResult, setSimResult] = useState(null);

  const [eventsErr, setEventsErr] = useState(null);
  const [rawEvent, setRawEvent] = useState(null);
  const [rawLoading, setRawLoading] = useState(false);

  const viewRaw = async (eventId) => {
    setRawEvent(null);
    setRawLoading(true);
    try {
      const res = await api.get(`/admin/debug/billing/event/${encodeURIComponent(eventId)}`, { headers: adminHeaders() });
      setRawEvent(res.data);
    } catch (e) {
      setRawEvent({ _error: e?.response?.data?.detail || e?.message || "Failed to load event" });
    } finally {
      setRawLoading(false);
    }
  };

  const load = async () => {
    setStatusErr(null);
    try {
      const s = await api.get("/admin/debug/billing/status", { headers: adminHeaders() });
      setStatus(s.data);
    } catch (e) {
      setStatusErr(e?.response?.data?.detail || e?.message || "Failed to load billing status");
    }
    setEventsErr(null);
    try {
      const e = await api.get("/admin/debug/billing/events?limit=15", { headers: adminHeaders() });
      setEvents(e.data?.events || []);
    } catch (e) {
      // Phase 7.UI.15 hotfix — surface the error so admins can see why
      // the feed is empty (e.g., 500 from ObjectId serialization in
      // legacy rows). Previously this was swallowed silently and the
      // panel showed "No events yet." which was misleading.
      const detail = e?.response?.data?.detail || e?.message || "Failed to load events";
      setEventsErr(typeof detail === "string" ? detail : JSON.stringify(detail));
      setEvents([]);
    }
    try {
      const u = await api.get("/admin/debug/billing/unmatched?limit=10", { headers: adminHeaders() });
      setUnmatched(u.data?.events || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const runSimulation = async () => {
    if (!simEmail) return;
    setBusy(true);
    setSimResult(null);
    try {
      const res = await api.post(
        "/admin/debug/billing/simulate-copecart-event",
        { event_type: simEvent, plan: simPlan, interval: simInterval, target_email: simEmail },
        { headers: adminHeaders() },
      );
      setSimResult(res.data);
      await load();
    } catch (e) {
      setSimResult({ status: "failed", detail: e?.response?.data?.detail || e?.message });
    } finally {
      setBusy(false);
    }
  };

  const procColor = (s) => {
    if (s === "processed") return "text-emerald-600 dark:text-emerald-400";
    if (s === "ignored") return "text-amber-600 dark:text-amber-400";
    if (s === "failed") return "text-rose-600 dark:text-rose-400";
    return "text-muted-foreground";
  };

  // Phase 7.UI.15h — one-time idempotent migration to normalize legacy
  // copecart_* fields to canonical copcart_* spellings and backfill the
  // latest_payment_test_mode flag from billing_events.
  const [migrateBusy, setMigrateBusy] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const runNormalizeMigration = async () => {
    if (!window.confirm(
      "Run the CopeCart user normalization migration?\n\n" +
      "This will:\n" +
      "• Normalize plan_source 'copecart' → 'copcart' on existing users.\n" +
      "• Mirror copecart_* fields to canonical copcart_* names.\n" +
      "• Backfill latest_payment_test_mode from the user's most recent processed billing_event.\n\n" +
      "Idempotent: safe to run multiple times."
    )) return;
    setMigrateBusy(true);
    setMigrateResult(null);
    try {
      const res = await api.post(
        "/admin/debug/billing/normalize-copcart-users",
        { confirm: true },
        { headers: adminHeaders() },
      );
      setMigrateResult(res.data);
      if (typeof load === "function") {
        try { load(); } catch { /* ignore */ }
      }
    } catch (e) {
      setMigrateResult({ status: "failed", detail: e?.response?.data?.detail || e?.message });
    } finally {
      setMigrateBusy(false);
    }
  };

  return (
    <Card title="CopeCart Billing" icon={Activity} accent="#A855F7">
      <div className="text-xs text-muted-foreground mb-3 max-w-prose">
        Billing infrastructure for paid plans. Webhook events are recorded even when the integration is
        disabled, so admins can verify CopeCart connectivity before flipping the master switch. Secrets
        are never exposed via the API; only a boolean "configured" flag is shown below.
      </div>

      {statusErr ? (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-[12px] text-rose-700 dark:text-rose-300 mb-3">
          {statusErr}
        </div>
      ) : null}

      {status ? (
        <div className="rounded-lg border border-border/60 bg-card/30 p-3 mb-3" data-testid="copecart-status">
          <div className="grid grid-cols-3 gap-3 text-[12px]">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Enabled</div>
              <div className={`font-mono ${status.copecart_enabled ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                {String(status.copecart_enabled)}
              </div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Checkout enabled</div>
              <div className={`font-mono ${status.copecart_checkout_enabled ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                {String(status.copecart_checkout_enabled)}
              </div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Webhook secret</div>
              <div className={`font-mono ${status.webhook_secret_configured ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                {status.webhook_secret_configured ? "configured" : "not configured"}
              </div>
            </div>
          </div>
          {Array.isArray(status.product_mapping) ? (
            <div className="mt-3 text-[11.5px]">
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mb-1">Product mapping</div>
              <div className="grid grid-cols-4 gap-x-3 gap-y-1 font-mono">
                <div className="text-muted-foreground">product_id</div>
                <div className="text-muted-foreground">plan</div>
                <div className="text-muted-foreground">interval</div>
                <div className="text-muted-foreground">price</div>
                {status.product_mapping.map((p) => (
                  <React.Fragment key={p.product_id}>
                    <div className="truncate">{p.product_id}</div>
                    <div>{p.plan}</div>
                    <div>{p.interval}</div>
                    <div>€{p.price_eur}</div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Simulation form */}
      <div className="rounded-lg border border-border/60 bg-card/30 p-3 mb-3" data-testid="copecart-simulation-form">
        <div className="text-sm font-medium mb-2">Simulate CopeCart event</div>
        <div className="text-[11px] text-muted-foreground mb-2">
          Does not call CopeCart. Marks the event as simulated. Will not count as real revenue.
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[12px]">
          <select
            className="rounded-md border border-border/70 bg-card/50 px-2 py-1.5 text-[12px]"
            value={simEvent}
            onChange={(e) => setSimEvent(e.target.value)}
            data-testid="copecart-sim-event"
          >
            <option value="payment_success">payment_success</option>
            <option value="renewal_success">renewal_success</option>
            <option value="subscription_cancelled">subscription_cancelled</option>
            <option value="payment_failed">payment_failed</option>
            <option value="refund">refund</option>
            <option value="chargeback">chargeback</option>
            <option value="expired">expired</option>
          </select>
          <select
            className="rounded-md border border-border/70 bg-card/50 px-2 py-1.5 text-[12px]"
            value={simPlan}
            onChange={(e) => setSimPlan(e.target.value)}
            data-testid="copecart-sim-plan"
          >
            <option value="pro">pro</option>
            <option value="premium">premium</option>
          </select>
          <select
            className="rounded-md border border-border/70 bg-card/50 px-2 py-1.5 text-[12px]"
            value={simInterval}
            onChange={(e) => setSimInterval(e.target.value)}
            data-testid="copecart-sim-interval"
          >
            <option value="monthly">monthly</option>
            <option value="yearly">yearly</option>
          </select>
          <input
            type="email"
            placeholder="target user email"
            className="rounded-md border border-border/70 bg-card/50 px-2 py-1.5 text-[12px] col-span-2 md:col-span-1"
            value={simEmail}
            onChange={(e) => setSimEmail(e.target.value)}
            data-testid="copecart-sim-email"
          />
          <Button
            type="button"
            size="sm"
            onClick={runSimulation}
            disabled={busy || !simEmail}
            data-testid="copecart-sim-run"
          >
            {busy ? "Simulating…" : "Run"}
          </Button>
        </div>
        {simResult ? (
          <pre className="mt-2 rounded-md bg-card/50 p-2 text-[11px] font-mono overflow-x-auto" data-testid="copecart-sim-result">
            {JSON.stringify(simResult, null, 2)}
          </pre>
        ) : null}
      </div>

      {/* Recent events */}
      <div className="rounded-lg border border-border/60 bg-card/30 p-3 mb-3" data-testid="copecart-recent-events">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium">Recent billing events</div>
          <Button type="button" variant="outline" size="sm" onClick={load} data-testid="copecart-events-refresh">Refresh</Button>
        </div>
        {eventsErr ? (
          <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-[11.5px] text-rose-700 dark:text-rose-300 mb-2" data-testid="copecart-events-error">
            {eventsErr}
          </div>
        ) : null}
        {events.length === 0 ? (
          <div className="text-[11.5px] text-muted-foreground italic">{eventsErr ? "Could not load events." : "No events yet."}</div>
        ) : (
          <div className="grid grid-cols-7 gap-x-3 gap-y-1 text-[11.5px] font-mono">
            <div className="text-muted-foreground">event_type</div>
            <div className="text-muted-foreground">plan/product</div>
            <div className="text-muted-foreground">email</div>
            <div className="text-muted-foreground">order_id</div>
            <div className="text-muted-foreground">status</div>
            <div className="text-muted-foreground">error / when</div>
            <div className="text-muted-foreground">raw</div>
            {events.map((e) => (
              <React.Fragment key={e._id}>
                <div className="truncate">{e.event_type}{e.simulated ? " (sim)" : ""}</div>
                <div className="truncate">{e.product_id || "-"}</div>
                <div className="truncate">{e.customer_email || "-"}</div>
                <div className="truncate">{e.order_id || "-"}</div>
                <div className={procColor(e.processed_status)}>{e.processed_status || "-"}</div>
                <div className="truncate text-muted-foreground" title={e.processing_error || ""}>
                  {e.processing_error ? <span className="text-rose-600 dark:text-rose-400 truncate">{e.processing_error}</span> : (e.created_at || "").slice(0, 19)}
                </div>
                <div>
                  <button
                    type="button"
                    className="text-[10.5px] underline text-muted-foreground hover:text-foreground"
                    onClick={() => viewRaw(e._id)}
                    data-testid={`copecart-view-raw-${e._id}`}
                  >
                    view
                  </button>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
        {rawLoading ? (
          <div className="mt-2 text-[11.5px] text-muted-foreground italic">Loading raw payload…</div>
        ) : null}
        {rawEvent ? (
          <div className="mt-2 rounded-md border border-border/60 bg-card/50 p-2" data-testid="copecart-raw-payload">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Raw payload (admin-only)</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-[10.5px] underline text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    try { navigator.clipboard.writeText(JSON.stringify(rawEvent, null, 2)); } catch { /* ignore */ }
                  }}
                >
                  copy
                </button>
                <button
                  type="button"
                  className="text-[10.5px] underline text-muted-foreground hover:text-foreground"
                  onClick={() => setRawEvent(null)}
                >
                  close
                </button>
              </div>
            </div>
            <pre className="text-[10.5px] font-mono overflow-x-auto max-h-[420px] whitespace-pre-wrap">{JSON.stringify(rawEvent, null, 2)}</pre>
          </div>
        ) : null}
      </div>

      {/* Unmatched events */}
      {unmatched.length > 0 ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" data-testid="copecart-unmatched">
          <div className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-1">Unmatched billing events</div>
          <div className="text-[11.5px] text-muted-foreground mb-2">
            These events arrived but no Stockaily user could be matched by email or tracking key. Link them
            manually from the User Management tab once the account is identified.
          </div>
          <div className="grid grid-cols-5 gap-x-3 gap-y-1 text-[11.5px] font-mono">
            <div className="text-muted-foreground">event_type</div>
            <div className="text-muted-foreground">email</div>
            <div className="text-muted-foreground">product_id</div>
            <div className="text-muted-foreground">order_id</div>
            <div className="text-muted-foreground">event_id</div>
            {unmatched.map((e) => (
              <React.Fragment key={e._id}>
                <div className="truncate">{e.event_type}</div>
                <div className="truncate">{e.customer_email || "-"}</div>
                <div className="truncate">{e.product_id || "-"}</div>
                <div className="truncate">{e.order_id || "-"}</div>
                <div className="truncate">{e._id}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : null}

      {/* Phase 7.UI.15h — one-time admin migration to normalize the
          CopeCart user field spellings + backfill the test-payment flag. */}
      <div className="rounded-lg border border-border/60 bg-card/30 p-3 mt-3" data-testid="copecart-normalize-users">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Normalize CopeCart users (one-time backfill)</div>
            <div className="text-[11px] text-muted-foreground max-w-prose">
              Idempotent migration: normalizes <span className="font-mono">plan_source</span>{" "}
              <span className="font-mono">copecart</span> → <span className="font-mono">copcart</span>,
              mirrors <span className="font-mono">copecart_*</span> ↔ <span className="font-mono">copcart_*</span>{" "}
              fields, and backfills <span className="font-mono">latest_payment_test_mode</span> from each
              user&apos;s latest processed billing event. Run this once after deploying the spelling fix.
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={runNormalizeMigration}
            disabled={migrateBusy}
            data-testid="copecart-normalize-users-run"
          >
            {migrateBusy ? "Running…" : "Run migration"}
          </Button>
        </div>
        {migrateResult ? (
          <pre
            className={`mt-2 rounded-md border p-2 text-[10.5px] font-mono overflow-x-auto whitespace-pre-wrap ${
              migrateResult.status === "ok"
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                : "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300"
            }`}
            data-testid="copecart-normalize-users-result"
          >
            {JSON.stringify(migrateResult, null, 2)}
          </pre>
        ) : null}
      </div>

      {/* Phase 7.UI.15d — Webhook attempts (raw inbound diagnostics) */}
      <WebhookAttemptsSection onReplayed={load} />
    </Card>
  );
}


/**
 * WebhookAttemptsSection — Phase 7.UI.15d.
 *
 * Admin-only diagnostics for every inbound POST to:
 *   • /api/billing/copecart/webhook
 *   • /api/billing/webhook
 *
 * Shows whether CopeCart reached Stockaily at all, on which route, with
 * which content-type, parse status, auth status, HTTP status, parsed
 * business fields, and (admin-only) a redacted body preview. Operators
 * can replay an attempt manually (requires explicit confirmation).
 */
function WebhookAttemptsSection({ onReplayed }) {
  const [attempts, setAttempts] = useState([]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replayBusy, setReplayBusy] = useState(false);
  const [replayResult, setReplayResult] = useState(null);

  const load = async () => {
    setErr(null);
    setLoading(true);
    try {
      const res = await api.get("/admin/debug/billing/webhook-attempts?limit=50", { headers: adminHeaders() });
      setAttempts(res.data?.attempts || []);
    } catch (e) {
      const d = e?.response?.data?.detail || e?.message || "Failed to load webhook attempts";
      setErr(typeof d === "string" ? d : JSON.stringify(d));
      setAttempts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const viewDetail = async (attemptId) => {
    setDetail(null);
    setDetailLoading(true);
    setReplayResult(null);
    try {
      const res = await api.get(
        `/admin/debug/billing/webhook-attempts/${encodeURIComponent(attemptId)}`,
        { headers: adminHeaders() },
      );
      setDetail(res.data);
    } catch (e) {
      setDetail({ _error: e?.response?.data?.detail || e?.message || "Failed to load attempt" });
    } finally {
      setDetailLoading(false);
    }
  };

  const replay = async (attemptId, isTestMode) => {
    const message = isTestMode
      ? "Replay this TEST webhook attempt through the billing pipeline? This may upgrade the matched test user."
      : "Replay this REAL webhook attempt through the billing pipeline? This may upgrade a real user. Continue?";
    if (!window.confirm(message)) return;
    setReplayBusy(true);
    setReplayResult(null);
    try {
      const res = await api.post(
        `/admin/debug/billing/webhook-attempts/${encodeURIComponent(attemptId)}/replay`,
        { confirm: true },
        { headers: adminHeaders() },
      );
      setReplayResult(res.data);
      await load();
      if (typeof onReplayed === "function") {
        try { onReplayed(); } catch { /* ignore */ }
      }
    } catch (e) {
      setReplayResult({ status: "failed", detail: e?.response?.data?.detail || e?.message });
    } finally {
      setReplayBusy(false);
    }
  };

  const parseColor = (s) => {
    if (s === "ok") return "text-emerald-600 dark:text-emerald-400";
    if (typeof s === "string" && s.startsWith("parse_failed")) return "text-rose-600 dark:text-rose-400";
    return "text-muted-foreground";
  };
  const authColor = (s) => {
    const v = String(s || "");
    if (v.startsWith("accepted_hmac") || v.startsWith("accepted_body_password") || v === "accepted" || v === "accepted_dev_no_secret") {
      return "text-emerald-600 dark:text-emerald-400";
    }
    if (v === "rejected_invalid_signature" || v === "rejected_invalid_body_password" || v === "rejected_signature") {
      return "text-rose-600 dark:text-rose-400";
    }
    if (v === "rejected_no_auth" || v === "rejected_missing_secret" || v === "rejected_other") {
      return "text-amber-600 dark:text-amber-400";
    }
    return "text-muted-foreground";
  };

  return (
    <div className="rounded-lg border border-border/60 bg-card/30 p-3 mt-3" data-testid="copecart-webhook-attempts">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium">Recent webhook attempts</div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="webhook-attempts-refresh"
        >
          {loading ? "Loading…" : "Refresh"}
        </Button>
      </div>
      <div className="text-[11px] text-muted-foreground mb-2 max-w-prose">
        Every inbound POST to <span className="font-mono">/api/billing/copecart/webhook</span> and{" "}
        <span className="font-mono">/api/billing/webhook</span> is logged here — including rejected and malformed
        requests — so you can confirm CopeCart connectivity even when an order didn&apos;t process. Secrets and
        signature header values are never stored.
      </div>

      {err ? (
        <div
          className="rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-[11.5px] text-rose-700 dark:text-rose-300 mb-2"
          data-testid="webhook-attempts-error"
        >
          {err}
        </div>
      ) : null}

      {attempts.length === 0 && !err ? (
        <div className="text-[11.5px] text-muted-foreground italic" data-testid="webhook-attempts-empty">
          No webhook attempts logged yet.
        </div>
      ) : null}

      {attempts.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono" data-testid="webhook-attempts-table">
            <thead>
              <tr className="text-muted-foreground border-b border-border/40">
                <th className="text-left py-1 pr-2">received_at</th>
                <th className="text-left py-1 pr-2">path</th>
                <th className="text-left py-1 pr-2">content_type</th>
                <th className="text-left py-1 pr-2">http</th>
                <th className="text-left py-1 pr-2">parse</th>
                <th className="text-left py-1 pr-2">auth</th>
                <th className="text-left py-1 pr-2">reason</th>
                <th className="text-left py-1 pr-2">product_id</th>
                <th className="text-left py-1 pr-2">email</th>
                <th className="text-left py-1 pr-2">order_id</th>
                <th className="text-left py-1 pr-2">test_mode</th>
                <th className="text-left py-1 pr-2">linked_event_id</th>
                <th className="text-left py-1 pr-2">actions</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map((a) => {
                const route = (a.path || "").replace("/api/billing", "");
                return (
                  <tr
                    key={a._id}
                    className="border-b border-border/20 align-top"
                    data-testid={`webhook-attempt-row-${a._id}`}
                  >
                    <td className="py-1 pr-2 whitespace-nowrap">{(a.received_at || "").slice(0, 19)}</td>
                    <td className="py-1 pr-2 whitespace-nowrap">{route || a.path || "-"}</td>
                    <td className="py-1 pr-2 whitespace-nowrap">{a.content_type || "-"}</td>
                    <td className="py-1 pr-2">{a.http_status_returned ?? "-"}</td>
                    <td className={`py-1 pr-2 ${parseColor(a.parse_status)}`}>{a.parse_status || "-"}</td>
                    <td className={`py-1 pr-2 ${authColor(a.auth_status)}`}>{a.auth_status || "-"}</td>
                    <td
                      className="py-1 pr-2 max-w-[180px] truncate"
                      title={a.reason || a.processing_error || ""}
                    >
                      {a.processing_error
                        ? <span className="text-rose-600 dark:text-rose-400">{a.processing_error}</span>
                        : (a.reason || "-")}
                    </td>
                    <td className="py-1 pr-2 truncate max-w-[140px]" title={a.parsed_product_id || ""}>
                      {a.parsed_product_id || "-"}
                    </td>
                    <td className="py-1 pr-2 truncate max-w-[180px]" title={a.parsed_customer_email || ""}>
                      {a.parsed_customer_email || "-"}
                    </td>
                    <td className="py-1 pr-2 truncate max-w-[140px]" title={a.parsed_order_id || ""}>
                      {a.parsed_order_id || "-"}
                    </td>
                    <td className="py-1 pr-2">
                      {a.parsed_test_mode ? (
                        <span className="text-amber-600 dark:text-amber-400">test</span>
                      ) : "-"}
                    </td>
                    <td className="py-1 pr-2 truncate max-w-[160px]" title={a.linked_event_id || ""}>
                      {a.linked_event_id || "-"}
                    </td>
                    <td className="py-1 pr-2 whitespace-nowrap">
                      <button
                        type="button"
                        className="text-[10.5px] underline text-muted-foreground hover:text-foreground mr-2"
                        onClick={() => viewDetail(a._id)}
                        data-testid={`webhook-attempt-view-${a._id}`}
                      >
                        view
                      </button>
                      <button
                        type="button"
                        className="text-[10.5px] underline text-violet-600 dark:text-violet-300 hover:text-foreground disabled:opacity-50"
                        onClick={() => replay(a._id, a.parsed_test_mode)}
                        disabled={replayBusy}
                        data-testid={`webhook-attempt-replay-${a._id}`}
                      >
                        replay
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {detailLoading ? (
        <div className="mt-2 text-[11.5px] text-muted-foreground italic">Loading attempt detail…</div>
      ) : null}

      {detail ? (
        <div
          className="mt-3 rounded-md border border-border/60 bg-card/50 p-2"
          data-testid="webhook-attempt-detail"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
              Attempt detail (admin-only, redacted)
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="text-[10.5px] underline text-muted-foreground hover:text-foreground"
                onClick={() => {
                  try { navigator.clipboard.writeText(JSON.stringify(detail, null, 2)); } catch { /* ignore */ }
                }}
              >
                copy
              </button>
              <button
                type="button"
                className="text-[10.5px] underline text-muted-foreground hover:text-foreground"
                onClick={() => setDetail(null)}
              >
                close
              </button>
            </div>
          </div>
          {detail._error ? (
            <div className="text-[11.5px] text-rose-700 dark:text-rose-300">{detail._error}</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1 text-[11px] font-mono mb-2">
                <div><span className="text-muted-foreground">path: </span>{detail.path}</div>
                <div><span className="text-muted-foreground">method: </span>{detail.method}</div>
                <div><span className="text-muted-foreground">content_type: </span>{detail.content_type}</div>
                <div><span className="text-muted-foreground">parse_method: </span>{detail.parse_method}</div>
                <div><span className="text-muted-foreground">parse_status: </span>{detail.parse_status}</div>
                <div><span className="text-muted-foreground">auth_status: </span>{detail.auth_status}</div>
                <div><span className="text-muted-foreground">http: </span>{detail.http_status_returned}</div>
                <div><span className="text-muted-foreground">reason: </span>{detail.reason || "-"}</div>
                <div>
                  <span className="text-muted-foreground">processing_error: </span>
                  {detail.processing_error || "-"}
                </div>
                <div><span className="text-muted-foreground">body_size: </span>{detail.body_size}</div>
                <div className="truncate" title={detail.body_hash || ""}>
                  <span className="text-muted-foreground">body_hash: </span>
                  {(detail.body_hash || "").slice(0, 16)}{detail.body_hash ? "…" : "-"}
                </div>
                <div><span className="text-muted-foreground">source_ip: </span>{detail.source_ip || "-"}</div>
                <div className="col-span-2 md:col-span-3 truncate">
                  <span className="text-muted-foreground">user_agent: </span>{detail.user_agent || "-"}
                </div>
                <div className="col-span-2 md:col-span-3">
                  <span className="text-muted-foreground">signature_headers_present: </span>
                  {Array.isArray(detail.signature_headers_present) && detail.signature_headers_present.length > 0
                    ? detail.signature_headers_present.join(", ")
                    : "(none)"}
                </div>
                <div className="col-span-2 md:col-span-3">
                  <span className="text-muted-foreground">body_keys: </span>
                  {Array.isArray(detail.body_keys) && detail.body_keys.length > 0
                    ? detail.body_keys.join(", ")
                    : "(none)"}
                </div>
                <div><span className="text-muted-foreground">parsed_event_type: </span>{detail.parsed_event_type || "-"}</div>
                <div><span className="text-muted-foreground">parsed_product_id: </span>{detail.parsed_product_id || "-"}</div>
                <div><span className="text-muted-foreground">parsed_test_mode: </span>{String(!!detail.parsed_test_mode)}</div>
                <div className="truncate" title={detail.parsed_customer_email || ""}>
                  <span className="text-muted-foreground">parsed_customer_email: </span>
                  {detail.parsed_customer_email || "-"}
                </div>
                <div><span className="text-muted-foreground">parsed_order_id: </span>{detail.parsed_order_id || "-"}</div>
                <div><span className="text-muted-foreground">linked_event_id: </span>{detail.linked_event_id || "-"}</div>
              </div>
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mt-2 mb-1">
                Signature diagnostics (admin-only, secret never exposed)
              </div>
              {detail.signature_diag ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1 text-[11px] font-mono mb-2">
                  <div>
                    <span className="text-muted-foreground">header_present: </span>
                    {String(!!detail.signature_diag.signature_header_present)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">format_detected: </span>
                    {detail.signature_diag.signature_format_detected || "-"}
                  </div>
                  <div>
                    <span className="text-muted-foreground">generated_format: </span>
                    {detail.signature_diag.generated_signature_format || "-"}
                  </div>
                  <div>
                    <span className="text-muted-foreground">len_received: </span>
                    {detail.signature_diag.signature_length_received ?? "-"}
                  </div>
                  <div>
                    <span className="text-muted-foreground">len_gen_b64: </span>
                    {detail.signature_diag.signature_length_generated_b64 ?? "-"}
                  </div>
                  <div>
                    <span className="text-muted-foreground">len_gen_hex: </span>
                    {detail.signature_diag.signature_length_generated_hex ?? "-"}
                  </div>
                  <div className="truncate">
                    <span className="text-muted-foreground">received: </span>
                    {detail.signature_diag.signature_received_bookend || "-"}
                  </div>
                  <div className="truncate">
                    <span className="text-muted-foreground">generated_b64: </span>
                    {detail.signature_diag.signature_generated_b64_bookend || "-"}
                  </div>
                  <div className="truncate">
                    <span className="text-muted-foreground">generated_hex: </span>
                    {detail.signature_diag.signature_generated_hex_bookend || "-"}
                  </div>
                  {detail.signature_diag.matched_format ? (
                    <div className="col-span-2 md:col-span-3">
                      <span className="text-muted-foreground">matched_format: </span>
                      <span className="text-emerald-600 dark:text-emerald-400">
                        {detail.signature_diag.matched_format}
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="text-[11px] text-muted-foreground italic mb-2">(no signature diagnostics)</div>
              )}
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mt-2 mb-1">
                Body preview (redacted, max 2KB)
              </div>
              <pre
                className="text-[10.5px] font-mono overflow-x-auto max-h-[260px] whitespace-pre-wrap rounded bg-background/40 p-2"
                data-testid="webhook-attempt-body-preview"
              >
                {detail.body_preview || "(empty)"}
                {detail.body_preview_truncated ? "\n\n[note: preview truncated to 2KB — full body not retained]" : ""}
              </pre>
            </>
          )}
        </div>
      ) : null}

      {replayResult ? (
        <div
          className={`mt-2 rounded-md border p-2 text-[11.5px] ${
            replayResult.status === "ok"
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              : "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300"
          }`}
          data-testid="webhook-attempt-replay-result"
        >
          <div className="font-medium mb-1">Replay result</div>
          <pre className="text-[10.5px] font-mono overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(replayResult, null, 2)}
          </pre>
        </div>
      ) : null}
    </div>
  );
}




function AdminQAPlanSimulationCard() {
  const [ticker, setTicker] = useState("AAPL");
  const [plan, setPlan] = useState("free");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Phase 6.BI-13 — open the SIMULATED analysis in the REAL frontend
  // (a new tab) so the operator can verify the actual rendered UI
  // (locked sections, basic summary card, structured tabs, etc.) under
  // the chosen plan — not just the JSON payload. The Analyze page
  // reads `?simulate_plan=...` from the query string and forwards it
  // to the backend POST. Quota bypass is enforced server-side: the
  // simulate_plan param is STRICTLY ignored for non-admin callers, so
  // we can use a plain `window.open(...)` without worrying about
  // leaking a privileged bypass.
  const openSimulatedAnalysis = (planKey, tk = ticker) => {
    const tkUpper = (tk || "").toUpperCase().trim();
    if (!tkUpper) return;
    const url = `/analyze/${encodeURIComponent(tkUpper)}?simulate_plan=${encodeURIComponent(planKey)}`;
    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      // Pop-up blocked: fall back to same-tab navigation so the
      // operator at least sees something happen.
      window.location.href = url;
    }
  };

  const run = async () => {
    setLoading(true);
    setErr(null);
    setResult(null);
    try {
      const t0 = performance.now();
      const res = await api.post(
        `/analyze/${encodeURIComponent(ticker.toUpperCase())}`,
        {},
        { params: { simulate_plan: plan } },
      );
      const elapsed_ms = Math.round(performance.now() - t0);
      setResult({ data: res.data, elapsed_ms });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        setErr("Quota exhausted (admin bypass should prevent this — check is_admin flag).");
      } else {
        setErr(e?.response?.data?.detail || e?.message || "Request failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const summary = result?.data;
  return (
    <Card title="QA Plan Simulation" icon={Activity} accent="#10B981">
      <div className="text-xs text-muted-foreground mb-3 max-w-prose space-y-1">
        <p>
          Temporarily run an analyze request as if you were on Free / Pro / Premium <b>without</b> changing your real account plan and <b>without</b> being blocked by quota. Admin sessions never count toward usage_events or business KPIs.
        </p>
        <p>
          Use <b>“Open in normal frontend”</b> below to render the actual Analyze page under a simulated plan (locked sections, basic summary, structured tabs, etc.).
        </p>
      </div>

      {/* Phase 6.BI-13 — Quick launchers: one-click open AAPL in the
          real Analyze frontend under each plan tier in a NEW TAB. */}
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 mb-3">
        <div className="text-[11px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 font-medium mb-2">
          Open {ticker.toUpperCase() || "TICKER"} in normal frontend
        </div>
        <div className="flex flex-wrap gap-2">
          {["free", "pro", "premium"].map((p) => (
            <Button
              key={p}
              type="button"
              size="sm"
              variant="outline"
              onClick={() => openSimulatedAnalysis(p)}
              disabled={!ticker}
              data-testid={`qa-sim-open-${p}-btn`}
            >
              Open as {p[0].toUpperCase() + p.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      <div className="text-[11px] uppercase tracking-wider text-muted-foreground/80 font-medium mb-2">
        Or inspect raw payload here
      </div>
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <input
          type="text"
          value={ticker}
          onChange={(e) => setTicker(e.target.value)}
          placeholder="Ticker"
          className="px-3 py-1.5 text-sm rounded-md border border-border bg-background w-24 font-mono"
          data-testid="qa-sim-ticker-input"
        />
        <select
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-md border border-border bg-background"
          data-testid="qa-sim-plan-select"
        >
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="premium">Premium</option>
        </select>
        <Button
          type="button"
          size="sm"
          variant="default"
          onClick={run}
          disabled={loading || !ticker}
          data-testid="qa-sim-run-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Running…" : `Inspect as ${plan}`}
        </Button>
      </div>
      {err ? (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-[12px] text-rose-700 dark:text-rose-300 mb-3" data-testid="qa-sim-error">
          {err}
        </div>
      ) : null}
      {summary ? (
        <div className="space-y-2 text-[12px]" data-testid="qa-sim-result">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span><b>simulate_plan_active:</b> <code>{String(summary.admin_simulated_plan)}</code></span>
            <span><b>simulated_plan:</b> <code>{summary.user_plan || "—"}</code></span>
            <span><b>real_account_plan:</b> <code>{summary.real_account_plan || "—"}</code></span>
            <span><b>admin_qa_bypass:</b> <code>{String(summary.admin_qa_bypass)}</code></span>
            <span><b>usage_counted:</b> <code>{String(summary.usage_counted)}</code></span>
            <span><b>business_kpi_counted:</b> <code>{String(summary.business_kpi_counted)}</code></span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span><b>elapsed:</b> <code>{result.elapsed_ms}ms</code></span>
            <span><b>cache_hit:</b> <code>{String(summary.cache_hit)}</code></span>
            <span><b>ai_mode:</b> <code>{summary.ai_mode || "—"}</code></span>
            <span><b>ai_status_final:</b> <code>{summary.ai_status_final || "—"}</code></span>
            <span><b>ai_model_used:</b> <code>{summary.ai_model_used || "—"}</code></span>
            <span><b>ai_duration_ms:</b> <code>{summary.ai_duration_ms ?? "—"}</code></span>
            <span><b>full_ai_generated:</b> <code>{String(summary.full_ai_generated)}</code></span>
            <span><b>locked_sections_generated:</b> <code>{String(summary.locked_sections_generated)}</code></span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span><b>current_price:</b> <code>{summary.canonical_valuation?.current_price ?? "—"}</code></span>
            <span><b>fair_value:</b> <code>{summary.canonical_valuation?.fair_value ?? "—"}</code></span>
            <span><b>ai_score:</b> <code>{summary.valuation?.ai_score ?? "—"}</code></span>
            <span><b>status:</b> <code>{summary.valuation?.status ?? "—"}</code></span>
            <span><b>upside_pct:</b> <code>{summary.valuation?.upside_pct ?? "—"}</code></span>
          </div>
          {/* Phase 6.BI-13 — KPI derivations + provenance snapshot. */}
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span>
              <b>fcf_yield:</b>{" "}
              <code>
                {summary.valuation?.diagnostics?.fcf_quality?.fcf_yield != null
                  ? `${(summary.valuation.diagnostics.fcf_quality.fcf_yield * 100).toFixed(2)}%`
                  : "—"}
              </code>
            </span>
            <span>
              <b>fcf_to_net_income:</b>{" "}
              <code>
                {summary.valuation?.diagnostics?.fcf_quality?.fcf_to_net_income != null
                  ? summary.valuation.diagnostics.fcf_quality.fcf_to_net_income.toFixed(2)
                  : "—"}
              </code>
            </span>
            <span>
              <b>debt_to_equity:</b>{" "}
              <code>
                {summary.valuation?.diagnostics?.balance_sheet?.debt_to_equity != null
                  ? summary.valuation.diagnostics.balance_sheet.debt_to_equity.toFixed(2)
                  : "—"}
              </code>
            </span>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => openSimulatedAnalysis(plan)}
              data-testid="qa-sim-open-frontend-btn"
            >
              Open this in normal frontend ↗
            </Button>
          </div>
          <details className="mt-2">
            <summary className="cursor-pointer text-muted-foreground">AI summary preview…</summary>
            <pre className="mt-1 text-[11px] whitespace-pre-wrap text-foreground/80 bg-muted/40 p-2 rounded">
              {(summary.ai?.summary || "—").substring(0, 600)}
            </pre>
          </details>
        </div>
      ) : (!err && !loading ? (
        <div className="text-[12px] text-muted-foreground" data-testid="qa-sim-empty">
          Click an "Open as …" button to render the real frontend under a simulated plan, or "Inspect as {plan}" to see the raw JSON payload.
        </div>
      ) : null)}
    </Card>
  );
}


/**
 * RuntimeStatusCard — Phase 6.AA production diagnostics.
 *
 * Shows the LIVE state of the FastAPI process serving this request:
 *   - build identity (timestamp, git SHA, hostname, pid, uptime)
 *   - effective env flags actually seen by THIS process
 *   - provider selection + FMP enable state
 *   - last analyze request observed by THIS replica
 *   - Mongo connectivity
 *   - cache sizes (which can be cleared from the Cache Clear card below)
 */
function RuntimeStatusCard({ runtime, loading, onRefresh }) {
  const r = runtime || {};
  const build = r.build || {};
  const env = r.env || {};
  const fmp = r.fmp || {};
  const llm = r.llm || {};
  const mongo = r.mongo || {};
  const lastReq = r.last_request || {};
  const caches = r.cache_sizes || {};
  const dh = build.deployment_hints || {};
  // Phase 6.AD — derive a strong "is this preview or production?" hint
  // from the runtime payload itself. We flag mismatches loudly so the
  // operator catches misconfigured deploys instantly.
  const isProdHint = (
    String(dh.deployment_env || "").toLowerCase() === "production" ||
    String(dh.environment || "").toLowerCase() === "production" ||
    String(dh.emergent_deployment || "").toLowerCase() === "production" ||
    String(dh.app_env || "").toLowerCase() === "production" ||
    String(dh.node_env || "").toLowerCase() === "production" ||
    String(build.build_version || "").toLowerCase().includes("prod")
  );
  const isPreviewHint = (
    String(build.build_version || "").toLowerCase().includes("preview") ||
    String(dh.deployment_env || "").toLowerCase() === "preview" ||
    String(dh.environment || "").toLowerCase() === "preview"
  );
  const envBadgeColor = isProdHint
    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-700"
    : isPreviewHint
      ? "bg-amber-500/15 border-amber-500/40 text-amber-700"
      : "bg-slate-500/15 border-slate-500/40 text-slate-700";
  const envBadgeLabel = isProdHint ? "production" : isPreviewHint ? "preview" : "unknown";

  return (
    <Card title="Runtime Status" icon={Activity} accent="#7C3AED">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="text-[11.5px] text-muted-foreground">
          What THIS backend process is actually running. Use this card to verify the deploy picked up the latest code, see effective env flags, and confirm requests are reaching this replica.
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onRefresh}
          disabled={loading}
          data-testid="runtime-status-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Phase 6.AD — Deployment environment badge. Lets the operator
          instantly see "preview vs prod vs unknown" without parsing env
          values. Backed by build.deployment_hints / build.build_version. */}
      {runtime ? (
        <div className={`inline-flex items-center gap-2 mb-3 px-2.5 py-1 rounded-full border text-[11px] font-semibold uppercase tracking-wide ${envBadgeColor}`} data-testid="runtime-env-badge">
          <span className="w-1.5 h-1.5 rounded-full bg-current" />
          Environment: {envBadgeLabel}
          {build.hostname ? <span className="ml-1 normal-case text-[10px] opacity-80">· host {build.hostname}</span> : null}
          {dh.db_name && dh.db_name !== "<unset>" ? <span className="ml-1 normal-case text-[10px] opacity-80">· db {dh.db_name}</span> : null}
        </div>
      ) : null}

      {!runtime && loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading runtime status…
        </div>
      ) : null}

      {!runtime ? null : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="runtime-status-grid">
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Build & process</div>
            <StatRow label="Version"         testId="runtime-build-version"   value={<code className="text-[11px]">{build.build_version || "—"}</code>} />
            <StatRow label="Phase"           testId="runtime-build-phase"     value={<code className="text-[11px]" data-testid="runtime-build-phase-value">{build.build_phase || "—"}</code>} />
            <StatRow label="Build timestamp" testId="runtime-build-ts"        value={<code className="text-[11px]">{build.build_timestamp || "—"}</code>} />
            <StatRow label="Git SHA"         testId="runtime-build-sha"       value={<code className="text-[11px]">{build.build_git_sha || "—"}</code>} />
            <StatRow label="Hostname"        testId="runtime-hostname"        value={<code className="text-[11px]">{build.hostname || "—"}</code>} />
            <StatRow label="PID"             testId="runtime-pid"             value={<code className="text-[11px]">{build.process_pid ?? "—"}</code>} />
            <StatRow label="Uptime"          testId="runtime-uptime"          value={<span className="text-xs">{build.uptime_seconds != null ? `${build.uptime_seconds}s` : "—"}</span>} />
            {/* Deployment hints — derived from env vars commonly set by
                hosting platforms. Helps confirm preview vs production. */}
            <div className="pt-2 mt-2 border-t border-border/30">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Deployment hints</div>
              <StatRow label="DB_NAME"            testId="runtime-dh-db"          value={<code className="text-[10px]">{dh.db_name || "—"}</code>} />
              <StatRow label="NODE_ENV"           testId="runtime-dh-node"        value={<code className="text-[10px]">{dh.node_env || "—"}</code>} />
              <StatRow label="DEPLOYMENT_ENV"     testId="runtime-dh-dep"         value={<code className="text-[10px]">{dh.deployment_env || "—"}</code>} />
              <StatRow label="EMERGENT_DEPLOY"    testId="runtime-dh-emergent"    value={<code className="text-[10px]">{dh.emergent_deployment || "—"}</code>} />
              <StatRow label="ENVIRONMENT"        testId="runtime-dh-env"         value={<code className="text-[10px]">{dh.environment || "—"}</code>} />
              <StatRow label="APP_ENV"            testId="runtime-dh-app"         value={<code className="text-[10px]">{dh.app_env || "—"}</code>} />
              <StatRow label="public backend url" testId="runtime-dh-public-url"  value={<code className="text-[10px] break-all">{dh.public_backend_url_hint || "—"}</code>} />
              <StatRow label="CORS_ORIGINS"       testId="runtime-dh-cors"        value={<code className="text-[10px] break-all">{dh.cors_origins || "—"}</code>} />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Effective env & provider</div>
            <StatRow
              label="Provider"
              testId="runtime-provider"
              value={
                <span className={`text-xs font-mono ${fmp.provider_selected === "fmp" ? "text-emerald-600" : "text-amber-600"}`}>
                  {fmp.provider_selected || "—"}
                </span>
              }
            />
            <StatRow
              label="FMP enabled"
              testId="runtime-fmp-enabled"
              value={<span className={`text-xs ${fmp.is_enabled_effective ? "text-emerald-600" : "text-amber-600"}`}>{String(!!fmp.is_enabled_effective)}</span>}
            />
            <StatRow label="ENABLE_FMP env"  testId="runtime-enable-fmp"     value={<code className="text-[11px]">{env.ENABLE_FMP_raw || "—"}</code>} />
            <StatRow label="FMP_API_KEY"     testId="runtime-fmp-key"        value={<span className="text-xs">{env.FMP_API_KEY_present ? `set (${env.FMP_API_KEY_length} chars)` : <span className="text-rose-600">missing</span>}</span>} />
            <StatRow label="FMP_BASE_URL"    testId="runtime-fmp-baseurl"    value={<code className="text-[11px] break-all">{env.FMP_BASE_URL || "—"}</code>} />
            <StatRow label="LLM key"         testId="runtime-llm-key"        value={<span className="text-xs">{llm.key_present ? <code className="text-[11px]">{llm.key_source}</code> : <span className="text-rose-600">missing</span>}</span>} />
            <StatRow label="Mongo"           testId="runtime-mongo"          value={<span className={`text-xs ${mongo.ok ? "text-emerald-600" : "text-rose-600"}`}>{mongo.ok ? `OK (${mongo.db_name})` : `error: ${mongo.error || "unknown"}`}</span>} />
            {/* Phase 6.AI — explicit "LIVE DATA ONLY mode" indicator. True
                whenever an FMP key is configured and the mock fallback
                is disabled, which is the required production posture. */}
            <StatRow
              label="LIVE DATA ONLY"
              testId="runtime-live-data-only"
              value={
                <span
                  className={`text-xs font-mono px-2 py-0.5 rounded ${fmp.live_data_only_mode ? "bg-emerald-500/10 text-emerald-600 border border-emerald-500/30" : "bg-amber-500/10 text-amber-600 border border-amber-500/30"}`}
                  data-testid="runtime-live-data-only-value"
                >
                  {fmp.live_data_only_mode ? "ACTIVE — mock fallback BLOCKED" : "INACTIVE — mock fallback allowed"}
                </span>
              }
            />
            <StatRow
              label="ALLOW_MOCK_FALLBACK env"
              testId="runtime-allow-mock"
              value={<code className="text-[11px]">{fmp.allow_mock_fallback_env || "—"}</code>}
            />
          </div>

          <div className="space-y-2 md:col-span-2 pt-4 border-t border-border/40">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Last analyze request (this replica only)</div>
              {/* Phase 6.AM — Stale chip. If last_request is older than
                  5 minutes, the operator must not interpret it as the
                  current MU test. */}
              {lastReq.is_stale || r.last_request_stale ? (
                <span
                  className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-700 border border-amber-500/30"
                  data-testid="runtime-last-request-stale"
                  title={`Age: ${r.last_request_age_seconds ?? lastReq.age_seconds ?? "?"}s — not the current test.`}
                >
                  STALE · {Math.round((r.last_request_age_seconds ?? lastReq.age_seconds ?? 0) / 60)}m old
                </span>
              ) : null}
            </div>
            {!lastReq.ticker ? (
              <div className="text-xs text-muted-foreground" data-testid="runtime-last-request-empty">
                No analyze request observed on this replica yet. Run an analyze (e.g. AAPL) and click "Refresh all" to confirm it lands here.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="runtime-last-request">
                <Stat label="Ticker"       value={<code>{lastReq.ticker}</code>}                                                                           testId="runtime-last-ticker" />
                <Stat label="Plan"         value={lastReq.plan || "—"}                                                                                       testId="runtime-last-plan" />
                <Stat label="Cache hit"    value={String(!!lastReq.cache_hit)}                                                                               testId="runtime-last-cache-hit" />
                <Stat label="Data source"  value={<code className="text-[11px]">{lastReq.data_source || "—"}</code>}                                         testId="runtime-last-source" />
                <Stat label="Live status"  value={<code className="text-[11px]">{lastReq.source_reason || "—"}</code>}                                       testId="runtime-last-source-reason" />
                <Stat label="AI status"    value={<code className="text-[11px]">{lastReq.ai_status || "—"}</code>}                                           testId="runtime-last-ai" />
                <Stat label="Duration"     value={lastReq.duration_s != null ? `${lastReq.duration_s.toFixed(1)}s` : "—"}                                    testId="runtime-last-duration" />
                <Stat label="Stage"        value={<code className="text-[11px]">{lastReq.stage_at_end || "—"}</code>}                                        testId="runtime-last-stage" />
                {lastReq.user_email ? (
                  <Stat label="User"       value={<code className="text-[11px] break-all">{lastReq.user_email}</code>}                                       testId="runtime-last-user" />
                ) : null}
                {lastReq.force_live ? (
                  <Stat label="Forced live" value={<span className="text-xs text-violet-600">yes</span>}                                                      testId="runtime-last-force-live" />
                ) : null}
              </div>
            )}
          </div>

          <div className="space-y-2 md:col-span-2 pt-4 border-t border-border/40">
            <CacheClearActions caches={caches} onChanged={onRefresh} />
          </div>

          {/* Phase 6.AM — Frontend Navigation / Error Trace.
              The chronological event chain in ONE panel. Shows EVERY
              navigation, search submission, route entry, fetch, response,
              render, and error-screen render in time order. The killer
              panel when other instrumentation panels say "no data yet" —
              this one captures ANY frontend lifecycle event, so if even
              ONE row appears here for MU, the operator knows the user
              action did reach the frontend. If NOTHING appears here, the
              issue is upstream of the lifecycle emitter (CSP, blocked
              domain, service worker hijack, etc.).
          */}
          <div className="space-y-2 md:col-span-2 pt-4 border-t-2 border-sky-500/40" data-testid="runtime-navigation-trace">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[11px] uppercase tracking-wide text-sky-700 font-semibold flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-sky-500" />
                Frontend Navigation / Error Trace
                <span className="text-muted-foreground/70 normal-case font-normal">
                  (chronological — last 30 minutes — browser traffic only)
                </span>
              </div>
              <div className="text-[10px] text-muted-foreground">
                {(r.navigation_trace || []).length} events
              </div>
            </div>
            {!(r.navigation_trace || []).length ? (
              <div className="text-xs text-muted-foreground" data-testid="runtime-navigation-trace-empty">
                No frontend events captured in the last 30 minutes. If you tested an analyze and this is empty, the lifecycle emitter is failing to reach the backend (likely CSP / CORS / blocked domain / service worker).
              </div>
            ) : (
              <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 overflow-hidden">
                <div className="max-h-[420px] overflow-auto">
                  <table className="w-full text-[11px]">
                    <thead className="sticky top-0 bg-sky-500/15 backdrop-blur">
                      <tr className="text-left text-muted-foreground">
                        <th className="px-2 py-1.5 font-medium">time</th>
                        <th className="px-2 py-1.5 font-medium">event</th>
                        <th className="px-2 py-1.5 font-medium">ticker</th>
                        <th className="px-2 py-1.5 font-medium">details</th>
                        <th className="px-2 py-1.5 font-medium">user / trace</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(r.navigation_trace || []).map((row, i) => {
                        const isError = (row.event || "").includes("error") || (row.event || "").includes("failed") || (row.event || "").includes("timeout") || (row.event || "").includes("rejected") || (row.event || "").includes("blocked");
                        const isSuccess = ["render_completed", "response_accepted", "response_received"].includes(row.event);
                        const isNav = ["route_changed", "route_entry", "home_search_submitted", "search_submit", "page_loaded"].includes(row.event);
                        const rowClass = isError ? "bg-rose-500/10" : isSuccess ? "bg-emerald-500/5" : isNav ? "bg-sky-500/5" : "";
                        return (
                          <tr
                            key={`${row.received_at}-${i}`}
                            className={`border-t border-sky-500/10 ${rowClass}`}
                            data-testid={`runtime-navigation-trace-row-${i}`}
                          >
                            <td className="px-2 py-1.5 font-mono text-[10px] text-muted-foreground whitespace-nowrap">
                              {(row.received_at || "").slice(11, 19)}
                            </td>
                            <td className="px-2 py-1.5 font-mono text-[10px] whitespace-nowrap">
                              <span className={isError ? "text-rose-700 font-semibold" : isSuccess ? "text-emerald-700" : isNav ? "text-sky-700" : ""} data-testid={`runtime-navigation-trace-event-${i}`}>
                                {row.event}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 font-mono text-[10px]">
                              {row.ticker || "—"}
                            </td>
                            <td className="px-2 py-1.5 text-[10px]">
                              {row.event === "home_search_submitted" || row.event === "search_submit" ? (
                                <span>
                                  typed=<code>{row.typed_raw || "—"}</code> → target=<code>{row.target || "—"}</code>
                                  {row.source ? <span className="text-muted-foreground"> ({row.source})</span> : null}
                                </span>
                              ) : row.event === "route_changed" ? (
                                <span>
                                  <code>{row.from_path || "—"}</code> → <code>{row.to_path || "—"}</code>
                                </span>
                              ) : row.event === "route_entry" || row.event === "route_entry_blocked" ? (
                                <span>
                                  pathname=<code>{row.pathname || "—"}</code>
                                  {row.reason ? <span className="text-amber-700"> reason={row.reason}</span> : null}
                                </span>
                              ) : row.event === "render_failed" || row.event === "error_screen_rendered" ? (
                                <span className="text-rose-700">
                                  {row.component ? <span>component=<code>{row.component}</code> </span> : null}
                                  {row.error_name ? <span>err=<code>{row.error_name}</code>: </span> : null}
                                  {row.error_message ? <span className="break-all">{String(row.error_message).slice(0, 120)}</span> : null}
                                  {row.reason ? <span> reason=<code>{row.reason}</code></span> : null}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">
                                  {row.pathname || row.url || "—"}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-[10px] text-muted-foreground">
                              {row.user_email ? <code className="block truncate max-w-[140px]" title={row.user_email}>{row.user_email}</code> : null}
                              {row.client_trace_id ? <code className="block text-violet-700">{row.client_trace_id}</code> : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Phase 6.AJ — Ticker Provenance Trace.
              The killer panel for the "I typed MU but the Owner Console
              shows AAPL" P0 bug. Each row is a single user analyze action
              with the FULL chain of ticker sources joined together:
                typed -> route_param -> normalized -> url_path
                       -> final_api_url -> audit_path -> audit_ticker
                       -> runtime_ticker -> backend_returned_ticker
              Any disagreement between any two values flips the row to RED
              and lists the specific mismatches. If everything agrees the
              row is green. Empty = no real browser traffic yet.
          */}
          <div className="space-y-2 md:col-span-2 pt-4 border-t-2 border-violet-500/40" data-testid="runtime-ticker-provenance">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[11px] uppercase tracking-wide text-violet-700 font-semibold flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-violet-500" />
                Ticker provenance trace
                <span className="text-muted-foreground/70 normal-case font-normal">
                  (route_entry → typed → route → normalized → url → audit → runtime → backend)
                </span>
              </div>
              <div className="text-[10px] text-muted-foreground">
                last 15 fetch_started events · last 1h · browser traffic only
              </div>
            </div>
            {!(r.ticker_provenance_trace || []).length ? (
              <div className="text-xs text-muted-foreground" data-testid="runtime-ticker-provenance-empty">
                No analyze attempts captured yet. Open an Analyze page (e.g. /analyze/MU) and the chain will appear here.
              </div>
            ) : (
              <div className="space-y-2">
                {(r.ticker_provenance_trace || []).map((row, i) => {
                  const ch = row.chain || {};
                  const cell = (k, v) => {
                    if (v == null || v === "") return <code className="text-[10px] text-muted-foreground">—</code>;
                    return <code className="text-[10px]">{String(v)}</code>;
                  };
                  return (
                    <div
                      key={row.request_id || i}
                      className={`rounded-lg border p-3 ${row.has_mismatch ? "border-rose-500/50 bg-rose-500/10" : "border-emerald-500/30 bg-emerald-500/5"}`}
                      data-testid={`runtime-ticker-provenance-row-${i}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap text-[11px] mb-2">
                        <span className="text-muted-foreground">{(row.received_at || "").slice(11, 19)} UTC</span>
                        {row.has_mismatch ? (
                          <span className="px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-700 font-semibold text-[10px]" data-testid={`runtime-ticker-provenance-mismatch-${i}`}>
                            MISMATCH
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700 font-semibold text-[10px]" data-testid={`runtime-ticker-provenance-ok-${i}`}>
                            CONSISTENT
                          </span>
                        )}
                        <code className="text-amber-700 text-[10px]">req={row.request_id || "—"}</code>
                        {row.client_trace_id ? (
                          <code className="text-violet-700 text-[10px]" data-testid={`runtime-ticker-trace-id-${i}`}>trace={row.client_trace_id}</code>
                        ) : null}
                        {row.event_anchor ? (
                          <code className="text-blue-700 text-[10px]">anchor={row.event_anchor}</code>
                        ) : null}
                        {row.user_email ? (
                          <code className="text-[10px] text-muted-foreground truncate max-w-[180px]" title={row.user_email}>{row.user_email}</code>
                        ) : null}
                        <span className="ml-auto inline-flex items-center gap-2 text-[10px] text-muted-foreground flex-wrap">
                          <span data-testid={`runtime-ticker-fetch-started-${i}`}>
                            fetch_started={
                              row.had_fetch_started
                                ? <span className="text-emerald-700 font-semibold">yes</span>
                                : <span className="text-rose-700 font-semibold">NO</span>
                            }
                          </span>
                          {row.blocked_reason ? (
                            <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 font-semibold" data-testid={`runtime-ticker-blocked-${i}`}>
                              blocked: {row.blocked_reason}
                            </span>
                          ) : null}
                          <span>backend={row.has_audit ? "yes" : "NO AUDIT"}</span>
                          {row.audit_status != null ? (
                            <code className={
                              row.audit_status >= 500 ? "text-rose-600" :
                              row.audit_status >= 400 ? "text-amber-600" :
                              "text-emerald-600"
                            }>http={row.audit_status}</code>
                          ) : null}
                          {row.audit_duration_ms != null ? <span>{row.audit_duration_ms}ms</span> : null}
                          <span>frontend_response={row.got_response ? "yes" : "no"}</span>
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 text-[11px]" data-testid={`runtime-ticker-provenance-chain-${i}`}>
                        <div>
                          <span className="text-muted-foreground">typed:</span>{" "}
                          <span data-testid={`runtime-ticker-typed-${i}`}>{cell("typed", ch.typed_ticker)}</span>
                          {ch.typed_source ? (
                            <span className="text-[9px] text-muted-foreground ml-1">({ch.typed_source})</span>
                          ) : null}
                        </div>
                        <div>
                          <span className="text-muted-foreground">route_param:</span>{" "}
                          <span data-testid={`runtime-ticker-route-${i}`}>{cell("route", ch.route_param_ticker)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">normalized:</span>{" "}
                          <span data-testid={`runtime-ticker-normalized-${i}`}>{cell("norm", ch.normalized_ticker)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">url_path:</span>{" "}
                          <span data-testid={`runtime-ticker-urlpath-${i}`}>{cell("urlpath", ch.url_path_ticker)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">audit_path:</span>{" "}
                          <span data-testid={`runtime-ticker-audit-path-${i}`}>{cell("ap", ch.audit_path_ticker)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">audit_ticker:</span>{" "}
                          <span data-testid={`runtime-ticker-audit-ticker-${i}`}>{cell("at", ch.audit_ticker)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">runtime:</span>{" "}
                          <span data-testid={`runtime-ticker-runtime-${i}`}>{cell("rt", ch.runtime_ticker)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">backend_returned:</span>{" "}
                          <span data-testid={`runtime-ticker-backend-returned-${i}`}>{cell("br", ch.backend_returned_ticker)}</span>
                        </div>
                      </div>
                      {ch.final_api_url ? (
                        <div className="mt-2 text-[10px] text-muted-foreground truncate" title={ch.final_api_url}>
                          final_api_url: <code className="text-[10px]">{ch.final_api_url}</code>
                        </div>
                      ) : null}
                      {row.has_mismatch ? (
                        <div className="mt-2 text-[11px] text-rose-700 font-mono" data-testid={`runtime-ticker-provenance-mismatch-list-${i}`}>
                          {(row.mismatches || []).map((m, j) => (
                            <div key={j}>⚠ {m}</div>
                          ))}
                        </div>
                      ) : null}
                      {row.response_analysis_status ? (
                        <div className="mt-1 text-[10px] text-muted-foreground">
                          analysis_status: <code>{row.response_analysis_status}</code>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Phase 6.AL — Last Analyze Response Debug.
              For each recent response_received, joins every downstream
              lifecycle event (response_body_parsed, response_schema_validated,
              response_accepted/rejected, render_started, render_completed,
              render_failed, error_state_set) so the operator can answer
              the killer question: "the backend returned 200 OK — why did
              the user see 'Something went wrong'?" in ONE panel without
              joining Mongo collections by hand.
          */}
          <div className="space-y-2 md:col-span-2 pt-4 border-t-2 border-violet-500/40" data-testid="runtime-analyze-response-debug">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[11px] uppercase tracking-wide text-violet-700 font-semibold flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-violet-500" />
                Last Analyze Response Debug
                <span className="text-muted-foreground/70 normal-case font-normal">
                  (response_received → body_parsed → schema_validated → accepted/rejected → render_*)
                </span>
              </div>
              <div className="text-[10px] text-muted-foreground">
                last 10 attempts · last 1h · browser traffic only
              </div>
            </div>
            {!(r.analyze_response_debug || []).length ? (
              <div className="text-xs text-muted-foreground" data-testid="runtime-analyze-response-debug-empty">
                No analyze responses captured yet. Trigger an analyze (e.g. /analyze/MU) and refresh.
              </div>
            ) : (
              <div className="space-y-2">
                {(r.analyze_response_debug || []).map((row, i) => {
                  const decision = row.decision || "unknown";
                  const isOk = decision === "rendered_ok";
                  const isCrash = decision === "render_failed";
                  const cardClass = isOk
                    ? "border-emerald-500/30 bg-emerald-500/5"
                    : isCrash
                      ? "border-rose-500/50 bg-rose-500/10"
                      : "border-amber-500/40 bg-amber-500/5";
                  return (
                    <div
                      key={row.request_id || i}
                      className={`rounded-lg border p-3 ${cardClass}`}
                      data-testid={`runtime-analyze-response-row-${i}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap text-[11px] mb-2">
                        <span className="text-muted-foreground">{(row.received_at || "").slice(11, 19)} UTC</span>
                        <span className={`px-1.5 py-0.5 rounded font-semibold text-[10px] ${
                          isOk ? "bg-emerald-500/20 text-emerald-700" :
                          isCrash ? "bg-rose-500/20 text-rose-700" :
                          "bg-amber-500/20 text-amber-700"
                        }`} data-testid={`runtime-analyze-response-decision-${i}`}>
                          {decision.toUpperCase().replace(/_/g, " ")}
                        </span>
                        <code className="text-[10px]">{row.ticker || "—"}</code>
                        {row.request_id ? (
                          <code className="text-amber-700 text-[10px]">req={row.request_id}</code>
                        ) : null}
                        {row.client_trace_id ? (
                          <code className="text-violet-700 text-[10px]">trace={row.client_trace_id}</code>
                        ) : null}
                        {row.status != null ? (
                          <code className={`text-[10px] ${
                            row.status >= 500 ? "text-rose-600" :
                            row.status >= 400 ? "text-amber-600" :
                            "text-emerald-600"
                          }`}>http={row.status}</code>
                        ) : null}
                        {row.user_email ? (
                          <code className="text-[10px] text-muted-foreground truncate max-w-[160px]" title={row.user_email}>{row.user_email}</code>
                        ) : null}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 text-[11px] mb-2">
                        <div>
                          <span className="text-muted-foreground">backend_returned:</span>{" "}
                          <code className="text-[10px]">{row.backend_returned_ticker || "—"}</code>
                        </div>
                        <div>
                          <span className="text-muted-foreground">analysis_status:</span>{" "}
                          <code className="text-[10px]">{row.analysis_status || "—"}</code>
                        </div>
                        <div>
                          <span className="text-muted-foreground">data_source:</span>{" "}
                          <code className="text-[10px]">{row.data_source || "—"}</code>
                        </div>
                        <div>
                          <span className="text-muted-foreground">schema_valid:</span>{" "}
                          <code className={`text-[10px] ${row.schema_valid === false ? "text-rose-700 font-semibold" : row.schema_valid === true ? "text-emerald-700" : ""}`}>
                            {row.schema_valid == null ? "—" : String(row.schema_valid)}
                          </code>
                        </div>
                      </div>
                      {Array.isArray(row.missing_required) && row.missing_required.length ? (
                        <div className="text-[11px] text-rose-700 mb-1" data-testid={`runtime-analyze-response-missing-${i}`}>
                          <span className="text-muted-foreground">missing_required:</span>{" "}
                          <code className="text-[10px]">{row.missing_required.join(", ")}</code>
                        </div>
                      ) : null}
                      {row.render_failed_detail ? (
                        <div className="text-[11px] mt-2 p-2 rounded bg-rose-500/15 border border-rose-500/30" data-testid={`runtime-analyze-response-render-failed-${i}`}>
                          <div className="text-rose-700 font-semibold mb-1">render_failed:</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-0.5 text-[10px]">
                            <div><span className="text-muted-foreground">component:</span> <code>{row.render_failed_detail.component || "—"}</code></div>
                            <div><span className="text-muted-foreground">boundary:</span> <code>{row.render_failed_detail.boundary || "—"}</code></div>
                            <div><span className="text-muted-foreground">error_name:</span> <code>{row.render_failed_detail.error_name || "—"}</code></div>
                            <div className="md:col-span-2"><span className="text-muted-foreground">error_message:</span> <code className="text-rose-800">{row.render_failed_detail.error_message || "—"}</code></div>
                          </div>
                        </div>
                      ) : null}
                      {row.error_state_reason ? (
                        <div className="text-[11px] mt-1">
                          <span className="text-muted-foreground">error_state_reason:</span>{" "}
                          <code className="text-[10px] text-amber-700">{row.error_state_reason}</code>
                        </div>
                      ) : null}
                      {Array.isArray(row.event_sequence) && row.event_sequence.length ? (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-[10px] text-muted-foreground hover:text-foreground">
                            event_sequence ({row.event_sequence.length} events)
                          </summary>
                          <pre className="mt-1 whitespace-pre-wrap break-all bg-black/40 text-amber-100 p-2 rounded text-[10px] max-h-32 overflow-auto" data-testid={`runtime-analyze-response-sequence-${i}`}>
                            {row.event_sequence.join(" → ")}
                          </pre>
                        </details>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-2 md:col-span-2 pt-4 border-t border-border/40" data-testid="runtime-http-audit">
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Recent analyze HTTP requests <span className="text-muted-foreground/70 normal-case">(real browser traffic only — curl/python/postman hidden)</span>
              </div>
              <div className="text-[10px] text-muted-foreground" data-testid="runtime-http-audit-counts">
                last 20 · 24h TTL{(r.test_traffic_count_last_hour ?? 0) > 0 ? ` · ${r.test_traffic_count_last_hour} test requests filtered (last 1h)` : ""}
              </div>
            </div>
            {!(r.recent_http_requests || []).length ? (
              <div className="text-xs text-muted-foreground" data-testid="runtime-http-audit-empty">
                No HTTP analyze traffic recorded yet on any replica.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border/50">
                <table className="w-full text-[11px]" data-testid="runtime-http-audit-table">
                  <thead className="bg-muted/40">
                    <tr className="text-left text-muted-foreground">
                      <th className="px-2 py-1.5 font-medium">Time (UTC)</th>
                      <th className="px-2 py-1.5 font-medium">Ticker</th>
                      <th className="px-2 py-1.5 font-medium">User</th>
                      <th className="px-2 py-1.5 font-medium">Auth</th>
                      <th className="px-2 py-1.5 font-medium">Status</th>
                      <th className="px-2 py-1.5 font-medium">ms</th>
                      <th className="px-2 py-1.5 font-medium">Outcome</th>
                      <th className="px-2 py-1.5 font-medium">request_id</th>
                      <th className="px-2 py-1.5 font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(r.recent_http_requests || []).slice(0, 20).map((row, i) => {
                      const tickerFromPath = (row.path || "").replace("/api/analyze/", "");
                      const ticker = row.ticker || tickerFromPath || "—";
                      const user = row.user_email || row.user_id || "—";
                      const errBody = row.error_body;
                      const errCode = errBody?.code || errBody?.stage || row.error_class || "";
                      const authDiag = row.auth_diag;
                      const is401 = row.status_code === 401;
                      const diagOutcome = authDiag?.outcome;
                      return (
                        <React.Fragment key={row.request_id || i}>
                        <tr className="border-t border-border/40" data-testid={`runtime-http-row-${i}`}>
                          <td className="px-2 py-1 font-mono text-muted-foreground" data-testid={`runtime-http-time-${i}`}>{(row.received_at || "").slice(11, 19)}</td>
                          <td className="px-2 py-1 font-mono" data-testid={`runtime-http-ticker-${i}`}>{ticker}</td>
                          <td className="px-2 py-1 max-w-[140px] truncate" title={user}>{user}</td>
                          <td className="px-2 py-1">
                            {row.has_auth_header ? <span className="text-emerald-600">bearer</span> :
                             row.has_session_cookie ? <span className="text-blue-600">cookie</span> :
                             <span className="text-rose-600">none</span>}
                          </td>
                          <td className="px-2 py-1 font-mono" data-testid={`runtime-http-status-${i}`}>
                            <span className={
                              row.status_code == null ? "text-rose-600" :
                              row.status_code >= 500 ? "text-rose-600" :
                              row.status_code >= 400 ? "text-amber-600" : "text-emerald-600"
                            }>
                              {row.status_code ?? "—"}
                            </span>
                          </td>
                          <td className="px-2 py-1">{row.duration_ms != null ? row.duration_ms : "—"}</td>
                          <td className="px-2 py-1">
                            <code className="text-[10px]">{row.outcome || row.phase || "—"}</code>
                          </td>
                          <td className="px-2 py-1" data-testid={`runtime-http-reqid-${i}`}>
                            <code className="text-[10px] text-amber-700">{row.request_id || "—"}</code>
                          </td>
                          <td className="px-2 py-1 text-rose-600 max-w-[260px] truncate" title={JSON.stringify(errBody || row.error_message || "")}>
                            {errCode ? <code className="text-[10px]">{errCode}</code> : ""}
                          </td>
                        </tr>
                        {/* Phase 6.AR — Inline auth diagnostic row for 401s.
                            Shows the EXACT step where authentication failed
                            so the operator can immediately see whether it's
                            a token expiry vs the production-frontend-hits-
                            preview-backend (jwt_decoded_but_user_not_in_db)
                            class of bug. */}
                        {is401 && authDiag ? (
                          <tr className="bg-rose-500/10" data-testid={`runtime-http-auth-diag-${i}`}>
                            <td colSpan={9} className="px-2 py-1.5 text-[10px]">
                              <span className="text-rose-700 font-semibold">auth_diag:</span>{" "}
                              <code className={
                                diagOutcome === "jwt_decoded_but_user_not_in_db" ? "text-rose-700 font-semibold" :
                                diagOutcome?.startsWith("ok_") ? "text-emerald-700" :
                                "text-amber-700"
                              } data-testid={`runtime-http-auth-diag-outcome-${i}`}>
                                {diagOutcome || "unknown"}
                              </code>{" "}
                              <span className="text-muted-foreground">
                                | bearer={authDiag.bearer_prefix_ok ? "ok" : "—"} len={authDiag.bearer_token_len}
                                {" "}| jwt_decoded={String(authDiag.jwt_decoded)}
                                {authDiag.user_id_in_jwt ? ` sub=${String(authDiag.user_id_in_jwt).slice(0,12)}…` : ""}
                                {" "}| user_in_db={String(authDiag.user_found_for_jwt_sub)}
                                {" "}| session_found={String(authDiag.session_found)}
                                {authDiag.session_expired ? " (expired)" : ""}
                              </span>
                              {diagOutcome === "jwt_decoded_but_user_not_in_db" ? (
                                <span className="ml-2 text-rose-700 font-semibold">
                                  ⚠ Token decoded but user_id is not in this DB — likely environment mismatch (frontend pointing at the wrong backend).
                                </span>
                              ) : null}
                            </td>
                          </tr>
                        ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {(r.recent_analyze_errors || []).length ? (
            <div className="space-y-2 md:col-span-2 pt-4 border-t border-border/40" data-testid="runtime-analyze-errors">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Recent analyze errors <span className="text-muted-foreground/70 normal-case">(handler-body crashes + cancellations)</span>
              </div>
              <div className="space-y-2">
                {(r.recent_analyze_errors || []).slice(0, 5).map((err, i) => (
                  <div key={i} className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-2 text-[11px]" data-testid={`runtime-analyze-error-${i}`}>
                    <div className="flex items-start gap-2 mb-1 flex-wrap">
                      <code className="text-rose-700 font-semibold">{err.error_class || "?"}</code>
                      <code className="text-muted-foreground">@{err.stage || "?"}</code>
                      <code className="text-muted-foreground">{err.ticker || "?"}</code>
                      {err.request_id ? (
                        <code className="text-amber-700" data-testid={`runtime-analyze-error-reqid-${i}`}>req={err.request_id}</code>
                      ) : null}
                      <div className="ml-auto text-[10px] text-muted-foreground">{(err.timestamp || "").slice(11, 19)} UTC</div>
                    </div>
                    {err.error_message ? (
                      <div className="text-rose-800/80 break-words">{err.error_message}</div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Phase 6.AH — Frontend crash reports posted by React error
              boundaries. Shows which UI components are crashing in the
              browser despite backend 200 responses. */}
          {(r.recent_frontend_errors || []).length ? (
            <div className="space-y-2 md:col-span-2 pt-4 border-t border-border/40" data-testid="runtime-frontend-errors">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Recent frontend crashes <span className="text-muted-foreground/70 normal-case">(React error boundaries · 24h TTL)</span>
              </div>
              <div className="space-y-2">
                {(r.recent_frontend_errors || []).slice(0, 8).map((err, i) => (
                  <div key={err._id || i} className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-2 text-[11px]" data-testid={`runtime-frontend-error-${i}`}>
                    <div className="flex items-start gap-2 mb-1 flex-wrap">
                      <code className="text-rose-700 font-semibold">{err.error_name || "Error"}</code>
                      {err.boundary ? <code className="text-violet-700">@{err.boundary}</code> : null}
                      {err.component ? <code className="text-muted-foreground truncate max-w-[160px]" title={err.component}>{err.component}</code> : null}
                      {err.ticker ? <code className="text-foreground">{err.ticker}</code> : null}
                      {err.request_id ? <code className="text-amber-700" data-testid={`runtime-frontend-reqid-${i}`}>req={err.request_id}</code> : null}
                      {err.schema_valid != null ? (
                        <code className={err.schema_valid ? "text-emerald-700" : "text-rose-700"}>
                          schema={String(err.schema_valid)}
                        </code>
                      ) : null}
                      {err._id ? <code className="text-blue-700" title="audit id">trace={err._id}</code> : null}
                      <div className="ml-auto text-[10px] text-muted-foreground">{(err.received_at || "").slice(11, 19)} UTC</div>
                    </div>
                    {err.error_message ? (
                      <div className="text-rose-800/80 break-words mb-1">{err.error_message}</div>
                    ) : null}
                    {err.url ? (
                      <div className="text-[10px] text-muted-foreground truncate" title={err.url}>
                        url: <code>{err.url}</code>
                      </div>
                    ) : null}
                    {err.component_stack ? (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-[10px] text-muted-foreground hover:text-foreground">
                          React component stack
                        </summary>
                        <pre className="mt-1 whitespace-pre-wrap break-all bg-black/40 text-amber-200 p-2 rounded text-[10px] max-h-40 overflow-auto">
                          {err.component_stack}
                        </pre>
                      </details>
                    ) : null}
                    {err.error_stack ? (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-[10px] text-muted-foreground hover:text-foreground">
                          JS stack trace
                        </summary>
                        <pre className="mt-1 whitespace-pre-wrap break-all bg-black/60 text-rose-200 p-2 rounded text-[10px] max-h-40 overflow-auto">
                          {err.error_stack}
                        </pre>
                      </details>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Phase 6.AI.5 — Frontend-timeout-without-backend-trace
              ALERT. Each orphan is a confirmed "the browser showed
              Analysis took too long but the backend never received the
              request" event. Color: rose / loud. This is the highest-
              priority signal in the panel because it means a routing
              issue (wrong base URL, CORS, ad-blocker, CDN edge) and
              demands immediate operator attention. */}
          {(r.frontend_timeout_no_backend_trace || []).length ? (
            <div className="space-y-2 md:col-span-2 pt-4 border-t-2 border-rose-500/40" data-testid="runtime-orphan-timeouts">
              <div className="text-[11px] uppercase tracking-wide text-rose-700 font-semibold flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                Frontend timeout — no backend trace ({(r.frontend_timeout_no_backend_trace || []).length})
                <span className="text-muted-foreground/70 normal-case font-normal">
                  Browser showed "Analysis took too long" but the backend never received the request. Likely cause: wrong base URL, CORS, ad-blocker, CDN edge, stale bundle.
                </span>
              </div>
              <div className="overflow-x-auto rounded-lg border border-rose-500/40 bg-rose-500/5">
                <table className="w-full text-[11px]" data-testid="runtime-orphan-timeouts-table">
                  <thead className="bg-rose-500/10">
                    <tr className="text-left">
                      <th className="px-2 py-1 font-medium">time</th>
                      <th className="px-2 py-1 font-medium">ticker</th>
                      <th className="px-2 py-1 font-medium">request_id</th>
                      <th className="px-2 py-1 font-medium">url</th>
                      <th className="px-2 py-1 font-medium">user_agent</th>
                      <th className="px-2 py-1 font-medium">phase seen</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {(r.frontend_timeout_no_backend_trace || []).map((row, i) => (
                      <tr key={i} className="border-t border-rose-500/20" data-testid={`runtime-orphan-${i}`}>
                        <td className="px-2 py-1 whitespace-nowrap">{(row.received_at || "").slice(11, 19)}</td>
                        <td className="px-2 py-1 font-semibold">{row.ticker || "—"}</td>
                        <td className="px-2 py-1"><code className="text-[10px] text-rose-700">{row.request_id || "(no req_id)"}</code></td>
                        <td className="px-2 py-1 max-w-[240px] truncate" title={row.url}>{row.url || "—"}</td>
                        <td className="px-2 py-1 max-w-[160px] truncate" title={row.user_agent}>{row.user_agent || "—"}</td>
                        <td className="px-2 py-1 max-w-[140px] truncate" title={row.build_phase_seen}>
                          <code className="text-[10px]">{(row.build_phase_seen || "—").slice(0, 12)}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {/* Phase 6.AI.3 — Frontend lifecycle events (request_started,
              response_received, response_accepted, timeout_screen_displayed,
              etc.). The single source of truth for "what did the browser
              actually do?" Especially critical for diagnosing the
              "backend 200 OK but user saw timeout screen" failure mode. */}
          {(r.recent_frontend_lifecycle || []).length ? (
            <div className="space-y-2 md:col-span-2 pt-4 border-t border-border/40" data-testid="runtime-frontend-lifecycle">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Recent frontend lifecycle <span className="text-muted-foreground/70 normal-case">(client-side state transitions · 24h TTL)</span>
              </div>
              <div className="overflow-x-auto rounded-lg border border-border/40">
                <table className="w-full text-[11px]" data-testid="runtime-frontend-lifecycle-table">
                  <thead className="bg-muted/40">
                    <tr className="text-left">
                      <th className="px-2 py-1 font-medium">time</th>
                      <th className="px-2 py-1 font-medium">event</th>
                      <th className="px-2 py-1 font-medium">ticker</th>
                      <th className="px-2 py-1 font-medium">request_id</th>
                      <th className="px-2 py-1 font-medium">elapsed</th>
                      <th className="px-2 py-1 font-medium">user</th>
                      <th className="px-2 py-1 font-medium">phase seen</th>
                      <th className="px-2 py-1 font-medium">extra</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {(r.recent_frontend_lifecycle || []).slice(0, 25).map((row, i) => {
                      const isAlarm = ["timeout_screen_displayed", "stale_timeout_ignored", "retry_failed"].includes(row.event);
                      const isSuccess = ["response_accepted", "retry_succeeded"].includes(row.event);
                      return (
                        <tr key={i} className={`border-t border-border/30 ${isAlarm ? "bg-rose-500/5" : isSuccess ? "bg-emerald-500/5" : ""}`} data-testid={`runtime-fe-lifecycle-${i}`}>
                          <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">{(row.received_at || "").slice(11, 19)}</td>
                          <td className="px-2 py-1">
                            <code className={`text-[10px] ${isAlarm ? "text-rose-700 font-semibold" : isSuccess ? "text-emerald-700" : "text-muted-foreground"}`}>
                              {row.event}
                            </code>
                          </td>
                          <td className="px-2 py-1">{row.ticker || "—"}</td>
                          <td className="px-2 py-1">
                            <code className="text-[10px] text-amber-700">{row.request_id || "—"}</code>
                          </td>
                          <td className="px-2 py-1">{row.elapsed_ms != null ? `${row.elapsed_ms}ms` : "—"}</td>
                          <td className="px-2 py-1 max-w-[120px] truncate" title={row.user_email}>{row.user_email || "—"}</td>
                          <td className="px-2 py-1 max-w-[140px] truncate" title={row.build_phase_seen}>
                            <code className="text-[10px]">{(row.build_phase_seen || "—").slice(0, 12)}</code>
                          </td>
                          <td className="px-2 py-1 max-w-[200px] truncate" title={row.extra ? JSON.stringify(row.extra) : ""}>
                            <code className="text-[10px] text-muted-foreground">{row.extra ? JSON.stringify(row.extra) : "—"}</code>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

/**
 * CacheClearActions — admin destructive actions to eliminate cache masking
 * during production debugging. Each button calls
 * `DELETE /api/admin/debug/cache/{name}` and refreshes the parent panel.
 */
function CacheClearActions({ caches, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const handleClear = async (name) => {
    if (busy) return;
    // Phase 6.BB — make the confirm dialog environment-aware. Show the
    // browser host so the operator cannot accidentally clear the wrong
    // environment's cache while debugging across two open tabs.
    let envLabel = "this environment";
    let hostLabel = "";
    if (typeof window !== "undefined") {
      hostLabel = window.location?.hostname || "";
      if (/\.emergent\.host$/i.test(hostLabel)) envLabel = "PRODUCTION";
      else if (/preview\.emergentagent/i.test(hostLabel)) envLabel = "PREVIEW";
    }
    if (!window.confirm(
      `Clear ${name} cache on ${envLabel}?\n\n` +
      `Browser host: ${hostLabel || "(unknown)"}\n` +
      `This is irreversible and only affects ${envLabel} backend caches.`
    )) return;
    setBusy(name);
    setLastResult(null);
    try {
      const res = await api.delete(`/admin/debug/cache/${name}`, { headers: adminHeaders() });
      setLastResult({ ok: true, ...res.data });
      onChanged?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed";
      setLastResult({ ok: false, error: typeof msg === "string" ? msg : "Failed" });
    } finally {
      setBusy(null);
    }
  };

  const items = [
    { name: "analysis", label: "Analysis cache",  count: caches.analysis_cache },
    { name: "raw-fmp",  label: "Raw FMP cache",   count: caches.raw_fmp_data },
    { name: "chart",    label: "Chart cache",     count: caches.chart_cache },
    { name: "search",   label: "Search cache",    count: caches.search_cache },
  ];

  return (
    <div data-testid="cache-clear-actions">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Cache controls</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
        {items.map((it) => (
          <Button
            key={it.name}
            type="button"
            size="sm"
            variant="outline"
            onClick={() => handleClear(it.name)}
            disabled={busy !== null}
            data-testid={`cache-clear-${it.name}`}
            className="justify-between"
          >
            <span className="text-xs">{it.label}</span>
            <span className="text-[10px] font-mono text-muted-foreground">{it.count ?? "—"}</span>
          </Button>
        ))}
      </div>
      <Button
        type="button"
        size="sm"
        variant="destructive"
        onClick={() => handleClear("all")}
        disabled={busy !== null}
        data-testid="cache-clear-all"
        className="w-full"
      >
        {busy === "all" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
        Clear all caches
      </Button>
      {lastResult ? (
        <div
          className={`mt-2 rounded-md border px-2.5 py-2 text-[11px] ${
            lastResult.ok
              ? (lastResult.environment === "production"
                  ? "border-rose-500/60 bg-rose-500/10 text-rose-700"
                  : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700")
              : "border-rose-500/60 bg-rose-500/10 text-rose-600"
          }`}
          data-testid="cache-clear-result"
        >
          {lastResult.ok ? (
            <>
              {/* Phase 6.BB — Environment-aware confirmation. Makes it
                  unambiguous which environment's cache was cleared. */}
              <div className="font-semibold uppercase tracking-wide flex items-center gap-1.5">
                {lastResult.environment === "production"
                  ? <AlertTriangle className="w-3.5 h-3.5" />
                  : <Check className="w-3.5 h-3.5" />}
                Cleared {(lastResult.environment || "unknown").toUpperCase()} cache · {lastResult.cache_name}
              </div>
              <div className="mt-1 font-mono opacity-90">
                {Object.entries(lastResult.summary || lastResult.deleted || {})
                  .filter(([k]) => k.endsWith("_deleted_count"))
                  .map(([k, v]) => `${k.replace("_deleted_count", "")}=${v}`)
                  .join(" · ") || JSON.stringify(lastResult.deleted)}
              </div>
              <div className="mt-0.5 opacity-80">
                {`backend ${lastResult.backend_hostname || "?"} · ${lastResult.elapsed_ms}ms`}
                {lastResult.summary?.frontend_cache_bust_token
                  ? ` · bust=${String(lastResult.summary.frontend_cache_bust_token).slice(0, 8)}…`
                  : ""}
              </div>
            </>
          ) : (
            <>Failed: {lastResult.error}</>
          )}
        </div>
      ) : null}
    </div>
  );
}

function OperationalMetricsCard({ metrics, loading }) {
  const m = metrics || {};
  const active = Array.isArray(m.active) ? m.active : [];
  const missingTop = Array.isArray(m.missing_kpi_top) ? m.missing_kpi_top : [];
  const longest = m.longest_active;

  return (
    <Card title="Operational Metrics" icon={Activity} accent="#10B981">
      {loading && !metrics ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="owner-opmetrics-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading operational metrics…
        </div>
      ) : null}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat
          label="Total today"
          value={m.total_today ?? 0}
          testId="opmetrics-total-today"
        />
        <Stat
          label="Cache hit rate"
          value={m.cache_hit_rate_pct != null ? `${m.cache_hit_rate_pct}%` : "—"}
          accent="text-emerald-600"
          testId="opmetrics-cache-rate"
        />
        <Stat
          label="Failures"
          value={m.failures_today ?? 0}
          accent={(m.failures_today ?? 0) > 0 ? "text-rose-600" : ""}
          testId="opmetrics-failures"
        />
        <Stat
          label="Samples"
          value={m.samples_today ?? 0}
          testId="opmetrics-samples"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Analysis duration</div>
          <StatRow
            label="Average"
            value={<span className="text-xs font-semibold">{m.avg_duration_s != null ? `${m.avg_duration_s.toFixed(1)}s` : "—"}</span>}
            testId="opmetrics-avg-duration"
          />
          <StatRow
            label="p95"
            value={<span className="text-xs font-semibold">{m.p95_duration_s != null ? `${m.p95_duration_s.toFixed(1)}s` : "—"}</span>}
            testId="opmetrics-p95-duration"
          />
          <StatRow
            label="AI generation avg"
            value={<span className="text-xs font-semibold">{m.avg_ai_duration_s != null ? `${m.avg_ai_duration_s.toFixed(1)}s` : "—"}</span>}
            testId="opmetrics-avg-ai"
          />
          <div className="pt-2 border-t border-border/40">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">In-flight now</div>
            <StatRow
              label="Active analyses"
              value={<span className="text-xs font-semibold" data-testid="opmetrics-active-count">{m.active_count ?? 0}</span>}
              testId="opmetrics-active-row"
            />
            <StatRow
              label="Longest current"
              value={
                longest ? (
                  <span className="text-xs" data-testid="opmetrics-longest">
                    <code className="text-[11px]">{longest.ticker}</code>
                    <span className="text-muted-foreground"> · {longest.elapsed_s != null ? `${longest.elapsed_s.toFixed(1)}s` : "—"}</span>
                  </span>
                ) : <span className="text-xs text-muted-foreground">—</span>
              }
              testId="opmetrics-longest-row"
            />
            {active.length > 0 ? (
              <div className="mt-2 space-y-1" data-testid="opmetrics-active-list">
                {active.slice(0, 6).map((row) => (
                  <div key={row.run_id} className="flex items-center justify-between text-[11px]">
                    <code>{row.ticker}</code>
                    <span className="text-muted-foreground tabular">{row.elapsed_s != null ? `${row.elapsed_s.toFixed(1)}s` : "—"}</span>
                  </div>
                ))}
                {active.length > 6 ? (
                  <div className="text-[10px] text-muted-foreground">+ {active.length - 6} more</div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Most common missing KPI fields</div>
          {missingTop.length === 0 ? (
            <div className="text-xs text-muted-foreground" data-testid="opmetrics-missing-empty">
              No missing KPI fields recorded today.
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="opmetrics-missing-list">
              {missingTop.map(([field, count]) => (
                <li key={field} className="flex items-center justify-between text-xs">
                  <code className="text-[11px]">{field}</code>
                  <span className="font-mono text-muted-foreground">{count}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="pt-3 border-t border-border/40 text-[10px] text-muted-foreground leading-relaxed">
            All counters are process-local and reset at UTC midnight or on backend restart. Today (UTC): <code>{m.today_utc || "—"}</code>.
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * AnalyzeFailuresCard — admin-only failure diagnostics for the analyze
 * pipeline. Lists the most recent entries from `db.analyze_errors`
 * (server-side stage failures + client-side timeout events) so the
 * owner can diagnose production reliability issues at a glance.
 *
 * Lives inside the System tab. Manual-refresh only — same UX contract
 * as the Live Data Status card above so admins observe a stable
 * snapshot when investigating an incident.
 */
function AnalyzeFailuresCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterTicker, setFilterTicker] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/admin/debug/analyze-errors", {
        headers: adminHeaders(),
        params: { limit: 50 },
      });
      setData(res.data);
      setLastFetchedAt(new Date());
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to load analyze errors";
      setError(typeof msg === "string" ? msg : "Failed to load analyze errors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  // Sub-pass 2A (2026-02-17) — stabilise `items` identity by wrapping
  // its logical-fallback in useMemo.  Without this, every render
  // produces a fresh empty array when `data.items` is undefined,
  // invalidating the `filtered` useMemo below on every render.
  const items = useMemo(() => data?.items || [], [data?.items]);
  const byStage = data?.by_stage || [];
  const byTicker = data?.by_ticker || [];

  const filtered = useMemo(() => {
    const t = filterTicker.trim().toUpperCase();
    const s = filterStage.trim().toLowerCase();
    return items.filter((row) => {
      if (t && !(row.ticker || "").toUpperCase().includes(t)) return false;
      if (s && !(row.stage || "").toLowerCase().includes(s)) return false;
      return true;
    });
  }, [items, filterTicker, filterStage]);

  return (
    <Card title="Recent Analyze Failures" icon={AlertTriangle} accent="#E11D48">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-muted-foreground">
          Admin-only diagnostics for failed analyses. Captures backend stage failures and frontend timeouts. Newest first, capped at 50.
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={fetchErrors}
          disabled={loading}
          data-testid="owner-failures-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {error ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2 mb-3" data-testid="owner-failures-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      {/* Aggregate breakdown — top stages + top tickers (last 1000 events) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="rounded-lg border border-border/50 bg-card/40 p-3" data-testid="owner-failures-summary">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Total recorded</div>
          <div className="text-2xl font-semibold">{data?.total ?? 0}</div>
        </div>
        <div className="rounded-lg border border-border/50 bg-card/40 p-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Top failing stages</div>
          {byStage.length === 0 ? (
            <div className="text-xs text-muted-foreground">No failures yet.</div>
          ) : (
            <ul className="space-y-1" data-testid="owner-failures-by-stage">
              {byStage.slice(0, 5).map(([stage, count]) => (
                <li key={stage} className="flex items-center justify-between text-xs">
                  <code className="text-[11px]">{stage}</code>
                  <span className="font-mono text-muted-foreground">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="rounded-lg border border-border/50 bg-card/40 p-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Top failing tickers</div>
          {byTicker.length === 0 ? (
            <div className="text-xs text-muted-foreground">No failures yet.</div>
          ) : (
            <ul className="space-y-1" data-testid="owner-failures-by-ticker">
              {byTicker.slice(0, 5).map(([sym, count]) => (
                <li key={sym} className="flex items-center justify-between text-xs">
                  <code className="text-[11px]">{sym}</code>
                  <span className="font-mono text-muted-foreground">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Input
          placeholder="Filter by ticker (e.g. MSFT)"
          value={filterTicker}
          onChange={(e) => setFilterTicker(e.target.value)}
          className="h-8 text-xs sm:max-w-[200px]"
          data-testid="owner-failures-filter-ticker"
        />
        <Input
          placeholder="Filter by stage (e.g. ai_insights)"
          value={filterStage}
          onChange={(e) => setFilterStage(e.target.value)}
          className="h-8 text-xs sm:max-w-[240px]"
          data-testid="owner-failures-filter-stage"
        />
        {(filterTicker || filterStage) ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => { setFilterTicker(""); setFilterStage(""); }}
            className="h-8 text-xs"
            data-testid="owner-failures-clear-filter"
          >
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
        ) : null}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="rounded-lg border border-border/40 bg-card/30 p-6 text-center text-sm text-muted-foreground" data-testid="owner-failures-empty">
          {loading ? "Loading failures…" : (items.length === 0 ? "No analyze failures recorded yet — production is clean." : "No failures match the current filters.")}
        </div>
      ) : (
        <div className="border border-border/50 rounded-lg overflow-hidden">
          <Table data-testid="owner-failures-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[160px]">Timestamp</TableHead>
                <TableHead className="w-[90px]">Ticker</TableHead>
                <TableHead className="w-[140px]">Stage</TableHead>
                <TableHead className="w-[80px]">Plan</TableHead>
                <TableHead className="w-[140px]">Error</TableHead>
                <TableHead>Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row, i) => (
                <TableRow key={`${row.ticker}-${row.timestamp}-${i}`}>
                  <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtDate(row.timestamp)}</TableCell>
                  <TableCell><code className="text-[11px]">{row.ticker || "—"}</code></TableCell>
                  <TableCell>
                    <FailureStageBadge stage={row.stage} />
                  </TableCell>
                  <TableCell className="text-[11px] capitalize">{row.user_plan || "—"}</TableCell>
                  <TableCell>
                    <code className="text-[11px]">{row.error_class || "—"}</code>
                  </TableCell>
                  <TableCell className="text-[11px] text-muted-foreground truncate max-w-[400px]" title={row.error_message || ""}>
                    {row.error_message || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {lastFetchedAt ? (
        <div className="mt-3 text-[10px] text-muted-foreground">
          Last refreshed {lastFetchedAt.toLocaleTimeString()} · showing {filtered.length} of {items.length}
        </div>
      ) : null}
    </Card>
  );
}

function FailureStageBadge({ stage }) {
  const s = (stage || "unknown").toLowerCase();
  // Light semantic coloring — frontend timeouts (client-side aborts) get
  // an amber tone, server-side stage failures get a rose tone, "unknown"
  // stays neutral. Keeps the table scannable without being noisy.
  let cls = "bg-rose-500/15 text-rose-700 border-rose-500/40";
  if (s === "frontend_timeout") {
    cls = "bg-amber-500/15 text-amber-700 border-amber-500/40";
  } else if (s === "unknown") {
    cls = "bg-slate-500/15 text-slate-700 border-slate-500/40";
  }
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] border ${cls}`} data-testid={`failure-stage-${s}`}>
      {s}
    </span>
  );
}

function FmpHealthBadge({ health }) {
  const map = {
    active:        { label: "Active",        cls: "bg-emerald-500/15 text-emerald-700 border-emerald-500/40", Icon: Wifi },
    idle:          { label: "Idle",          cls: "bg-slate-500/15 text-slate-700 border-slate-500/40", Icon: WifiOff },
    disabled:      { label: "Disabled",      cls: "bg-slate-500/15 text-slate-700 border-slate-500/40", Icon: WifiOff },
    rate_limited:  { label: "Rate Limited",  cls: "bg-amber-500/15 text-amber-700 border-amber-500/40", Icon: AlertTriangle },
    auth_failure:  { label: "Auth Failure",  cls: "bg-rose-500/15 text-rose-700 border-rose-500/40", Icon: AlertTriangle },
    failing:       { label: "Failing",       cls: "bg-rose-500/15 text-rose-700 border-rose-500/40", Icon: AlertTriangle },
  };
  const meta = map[health] || map.idle;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${meta.cls}`} data-testid="fmp-health-badge">
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function HealthLabel({ health }) {
  const text = {
    active: "FMP responding normally",
    idle: "No FMP traffic yet",
    disabled: "FMP integration off",
    rate_limited: "FMP quota exceeded (429)",
    auth_failure: "FMP rejected the API key",
    failing: "FMP returning errors",
  }[health] || health || "—";
  return <span className="text-xs">{text}</span>;
}

function DataSourceBadge({ source, mockBlocked = true }) {
  // Phase 6.AI.4 — when mock fallback is blocked (production posture)
  // there is no such thing as "cache_or_mock" — a cache hit is always
  // a previously-validated FMP payload. Rendering "Cache or Mock"
  // misleads operators into thinking mock data could still appear.
  const map = {
    fmp:            { label: "Live (FMP)", cls: "bg-emerald-500/15 text-emerald-700 border-emerald-500/40" },
    cache_or_mock:  mockBlocked
      ? { label: "Cached (FMP)", cls: "bg-sky-500/15 text-sky-700 border-sky-500/40" }
      : { label: "Cache or Mock", cls: "bg-amber-500/15 text-amber-700 border-amber-500/40" },
    cache:          { label: "Cache", cls: "bg-sky-500/15 text-sky-700 border-sky-500/40" },
    mock:           mockBlocked
      ? { label: "Unavailable", cls: "bg-rose-500/15 text-rose-700 border-rose-500/40" }
      : { label: "Mock", cls: "bg-slate-500/15 text-slate-700 border-slate-500/40" },
  };
  const meta = map[source] || (mockBlocked ? map.fmp : map.mock);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function CircuitBadge({ open, until, reason }) {
  if (!open) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border bg-emerald-500/15 text-emerald-700 border-emerald-500/40" data-testid="circuit-closed">
        Closed
      </span>
    );
  }
  const reasonLabel = reason === "rate_limited" ? "rate limit" : reason === "auth_error" ? "auth error" : (reason || "open");
  return (
    <span className="inline-flex flex-col items-end gap-0.5" data-testid="circuit-open">
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border bg-rose-500/15 text-rose-700 border-rose-500/40">
        Open · {reasonLabel}
      </span>
      {until ? (
        <span className="text-[10px] text-muted-foreground">resets {new Date(until).toLocaleTimeString()}</span>
      ) : null}
    </span>
  );
}

function FmpEventLine({ ts, path, reason, status }) {
  if (!ts) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="text-xs">
      <span title={ts}>{fmtDate(ts)}</span>
      {path ? <> · <code className="text-[10px]">{path}</code></> : null}
      {reason ? <> · <span className="text-rose-600">{reason}{status ? ` (${status})` : ""}</span></> : null}
    </span>
  );
}

function HitRate({ rate, hits, misses }) {
  if (rate == null) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="text-xs">
      {rate}% <span className="text-muted-foreground">({hits ?? 0} hits / {misses ?? 0} misses)</span>
    </span>
  );
}

function StatRow({ label, value, testId }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border/40 last:border-0">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground shrink-0">{label}</span>
      <span className="text-right" data-testid={testId}>{value}</span>
    </div>
  );
}

function Stat({ label, value, accent, compact, testId }) {
  return (
    <div className={`rounded-lg border border-border/50 bg-card/40 ${compact ? "p-2" : "p-3"} text-center`} data-testid={testId}>
      <div className={`font-semibold ${compact ? "text-lg" : "text-2xl"} ${accent || ""}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

// =============================================================================
// Phase 9.HMS.v1 — Homepage Market Snapshot Owner controls
// =============================================================================
// Read-only inspection + manual "Generate" and "Republish LKG" actions.
// Owner-only per the backend Depends(admin_user) guard.

function HomepageSnapshotPanel() {
  // Landing 2b — single owner-facing control surface for the Homepage
  // Market Snapshot.  All published-snapshot summary, static-fallback
  // state, four-slot draft composition (pin/generate/publish/discard),
  // republish action, reload and error handling live inside the
  // composer.  Legacy Active-snapshot + Manual-controls +
  // Generate/Republish blocks were removed as part of the Landing 2b
  // correction.
  return (
    <div className="space-y-4" data-testid="owner-homepage-snapshot-panel">
      <HomepageSnapshotComposer adminHeaders={adminHeaders} />
    </div>
  );
}


// =============================================================================
// Early Access / Waitlist v1 — Owner visibility panel.
//
// Read-only.  Shows the metrics envelope from
// GET /api/admin/early-access/metrics and the most recent confirmed
// entries from GET /api/admin/early-access/recent (both owner-gated
// server-side).  No PII is ever rendered — recent entries are
// identified by a short per-row ``email_hash`` only.
// =============================================================================
function EarlyAccessPanel() {
  const [metrics, setMetrics] = React.useState(null);
  const [recent, setRecent] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [reloadTick, setReloadTick] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [m, r] = await Promise.all([
          api.get("/admin/early-access/metrics", { headers: adminHeaders() }),
          api.get("/admin/early-access/recent", { headers: adminHeaders(), params: { limit: 25 } }),
        ]);
        if (cancelled) return;
        setMetrics(m.data || {});
        setRecent((r.data && r.data.rows) || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "load_failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadTick]);

  const foundingLimit = metrics?.founding_offer_limit ?? 100;
  const foundingUsed = metrics?.confirmations_in_founding_window ?? 0;
  const foundingRemaining = Math.max(0, foundingLimit - foundingUsed);

  return (
    <div className="space-y-4" data-testid="owner-early-access-panel">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold tracking-tight flex items-center gap-1.5">
            <Mail className="w-4 h-4" /> Early Access / Waitlist
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Signups remain open beyond 100. The counter tracks the publicly guaranteed first-100 offer; positions are assigned after DOI confirmation.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setReloadTick((t) => t + 1)}
          data-testid="owner-early-access-reload"
          disabled={loading}
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Reload
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive" data-testid="owner-early-access-error">
          Failed to load Early Access metrics: {String(error)}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="owner-early-access-metrics">
        <MetricCard label="Total submissions" value={metrics?.total_submissions ?? "—"} testId="metric-total" />
        <MetricCard label="Pending confirmations" value={metrics?.pending_confirmations ?? "—"} testId="metric-pending" />
        <MetricCard label="Confirmed contacts" value={metrics?.confirmed_contacts ?? "—"} testId="metric-confirmed" />
        <MetricCard
          label="Guaranteed first-100 offer"
          value={`${foundingUsed} / ${foundingLimit}`}
          sub={`${foundingRemaining} guaranteed places remaining · signups stay open`}
          testId="metric-founding"
        />
        <MetricCard label="Unsubscribed" value={metrics?.unsubscribed_contacts ?? "—"} testId="metric-unsub" />
        <MetricCard label="Resend sync failures" value={metrics?.resend_sync_failures ?? "—"} testId="metric-resend-fail" />
        <MetricCard label="Event sync failures" value={metrics?.event_sync_failures ?? "—"} testId="metric-event-fail" />
        <MetricCard
          label="Founding offer limit"
          value={foundingLimit}
          sub="marketing guarantee; not a signup cap"
          testId="metric-founding-limit"
        />
      </div>

      <div className="rounded-xl border border-border/70 bg-card/40">
        <div className="px-4 py-2.5 text-xs font-medium border-b border-border/60 flex items-center justify-between">
          <span>Recent confirmed (newest first)</span>
          <span className="text-[11px] text-muted-foreground">
            {recent.length ? `${recent.length} shown` : loading ? "Loading…" : "No confirmed entries yet"}
          </span>
        </div>
        <Table data-testid="owner-early-access-recent">
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Position</TableHead>
              <TableHead className="w-24">Hash</TableHead>
              <TableHead className="w-20">Locale</TableHead>
              <TableHead className="w-28">Source</TableHead>
              <TableHead className="w-28">UTM</TableHead>
              <TableHead className="w-24">Founding</TableHead>
              <TableHead className="w-28">Resend</TableHead>
              <TableHead className="w-28">Event</TableHead>
              <TableHead>Confirmed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recent.map((row) => (
              <TableRow key={`${row.waitlist_position}-${row.email_hash}`} data-testid={`owner-early-access-row-${row.waitlist_position}`}>
                <TableCell className="font-mono tabular-nums">#{row.waitlist_position}</TableCell>
                <TableCell className="font-mono text-[11px]">{row.email_hash || "—"}</TableCell>
                <TableCell>{row.locale || "—"}</TableCell>
                <TableCell>{row.source || "—"}</TableCell>
                <TableCell>{row.utm_source || "—"}</TableCell>
                <TableCell>
                  {row.founding_offer_guaranteed ? (
                    <Badge variant="default">Yes</Badge>
                  ) : (
                    <Badge variant="outline">No</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <SyncBadge state={row.resend_sync_state} />
                </TableCell>
                <TableCell>
                  <SyncBadge state={row.event_sync_state} />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(row.confirmed_at)}</TableCell>
              </TableRow>
            ))}
            {(!recent.length && !loading) && (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-xs text-muted-foreground py-6">
                  No confirmed Early Access entries yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function MetricCard({ label, value, sub, testId }) {
  return (
    <div className="rounded-xl border border-border/70 bg-card/50 p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
      {sub ? <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

function SyncBadge({ state }) {
  const label = state || "—";
  if (state === "sent" || state === "synced") return <Badge variant="default">{label}</Badge>;
  if (state === "failed") return <Badge variant="destructive">{label}</Badge>;
  return <Badge variant="outline">{label}</Badge>;
}

